// ============================================================
//  RENT THIS RENT THAT — Supabase Connected App
//  File: src/App.jsx
//
//  HOW TO USE THIS FILE:
//  1. Run the SQL schema in Supabase → SQL Editor first
//  2. Fill in your keys in supabaseClient.js
//  3. npm install @supabase/supabase-js
//  4. Replace your current App.jsx with this file
//  5. Your app now reads/writes real data!
// ============================================================

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  supabase,
  signUp, signIn, signOut, resetPassword, updatePassword, getProfile, uploadAvatar,
  getListings, createListing, getOwnerListings,
  createBooking, getRenterBookings,
  submitLicenseVerification, uploadLicenseDocument, checkLicenseStatus,
  saveTrainingCompletion, checkTrainingStatus,
  uploadListingPhoto,
  createDamageClaim, getClaimsForBooking, getMyClaims,
  respondToClaim, withdrawClaim, uploadClaimPhoto,
  getOwnerBookings,
  getNotifications, markNotificationRead, markAllNotificationsRead,
  subscribeToNotifications,
  getAdminUsers, getAdminBookings, getAdminClaims, getAdminStats,
  checkIsAdmin, updateClaimStatus,
} from "./supabaseClient";

// ─── COLORS & SHARED STYLES ────────────────────────────────────────────────────
const COLORS = {
  bg:"#0D0F14", surface:"#161A23", card:"#1E2433", border:"#2A3245",
  accent:"#F4A235", green:"#2ECC71", red:"#E74C3C",
  blue:"#3B82F6", purple:"#8B5CF6", text:"#F0F2F7", muted:"#8A93A8",
};

const LICENSE_REQUIREMENTS = {
  "Boats":            { required:true,  licenseType:"boating",   licenseLabel:"State Boating License / Safety Certificate",          licenseDesc:"A valid state-issued boating license or NASBLA-approved safety certificate is required." },
  "Jet Skis":         { required:true,  licenseType:"boating",   licenseLabel:"State Boating License / PWC Certificate",             licenseDesc:"Personal Watercraft operation requires a valid boating license or PWC certificate." },
  "Campers & RVs":    { required:true,  licenseType:"cdl_or_dl", licenseLabel:"Valid Driver's License (Class A/B for large RVs)",    licenseDesc:"A standard driver's license is required. Large motorhomes may need Class A or B CDL." },
  "Aircraft":         { required:true,  licenseType:"pilot",     licenseLabel:"FAA Pilot Certificate (PPL, Sport Pilot, or higher)", licenseDesc:"FAA Pilot Certificate required. Upload certificate and current medical." },
  "Automobiles":      { required:true,  licenseType:"drivers",   licenseLabel:"Valid Driver's License",                              licenseDesc:"A valid government-issued driver's license is required." },
  "Motorcycles":      { required:true,  licenseType:"moto",      licenseLabel:"Motorcycle Endorsement or M-Class License",           licenseDesc:"M-class endorsement or separate motorcycle license required." },
  "Heavy Equipment":  { required:true,  licenseType:"equipment", licenseLabel:"Equipment Operator Certification (NCCCO or equiv.)", licenseDesc:"OSHA-compliant operator certification required." },
  "Power Tools":      { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Outdoor Equipment":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Construction":     { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Garden":           { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Events":           { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Electronics":      { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Sports & Recreation":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Appliances":       { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Clothing & Fashion":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Costumes & Cosplay":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Furniture":        { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Camera & Film":    { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Musical Instruments":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Party & Entertainment":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Baby & Kids":      { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Wedding":          { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Camping & Hiking": { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Fitness Equipment":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Drones":           { required:true,  licenseType:"drone",     licenseLabel:"FAA Part 107 Remote Pilot Certificate",                licenseDesc:"FAA Part 107 certification required for commercial drone operation." },
  "Trailers":         { required:true,  licenseType:"drivers",   licenseLabel:"Valid Driver's License",                               licenseDesc:"A valid driver's license is required. Heavy trailers may need special endorsement." },
  "Storage & Moving": { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Inflatables":      { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Luxury & Designer":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Seasonal & Holiday":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Farm Equipment":   { required:true,  licenseType:"equipment", licenseLabel:"Equipment Operator Certification or Driver's License",  licenseDesc:"Large farm equipment may require operator certification or CDL." },
  "Medical Equipment":{ required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "AV & Sound":       { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
  "Office Equipment": { required:false, licenseType:null, licenseLabel:null, licenseDesc:null },
};
const LICENSE_ICONS = { boating:"⚓", cdl_or_dl:"🚐", pilot:"✈️", drivers:"🚗", moto:"🏍️", equipment:"🏗️", drone:"🚁" };

// ─── FEE CONFIGURATION ────────────────────────────────────────────────────────
const PLATFORM_FEE_RATE       = 0.12;   // 12% platform service fee on rental subtotal
const INSURANCE_PROVIDER_RATE = 0.08;   // 8% actual insurance cost (paid to provider)
const INSURANCE_BROKER_MARKUP = 0.04;   // 4% broker fee kept by RTRT
const INSURANCE_TOTAL_RATE    = INSURANCE_PROVIDER_RATE + INSURANCE_BROKER_MARKUP; // 12% total shown to renter
const INSURANCE_MIN_FEE       = 8;      // minimum insurance fee $8
const SECURITY_DEPOSIT_RATE   = 0.20;   // 20% refundable security deposit on rental subtotal

// ─── BADGES & RATING SYSTEM ──────────────────────────────────────────────────
const USER_BADGES = [
  { id:"newcomer",     label:"New Member",       icon:"🌱", color:"#8A93A8", minRentals:0,  maxRentals:0,   minRating:0 },
  { id:"verified",     label:"Verified",         icon:"✅", color:"#2ECC71", minRentals:1,  maxRentals:4,   minRating:0 },
  { id:"trusted",      label:"Trusted",          icon:"🤝", color:"#3B82F6", minRentals:5,  maxRentals:14,  minRating:3.5 },
  { id:"superstar",    label:"Superstar",        icon:"⭐", color:"#F4A235", minRentals:15, maxRentals:49,  minRating:4.0 },
  { id:"elite",        label:"Elite",            icon:"💎", color:"#8B5CF6", minRentals:50, maxRentals:9999,minRating:4.5 },
];
const SPECIAL_BADGES = [
  { id:"fast_responder",  label:"Fast Responder",    icon:"⚡", color:"#F4A235", desc:"Responds within 1 hour" },
  { id:"top_rated",       label:"Top Rated",          icon:"🏆", color:"#F4A235", desc:"4.8+ average rating" },
  { id:"five_star",       label:"5-Star Owner",       icon:"🌟", color:"#FFD700", desc:"Perfect 5.0 rating" },
  { id:"repeat_renter",   label:"Repeat Renter",      icon:"🔄", color:"#2ECC71", desc:"10+ completed rentals" },
  { id:"community_fav",   label:"Community Favorite",  icon:"❤️", color:"#E74C3C", desc:"25+ positive reviews" },
  { id:"on_time",         label:"Always On Time",     icon:"⏰", color:"#3B82F6", desc:"4.8+ on-time rating" },
  { id:"quality_pro",     label:"Quality Pro",        icon:"💯", color:"#8B5CF6", desc:"4.8+ quality rating" },
];
const RATING_CATEGORIES = [
  { key:"overall_rating",       label:"Overall",        icon:"⭐" },
  { key:"communication_rating", label:"Communication",  icon:"💬" },
  { key:"quality_rating",       label:"Quality",        icon:"✨" },
  { key:"ontime_rating",        label:"On Time",        icon:"⏰" },
  { key:"value_rating",         label:"Value",          icon:"💰" },
];
function getUserBadge(totalRentals, avgRating) {
  let badge = USER_BADGES[0];
  for (const b of USER_BADGES) {
    if (totalRentals >= b.minRentals && totalRentals <= b.maxRentals && avgRating >= b.minRating) badge = b;
  }
  return badge;
}
function getSpecialBadges(avgRating, avgOntime, avgQuality, totalReviews, totalRentals) {
  const badges = [];
  if (avgRating >= 5.0 && totalReviews >= 3) badges.push(SPECIAL_BADGES.find(b=>b.id==="five_star"));
  if (avgRating >= 4.8 && totalReviews >= 5) badges.push(SPECIAL_BADGES.find(b=>b.id==="top_rated"));
  if (totalRentals >= 10) badges.push(SPECIAL_BADGES.find(b=>b.id==="repeat_renter"));
  if (totalReviews >= 25) badges.push(SPECIAL_BADGES.find(b=>b.id==="community_fav"));
  if (avgOntime >= 4.8 && totalReviews >= 3) badges.push(SPECIAL_BADGES.find(b=>b.id==="on_time"));
  if (avgQuality >= 4.8 && totalReviews >= 3) badges.push(SPECIAL_BADGES.find(b=>b.id==="quality_pro"));
  return badges.filter(Boolean);
}
function renderStars(rating, size=14) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <span key={i} style={{ fontSize:size, color: i <= Math.round(rating) ? "#F4A235" : "#2A3245" }}>★</span>
    );
  }
  return <span style={{ display:"inline-flex", gap:1 }}>{stars}</span>;
}

const TRAINING_STEPS = {
  "Power Tools":[
    { title:"PPE", desc:"Always wear safety glasses, gloves, and footwear.", q:"What must you wear?", a:"b", opts:["a) Just gloves","b) Safety glasses, gloves, footwear","c) Nothing","d) Only glasses"] },
    { title:"Inspection", desc:"Inspect before every use.", q:"When should you inspect?", a:"c", opts:["a) Monthly","b) When broken","c) Before every use","d) After use only"] },
    { title:"Safe Operation", desc:"Disconnect power when changing accessories.", q:"Changing drill bits, first:", a:"a", opts:["a) Disconnect battery","b) Hold firmly","c) Ask someone","d) Extra gloves"] },
  ],
  "Boats":[
    { title:"Pre-Departure Check", desc:"Check life jackets, fire extinguisher, horn, flares.", q:"Not required on most boats?", a:"d", opts:["a) Life jackets","b) Fire extinguisher","c) Horn","d) GPS unit"] },
    { title:"Navigation Rules", desc:"When two vessels meet head-on, both turn RIGHT.", q:"Two powerboats head-on:", a:"b", opts:["a) Speed up left","b) Both turn starboard","c) Stop","d) Flash lights"] },
    { title:"Emergency Procedures", desc:"Man overboard: throw floatation, mark position, VHF Channel 16.", q:"Marine distress channel:", a:"c", opts:["a) Channel 9","b) Channel 22","c) Channel 16","d) Channel 68"] },
  ],
  "Jet Skis":[
    { title:"PWC Safety", desc:"Always wear life jacket. Attach cut-off lanyard to wrist.", q:"Lanyard attaches to:", a:"b", opts:["a) Handlebars","b) Your wrist","c) The dock","d) Life jacket"] },
    { title:"No-Wake Zones", desc:"Stay 100 feet from swimmers, docks, vessels at speed.", q:"Minimum distance from swimmers:", a:"c", opts:["a) 10 feet","b) 50 feet","c) 100 feet","d) 200 feet"] },
  ],
  "Aircraft":[
    { title:"Pre-Flight (AROW)", desc:"Airworthiness, Registration, Operating handbook, Weight & balance.", q:"AROW stands for:", a:"c", opts:["a) Altitude Route Operations Weather","b) Airspeed Runway Outbound Wind","c) Airworthiness Registration Operating-handbook Weight-balance","d) All Required Operations Worldwide"] },
    { title:"VFR Minimums", desc:"Class B airspace: clear of clouds.", q:"VFR in Class B:", a:"d", opts:["a) 500 below 1000 above","b) 1000 below clear","c) No requirement","d) Clear of clouds"] },
    { title:"Emergency Freq", desc:"MAYDAY on 121.5 MHz. Fly aircraft first.", q:"Aviation emergency frequency:", a:"a", opts:["a) 121.5 MHz","b) 122.8 MHz","c) 118.0 MHz","d) 124.0 MHz"] },
  ],
  "Campers & RVs":[
    { title:"Pre-Trip", desc:"Check tires, propane, slides, awning, hitch.", q:"Turn propane OFF when:", a:"b", opts:["a) At campsite","b) Driving through tunnels","c) Using furnace","d) Sleeping"] },
    { title:"Leveling", desc:"Level before unhitching. Chock all wheels.", q:"Why level RV?", a:"a", opts:["a) Prevents fridge damage","b) Looks better","c) Required everywhere","d) Fuel economy"] },
  ],
  "Motorcycles":[
    { title:"ATGATT", desc:"All The Gear All The Time. DOT helmet, jacket, gloves, boots.", q:"Most important item:", a:"b", opts:["a) Hi-vis vest","b) DOT-approved helmet","c) Knee pads","d) Steel-toed shoes"] },
    { title:"Countersteering", desc:"Push right handlebar to turn right at speed.", q:"To turn right at highway speed:", a:"c", opts:["a) Lean body right","b) Pull left bar","c) Push right bar forward","d) Rear brake only"] },
  ],
  "Heavy Equipment":[
    { title:"Pre-Op Inspection", desc:"Check hydraulics, tracks, fluids, ROPS/FOPS.", q:"ROPS stands for:", a:"a", opts:["a) Roll-Over Protective Structure","b) Rear Outrigger Position","c) Remote Operation Protocol","d) Rated Operating Power"] },
    { title:"Spotters", desc:"Spotter mandatory in limited visibility. Never move if spotter is lost.", q:"If you lose sight of spotter:", a:"d", opts:["a) Slow down continue","b) Use horn","c) Switch radio","d) Stop immediately"] },
  ],
  "Construction":[
    { title:"Site Safety", desc:"Clear work area. Bystanders 10 feet minimum.", q:"Minimum bystander distance:", a:"c", opts:["a) 2 feet","b) 5 feet","c) 10 feet","d) 1 foot"] },
    { title:"Load Safety", desc:"Never exceed rated capacity. Wear hearing protection.", q:"Exceeding capacity causes:", a:"a", opts:["a) Equipment failure","b) Faster work","c) Nothing","d) Better results"] },
  ],
  "Outdoor Equipment":[
    { title:"Pressure Washer", desc:"Never point at people or animals. Start on lowest setting.", q:"First rule of pressure washer:", a:"b", opts:["a) Use highest pressure","b) Never point at people","c) Wear sandals","d) Indoors only"] },
    { title:"Chemical Handling", desc:"Wear chemical-resistant gloves and eye protection.", q:"Using detergents, wear:", a:"d", opts:["a) Mix freely","b) No precautions","c) Smell first","d) Gloves and eye protection"] },
  ],
};

const ALL_CATEGORIES = ["All","Boats","Jet Skis","Aircraft","Campers & RVs","Automobiles","Motorcycles","Heavy Equipment","Power Tools","Outdoor Equipment","Construction","Garden","Events","Electronics","Sports & Recreation","Appliances","Clothing & Fashion","Costumes & Cosplay","Furniture","Camera & Film","Musical Instruments","Party & Entertainment","Baby & Kids","Wedding","Camping & Hiking","Fitness Equipment","Drones","Trailers","Storage & Moving","Inflatables","Luxury & Designer","Seasonal & Holiday","Farm Equipment","Medical Equipment","AV & Sound","Office Equipment"];
const FEATURED_CATS  = ["All","Boats","Jet Skis","Aircraft","Campers & RVs","Automobiles","Motorcycles","Heavy Equipment","Clothing & Fashion","Costumes & Cosplay"];
const CAT_ICONS = { "All":"🏪","Boats":"⛵","Jet Skis":"💦","Aircraft":"✈️","Campers & RVs":"🚐","Automobiles":"🚗","Motorcycles":"🏍️","Heavy Equipment":"🏗️","Power Tools":"🔧","Outdoor Equipment":"💧","Construction":"🪜","Garden":"🌿","Events":"⛺","Electronics":"📱","Sports & Recreation":"⚽","Appliances":"🏠","Clothing & Fashion":"👗","Costumes & Cosplay":"🎭","Furniture":"🛋️","Camera & Film":"📸","Musical Instruments":"🎸","Party & Entertainment":"🎉","Baby & Kids":"👶","Wedding":"💍","Camping & Hiking":"⛺","Fitness Equipment":"💪","Drones":"🚁","Trailers":"🚛","Storage & Moving":"📦","Inflatables":"🎈","Luxury & Designer":"💎","Seasonal & Holiday":"🎄","Farm Equipment":"🚜","Medical Equipment":"🏥","AV & Sound":"🎤","Office Equipment":"🖨️" };

// ─── LOCATION BANNERS: 50 US STATES + TERRITORIES + COUNTRIES ──────────────
// Each location has a respectful story about renting in that area and a Rent This Rent That promo video
const LOCATION_DATA = [
  // ── 50 US States ──
  { name:"Alabama", flag:"🇺🇸", abbr:"AL", img:"https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=300&h=120&fit=crop", story:"From the Gulf Shores beaches to the rolling hills of the Appalachian foothills, Alabama is a state rich in culture, hospitality, and Southern charm. Rent This Rent That connects Alabama communities — whether you need a boat for Lake Martin, camping gear for Cheaha Mountain, or party supplies for a Birmingham block party. Neighbor helping neighbor — that's the Alabama way.", videoId:"alabama-rtrt" },
  { name:"Alaska", flag:"🇺🇸", abbr:"AK", img:"https://images.unsplash.com/photo-1531176175280-109affc7e4dd?w=300&h=120&fit=crop", story:"The Last Frontier is a land of glaciers, wildlife, and breathtaking wilderness. In Alaska, the right equipment can mean everything — from snowmobiles in Fairbanks to fishing boats in Kodiak. Rent This Rent That helps Alaskans share the gear that makes adventure possible, building community across America's most rugged and beautiful state.", videoId:"alaska-rtrt" },
  { name:"Arizona", flag:"🇺🇸", abbr:"AZ", img:"https://images.unsplash.com/photo-1474044159687-1ee9f3a51722?w=300&h=120&fit=crop", story:"Under endless blue skies, Arizona's desert landscapes inspire awe — from the Grand Canyon to the red rocks of Sedona. Rent This Rent That brings Arizona's communities together, offering everything from off-road vehicles for desert trails to camping gear for stargazing under the clearest skies in America.", videoId:"arizona-rtrt" },
  { name:"Arkansas", flag:"🇺🇸", abbr:"AR", img:"https://images.unsplash.com/photo-1584483766114-2cea6facdf57?w=300&h=120&fit=crop", story:"The Natural State lives up to its name with hot springs, Ozark forests, and crystal-clear rivers. In Arkansas, community means sharing what you have. Rent This Rent That connects folks from Little Rock to Fayetteville — kayaks for the Buffalo River, tools for weekend projects, and everything in between.", videoId:"arkansas-rtrt" },
  { name:"California", flag:"🇺🇸", abbr:"CA", img:"https://images.unsplash.com/photo-1449034446853-66c86144b0ad?w=300&h=120&fit=crop", story:"From the Golden Gate to the sun-drenched shores of San Diego, California is a land of innovation and adventure. Rent This Rent That thrives here — surfboards in Malibu, camera gear in Hollywood, mountain bikes in Tahoe. The sharing economy was born here, and we're proud to be part of California's creative spirit.", videoId:"california-rtrt" },
  { name:"Colorado", flag:"🇺🇸", abbr:"CO", img:"https://images.unsplash.com/photo-1546156929-a4c0ac411f47?w=300&h=120&fit=crop", story:"With 58 peaks over 14,000 feet, Colorado is an outdoor paradise. Rent This Rent That helps Coloradans access the gear they need — ski equipment in Aspen, camping gear for Rocky Mountain National Park, or a trailer for a Denver move. Adventure is better when it's shared.", videoId:"colorado-rtrt" },
  { name:"Connecticut", flag:"🇺🇸", abbr:"CT", img:"https://images.unsplash.com/photo-1601220335939-ae391b64e659?w=300&h=120&fit=crop", story:"Where colonial history meets modern New England charm, Connecticut offers everything from Long Island Sound sailing to scenic autumn leaf-peeping. Rent This Rent That brings Connecticut neighbors together — boats for Mystic harbor, event supplies for Hartford festivals, and garden tools for charming suburban yards.", videoId:"connecticut-rtrt" },
  { name:"Delaware", flag:"🇺🇸", abbr:"DE", img:"https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=300&h=120&fit=crop", story:"The First State may be small, but its heart is mighty. From the beaches of Rehoboth to the historic streets of Wilmington, Delaware's tight-knit communities thrive on neighborly sharing. Rent This Rent That makes it easy to borrow what you need from the people right next door.", videoId:"delaware-rtrt" },
  { name:"Florida", flag:"🇺🇸", abbr:"FL", img:"https://images.unsplash.com/photo-1506966953602-c20cc11f75e3?w=300&h=120&fit=crop", story:"The Sunshine State — where crystal-clear springs, world-famous beaches, and vibrant culture create endless opportunity. Rent This Rent That connects Florida's diverse communities with boats for the Keys, jet skis in Miami, fishing gear on the Gulf Coast, and party supplies for every celebration under the sun.", videoId:"florida-rtrt" },
  { name:"Georgia", flag:"🇺🇸", abbr:"GA", img:"https://images.unsplash.com/photo-1575931953324-fcac7094999d?w=300&h=120&fit=crop", story:"From the vibrant streets of Atlanta to the moss-draped oaks of Savannah, Georgia is a state of warmth and grace. Rent This Rent That honors Georgia's tradition of hospitality by connecting communities — whether it's fishing boats on Lake Lanier or equipment for a peach orchard.", videoId:"georgia-rtrt" },
  { name:"Hawaii", flag:"🌺", abbr:"HI", img:"https://images.unsplash.com/photo-1507876466758-bc54f384809c?w=300&h=120&fit=crop", story:"Aloha! In the spirit of ohana (family), Hawaii's island communities have always shared with one another. Rent This Rent That carries on that tradition — surfboards on the North Shore, snorkel gear in Maui, camping equipment for Na Pali Coast. Mahalo for being part of our island family.", videoId:"hawaii-rtrt" },
  { name:"Idaho", flag:"🇺🇸", abbr:"ID", img:"https://images.unsplash.com/photo-1472396961693-142e6e269027?w=300&h=120&fit=crop", story:"The Gem State is a hidden treasure of mountain rivers, vast wilderness, and friendly towns. In Idaho, neighbors look out for each other. Rent This Rent That connects Boise to Sun Valley and everywhere between — rafting gear for the Salmon River, ski equipment, and farm tools for the harvest.", videoId:"idaho-rtrt" },
  { name:"Illinois", flag:"🇺🇸", abbr:"IL", img:"https://images.unsplash.com/photo-1494522855154-9297ac14b55f?w=300&h=120&fit=crop", story:"From Chicago's magnificent skyline to the fertile farmland downstate, Illinois is a place where big-city energy meets heartland values. Rent This Rent That serves the whole state — event equipment for Wrigleyville block parties, farm equipment in Springfield, and everything in between.", videoId:"illinois-rtrt" },
  { name:"Indiana", flag:"🇺🇸", abbr:"IN", img:"https://images.unsplash.com/photo-1564604761379-cff8c04fe571?w=300&h=120&fit=crop", story:"The Crossroads of America is where Hoosier hospitality shines. Whether it's tailgate gear for the Indy 500, camping supplies for Brown County, or power tools in Indianapolis, Rent This Rent That brings Indiana's generous communities together. Here, sharing isn't just smart — it's who we are.", videoId:"indiana-rtrt" },
  { name:"Iowa", flag:"🇺🇸", abbr:"IA", img:"https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=300&h=120&fit=crop", story:"Iowa's golden fields stretch to the horizon, and the people here are as genuine as the land. Rent This Rent That helps Iowa communities share what they have — farm equipment during harvest, party supplies for state fair celebrations, and boats for the Mississippi River.", videoId:"iowa-rtrt" },
  { name:"Kansas", flag:"🇺🇸", abbr:"KS", img:"https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=300&h=120&fit=crop", story:"In the heartland of America, Kansas teaches us that wide-open spaces and close-knit communities go hand in hand. Rent This Rent That connects Kansans from Wichita to Topeka — farm equipment for wheat season, outdoor gear for the Flint Hills, and everything a sunflower state family needs.", videoId:"kansas-rtrt" },
  { name:"Kentucky", flag:"🇺🇸", abbr:"KY", img:"https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=300&h=120&fit=crop", story:"Bluegrass country, where rolling horse farms meet Appalachian heritage and bourbon tradition. Rent This Rent That celebrates Kentucky's spirit — boats for Lake Cumberland, event supplies for Derby parties, camping gear for Red River Gorge, and tools for Louisville's creative community.", videoId:"kentucky-rtrt" },
  { name:"Louisiana", flag:"🇺🇸", abbr:"LA", img:"https://images.unsplash.com/photo-1568402102990-bc541580b59f?w=300&h=120&fit=crop", story:"Laissez les bons temps rouler! Louisiana's vibrant culture of music, food, and celebration is one of a kind. Rent This Rent That embraces the Bayou State spirit — boats for the swamps, party equipment for Mardi Gras, fishing gear for the Gulf, and sound systems for jazz festivals in New Orleans.", videoId:"louisiana-rtrt" },
  { name:"Maine", flag:"🇺🇸", abbr:"ME", img:"https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=300&h=120&fit=crop", story:"Where lighthouses stand sentinel over rocky coastlines and lobster boats dot the harbors, Maine is a place of rugged beauty and resilient people. Rent This Rent That connects Maine communities — boats for Casco Bay, camping gear for Acadia, and snowmobiles for winter adventures.", videoId:"maine-rtrt" },
  { name:"Maryland", flag:"🇺🇸", abbr:"MD", img:"https://images.unsplash.com/photo-1569161031678-50e7dd12b555?w=300&h=120&fit=crop", story:"America in miniature — from the Chesapeake Bay to the Appalachian Mountains, Maryland packs incredible diversity into one state. Rent This Rent That helps Marylanders share boats for crabbing season, kayaks for the bay, and event equipment for Baltimore's famous Inner Harbor celebrations.", videoId:"maryland-rtrt" },
  { name:"Massachusetts", flag:"🇺🇸", abbr:"MA", img:"https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=300&h=120&fit=crop", story:"Where American independence was born and innovation never stopped. From Cape Cod beaches to the Berkshire hills, Massachusetts blends history with progress. Rent This Rent That connects communities across the Commonwealth — sailboats in Martha's Vineyard, ski gear for the Berkshires, and tools for Boston renovations.", videoId:"massachusetts-rtrt" },
  { name:"Michigan", flag:"🇺🇸", abbr:"MI", img:"https://images.unsplash.com/photo-1575072772588-6fccb9bc8257?w=300&h=120&fit=crop", story:"Surrounded by the Great Lakes, Michigan is water wonderland. Rent This Rent That connects Michiganders from Detroit to Traverse City — boats for Lake Michigan, camping gear for the Upper Peninsula, jet skis for summer fun, and tools for Motor City makers.", videoId:"michigan-rtrt" },
  { name:"Minnesota", flag:"🇺🇸", abbr:"MN", img:"https://images.unsplash.com/photo-1505228395891-9a51e7e86bf6?w=300&h=120&fit=crop", story:"The Land of 10,000 Lakes — where ice fishing, canoeing, and northern hospitality define the culture. Rent This Rent That helps Minnesotans share fishing boats, snowmobiles, camping gear for the Boundary Waters, and everything that makes Minnesota nice even nicer.", videoId:"minnesota-rtrt" },
  { name:"Mississippi", flag:"🇺🇸", abbr:"MS", img:"https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=300&h=120&fit=crop", story:"Along the banks of the mighty river that bears its name, Mississippi's deep roots in music, storytelling, and community shine bright. Rent This Rent That honors Mississippi's generous spirit — fishing boats for the Delta, event gear for blues festivals, and tools for building together.", videoId:"mississippi-rtrt" },
  { name:"Missouri", flag:"🇺🇸", abbr:"MO", img:"https://images.unsplash.com/photo-1568402102990-bc541580b59f?w=300&h=120&fit=crop", story:"The Show-Me State, where the Gateway Arch welcomes all and the Ozarks offer natural wonder. Rent This Rent That brings Missouri communities together — boats for the Lake of the Ozarks, camping gear for Mark Twain National Forest, and equipment for Kansas City BBQ competitions.", videoId:"missouri-rtrt" },
  { name:"Montana", flag:"🇺🇸", abbr:"MT", img:"https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=120&fit=crop", story:"Big Sky Country — where the horizons stretch forever and Glacier National Park takes your breath away. Rent This Rent That helps Montanans access fishing gear for blue-ribbon trout streams, camping equipment for wilderness adventures, and the tools that keep ranch life running.", videoId:"montana-rtrt" },
  { name:"Nebraska", flag:"🇺🇸", abbr:"NE", img:"https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=300&h=120&fit=crop", story:"The Cornhusker State — where the Great Plains meet genuine Midwestern values. In Nebraska, people look after each other. Rent This Rent That connects Omaha to the Sandhills, sharing farm equipment, event supplies for Husker tailgates, and outdoor gear for the state's hidden natural treasures.", videoId:"nebraska-rtrt" },
  { name:"Nevada", flag:"🇺🇸", abbr:"NV", img:"https://images.unsplash.com/photo-1581351721010-8cf859cb14a4?w=300&h=120&fit=crop", story:"From the dazzling lights of Las Vegas to the serene beauty of Lake Tahoe, Nevada is a land of contrast and excitement. Rent This Rent That serves Nevada's diverse communities — event equipment for Vegas celebrations, off-road vehicles for desert exploration, and boats for Lake Mead.", videoId:"nevada-rtrt" },
  { name:"New Hampshire", flag:"🇺🇸", abbr:"NH", img:"https://images.unsplash.com/photo-1601220335939-ae391b64e659?w=300&h=120&fit=crop", story:"Live Free or Die — New Hampshire's motto reflects its independent spirit. From the White Mountains to Lake Winnipesaukee, this state is a four-season playground. Rent This Rent That connects Granite Staters with ski gear, boats, hiking equipment, and leaf-peeping essentials.", videoId:"newhampshire-rtrt" },
  { name:"New Jersey", flag:"🇺🇸", abbr:"NJ", img:"https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=300&h=120&fit=crop", story:"The Garden State — where the Jersey Shore meets pine barrens and diverse communities thrive. Rent This Rent That brings New Jerseyans together, from beach gear in Cape May to boats for Barnegat Bay and tools for renovating those charming Victorian homes.", videoId:"newjersey-rtrt" },
  { name:"New Mexico", flag:"🇺🇸", abbr:"NM", img:"https://images.unsplash.com/photo-1474044159687-1ee9f3a51722?w=300&h=120&fit=crop", story:"The Land of Enchantment, where ancient Pueblo cultures, Spanish heritage, and stunning desert landscapes create a truly magical place. Rent This Rent That respects New Mexico's rich traditions while connecting communities — camping gear for White Sands, event equipment for fiesta season, and more.", videoId:"newmexico-rtrt" },
  { name:"New York", flag:"🇺🇸", abbr:"NY", img:"https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=300&h=120&fit=crop", story:"The Empire State — from the bright lights of Manhattan to the Adirondack wilderness and the vineyards of the Finger Lakes. Rent This Rent That serves all of New York, connecting city dwellers who need event gear with upstate adventurers seeking boats, camping equipment, and more.", videoId:"newyork-rtrt" },
  { name:"North Carolina", flag:"🇺🇸", abbr:"NC", img:"https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=300&h=120&fit=crop", story:"First in flight and first in community spirit. From the Outer Banks to the Blue Ridge Parkway, North Carolina offers mountain-to-sea beauty. Rent This Rent That connects Tar Heel communities with boats, camping gear, event supplies, and the equipment that keeps this great state moving.", videoId:"northcarolina-rtrt" },
  { name:"North Dakota", flag:"🇺🇸", abbr:"ND", img:"https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=300&h=120&fit=crop", story:"Where the Great Plains meet the Badlands and Theodore Roosevelt's beloved wilderness. North Dakota's tight-knit communities have always relied on each other. Rent This Rent That continues that tradition — farm equipment for harvest, snowmobiles for winter, and boats for Lake Sakakawea.", videoId:"northdakota-rtrt" },
  { name:"Ohio", flag:"🇺🇸", abbr:"OH", img:"https://images.unsplash.com/photo-1564604761379-cff8c04fe571?w=300&h=120&fit=crop", story:"The Buckeye State — birthplace of aviation and home to a spirit of innovation. From Cleveland's rock and roll soul to Cincinnati's riverfront charm, Ohio communities thrive on connection. Rent This Rent That brings Ohioans together with boats for Lake Erie, event gear, and tools for every project.", videoId:"ohio-rtrt" },
  { name:"Oklahoma", flag:"🇺🇸", abbr:"OK", img:"https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=300&h=120&fit=crop", story:"Where the wind comes sweepin' down the plain — Oklahoma is a place of resilience, diversity, and strong community bonds. Rent This Rent That honors Oklahoma's generous spirit with boats for Grand Lake, event supplies for powwows and festivals, and farm equipment for the heartland.", videoId:"oklahoma-rtrt" },
  { name:"Oregon", flag:"🇺🇸", abbr:"OR", img:"https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=300&h=120&fit=crop", story:"The Beaver State — where Pacific coastline, volcanic peaks, and lush forests create an outdoor paradise. Oregonians are natural sharers. Rent This Rent That fits right in, connecting communities with camping gear for Crater Lake, surfboards for Cannon Beach, and tools for Portland's maker scene.", videoId:"oregon-rtrt" },
  { name:"Pennsylvania", flag:"🇺🇸", abbr:"PA", img:"https://images.unsplash.com/photo-1569161031678-50e7dd12b555?w=300&h=120&fit=crop", story:"The Keystone State — where independence was declared and community still thrives. From Philadelphia's historic streets to Pittsburgh's rebirth and the Poconos' beauty, Pennsylvania is made for sharing. Rent This Rent That connects neighbors with everything from boats to construction equipment.", videoId:"pennsylvania-rtrt" },
  { name:"Rhode Island", flag:"🇺🇸", abbr:"RI", img:"https://images.unsplash.com/photo-1572120360610-d971b9d7767c?w=300&h=120&fit=crop", story:"The Ocean State — small in size but huge in character. Rhode Island's coastline, historic Newport mansions, and vibrant communities make it a gem. Rent This Rent That helps Rhode Islanders share sailboats for Narragansett Bay, event gear for summer festivals, and tools for seaside living.", videoId:"rhodeisland-rtrt" },
  { name:"South Carolina", flag:"🇺🇸", abbr:"SC", img:"https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=300&h=120&fit=crop", story:"Where Southern grace meets coastal beauty. Charleston's charm, Myrtle Beach's fun, and the upcountry's rolling hills make South Carolina special. Rent This Rent That embraces Palmetto State hospitality — boats for Lake Murray, beach gear for Hilton Head, and event supplies for every occasion.", videoId:"southcarolina-rtrt" },
  { name:"South Dakota", flag:"🇺🇸", abbr:"SD", img:"https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=120&fit=crop", story:"Home to Mount Rushmore, the Badlands, and the sacred Black Hills. South Dakota's wide-open spaces and proud heritage inspire all who visit. Rent This Rent That connects communities here — camping gear for Custer State Park, motorcycles for the Sturgis rally, and equipment for ranch life.", videoId:"southdakota-rtrt" },
  { name:"Tennessee", flag:"🇺🇸", abbr:"TN", img:"https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=300&h=120&fit=crop", story:"The Volunteer State — where Nashville's music and Memphis's soul create America's soundtrack. Tennessee's spirit of volunteering extends to sharing. Rent This Rent That brings Tennesseans together with sound equipment for live music, boats for Norris Lake, and camping gear for the Smoky Mountains.", videoId:"tennessee-rtrt" },
  { name:"Texas", flag:"🇺🇸", abbr:"TX", img:"https://images.unsplash.com/photo-1531218150217-54595bc2b934?w=300&h=120&fit=crop", story:"Everything's bigger in Texas — including the generosity. From the Hill Country to the Gulf Coast, the Panhandle to the Rio Grande Valley, Texas communities are strong. Rent This Rent That serves the Lone Star State with boats, RVs, ranch equipment, party supplies, and true Texas-sized hospitality.", videoId:"texas-rtrt" },
  { name:"Utah", flag:"🇺🇸", abbr:"UT", img:"https://images.unsplash.com/photo-1474044159687-1ee9f3a51722?w=300&h=120&fit=crop", story:"The Beehive State — where five national parks showcase some of Earth's most stunning landscapes. Utah's communities work together like bees in a hive. Rent This Rent That connects adventurers with camping gear for Zion, ski equipment for Park City, and boats for Lake Powell.", videoId:"utah-rtrt" },
  { name:"Vermont", flag:"🇺🇸", abbr:"VT", img:"https://images.unsplash.com/photo-1601220335939-ae391b64e659?w=300&h=120&fit=crop", story:"The Green Mountain State — where covered bridges, maple syrup, and autumn colors create a storybook setting. Vermont's small-town values and neighborly spirit make Rent This Rent That a natural fit. Ski gear for Stowe, kayaks for Lake Champlain, and farm equipment for Vermont's beloved local farms.", videoId:"vermont-rtrt" },
  { name:"Virginia", flag:"🇺🇸", abbr:"VA", img:"https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=300&h=120&fit=crop", story:"Where American history lives and breathes — from Jamestown to Shenandoah Valley. Virginia's rich heritage and diverse landscapes make it extraordinary. Rent This Rent That serves the Old Dominion with boats for the Chesapeake, camping gear for the Blue Ridge, and tools for historic preservation.", videoId:"virginia-rtrt" },
  { name:"Washington", flag:"🇺🇸", abbr:"WA", img:"https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=300&h=120&fit=crop", story:"The Evergreen State — where Mount Rainier towers above, Puget Sound glistens, and innovation thrives from Seattle to Spokane. Rent This Rent That connects Washington communities with boats for the San Juan Islands, camping gear for Olympic National Park, and tech equipment for the creative class.", videoId:"washington-rtrt" },
  { name:"West Virginia", flag:"🇺🇸", abbr:"WV", img:"https://images.unsplash.com/photo-1508193638397-1c4234db14d8?w=300&h=120&fit=crop", story:"Almost heaven — that's how John Denver described West Virginia, and he was right. The Mountain State's wild rivers, ancient forests, and Appalachian culture are treasures. Rent This Rent That helps Mountain State communities share rafting gear for the New River Gorge, ATVs for trails, and more.", videoId:"westvirginia-rtrt" },
  { name:"Wisconsin", flag:"🇺🇸", abbr:"WI", img:"https://images.unsplash.com/photo-1575072772588-6fccb9bc8257?w=300&h=120&fit=crop", story:"America's Dairyland — where cheese curds, Green Bay Packers, and lake country define the good life. Wisconsin's community spirit runs deep. Rent This Rent That connects Badger State folks with boats for Door County, ice fishing gear, snowmobiles, and tailgating equipment for Lambeau Field.", videoId:"wisconsin-rtrt" },
  { name:"Wyoming", flag:"🇺🇸", abbr:"WY", img:"https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=120&fit=crop", story:"The Cowboy State — home to Yellowstone, Grand Teton, and wide-open ranges where the West is still wild. Wyoming's small population means neighbors truly depend on each other. Rent This Rent That supports that bond with camping gear, ranch equipment, and adventure gear for the great outdoors.", videoId:"wyoming-rtrt" },
  // ── US Territories ──
  { name:"Puerto Rico", flag:"🇵🇷", abbr:"PR", img:"https://images.unsplash.com/photo-1548574505-5e239809ee19?w=300&h=120&fit=crop", story:"La Isla del Encanto! Puerto Rico's vibrant culture, bioluminescent bays, and tropical rainforests make it magical. Rent This Rent That celebrates the island's spirit of community — boats for island hopping, event gear for festivals, and beach equipment for some of the world's most beautiful coastline.", videoId:"puertorico-rtrt" },
  // ── Countries ──
  { name:"Canada", flag:"🇨🇦", abbr:"CA", img:"https://images.unsplash.com/photo-1503614472-8c93d56e92ce?w=300&h=120&fit=crop", story:"From the Rocky Mountains to the Maritime provinces, Canada's vast beauty and welcoming culture are legendary. Rent This Rent That connects Canadians coast to coast — canoes for Algonquin, ski gear for Whistler, camping equipment for Banff, and tools for communities from Vancouver to Halifax.", videoId:"canada-rtrt" },
  { name:"Mexico", flag:"🇲🇽", abbr:"MX", img:"https://images.unsplash.com/photo-1518638150340-f706e86654de?w=300&h=120&fit=crop", story:"A land of ancient civilizations, stunning coastlines, and vibrant traditions. Mexico's warmth and generosity are unmatched. Rent This Rent That is honored to serve Mexican communities — boats for the Sea of Cortez, event gear for fiestas, and equipment that brings people together in celebration.", videoId:"mexico-rtrt" },
  { name:"United Kingdom", flag:"🇬🇧", abbr:"UK", img:"https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=300&h=120&fit=crop", story:"From the Scottish Highlands to the white cliffs of Dover, the UK blends centuries of tradition with modern innovation. Rent This Rent That connects communities across England, Scotland, Wales, and Northern Ireland — boats for the Lake District, event gear for festivals, and equipment for every British adventure.", videoId:"uk-rtrt" },
  { name:"Australia", flag:"🇦🇺", abbr:"AU", img:"https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=300&h=120&fit=crop", story:"G'day! The Land Down Under — where the Great Barrier Reef, outback adventures, and legendary beaches await. Australians are natural sharers, and Rent This Rent That fits right into the Aussie spirit of mateship. Surfboards, camping gear, boats, and everything for outdoor life under the Southern Cross.", videoId:"australia-rtrt" },
  { name:"Caribbean", flag:"🏝️", abbr:"CB", img:"https://images.unsplash.com/photo-1548574505-5e239809ee19?w=300&h=120&fit=crop", story:"Sun, sea, and the rhythm of island life — the Caribbean is paradise on Earth. Rent This Rent That brings island communities together, sharing boats for sailing the turquoise waters, snorkel gear for coral reefs, and event equipment for the festivals that make Caribbean culture shine.", videoId:"caribbean-rtrt" },
  { name:"Europe", flag:"🇪🇺", abbr:"EU", img:"https://images.unsplash.com/photo-1491557345352-5929e343eb89?w=300&h=120&fit=crop", story:"A continent of diverse cultures, stunning architecture, and timeless beauty. From the Alps to the Mediterranean, Europe's communities value sharing and sustainability. Rent This Rent That connects Europeans with everything they need — bikes for Amsterdam, camping gear for the Alps, and boats for the coast.", videoId:"europe-rtrt" },
  { name:"Costa Rica", flag:"🇨🇷", abbr:"CR", img:"https://images.unsplash.com/photo-1519999482648-25049ddd37b1?w=300&h=120&fit=crop", story:"Pura Vida! Costa Rica's philosophy of pure life and its incredible biodiversity make it truly special. Rent This Rent That embraces the Pura Vida spirit, connecting communities with surfboards for the Pacific coast, hiking gear for cloud forests, and equipment for eco-adventures.", videoId:"costarica-rtrt" },
  { name:"Japan", flag:"🇯🇵", abbr:"JP", img:"https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=300&h=120&fit=crop", story:"Where ancient tradition meets cutting-edge innovation. Japan's respect for community and craftsmanship is an inspiration. Rent This Rent That honors Japanese values of sharing and caring for shared resources — ski gear for Hokkaido, camping equipment for Fuji, and everything for exploring this remarkable country.", videoId:"japan-rtrt" },
  { name:"New Zealand", flag:"🇳🇿", abbr:"NZ", img:"https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=300&h=120&fit=crop", story:"Kia ora! Aotearoa — the land of the long white cloud. New Zealand's stunning fjords, mountains, and Maori heritage create a uniquely special place. Rent This Rent That connects Kiwi communities with camping gear for Milford Sound, boats for the Bay of Islands, and adventure equipment for this outdoor paradise.", videoId:"newzealand-rtrt" },
  { name:"Brazil", flag:"🇧🇷", abbr:"BR", img:"https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=300&h=120&fit=crop", story:"The land of Carnival, the Amazon, and incredible beaches. Brazil's energy, diversity, and love of life are contagious. Rent This Rent That celebrates Brazilian community spirit — surfboards for Copacabana, boats for the Amazon, event equipment for Carnival celebrations, and so much more.", videoId:"brazil-rtrt" },
  { name:"Germany", flag:"🇩🇪", abbr:"DE", img:"https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=300&h=120&fit=crop", story:"Engineering excellence meets breathtaking landscapes — from the Black Forest to the Baltic Sea. Germany's culture of precision and community makes Rent This Rent That a natural fit. Bikes for the Rhine Valley, camping gear for Bavaria, and tools for every Meister project.", videoId:"germany-rtrt" },
  { name:"France", flag:"🇫🇷", abbr:"FR", img:"https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=300&h=120&fit=crop", story:"The art of living — France inspires the world with its culture, cuisine, and joie de vivre. Rent This Rent That connects French communities from Paris to Provence, sharing bikes for wine country, boats for the Côte d'Azur, and equipment for the art of enjoying life beautifully.", videoId:"france-rtrt" },
  { name:"Italy", flag:"🇮🇹", abbr:"IT", img:"https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=300&h=120&fit=crop", story:"Bella Italia! From the Amalfi Coast to the Dolomites, Italy's beauty, history, and passion for life are unmatched. Rent This Rent That celebrates Italian community — boats for Lake Como, Vespa scooters for Tuscany, and event equipment for festivals that bring la famiglia together.", videoId:"italy-rtrt" },
  { name:"Spain", flag:"🇪🇸", abbr:"ES", img:"https://images.unsplash.com/photo-1543783207-ec64e4d95325?w=300&h=120&fit=crop", story:"Vibrant, passionate, and full of life — Spain's diverse regions each tell their own beautiful story. Rent This Rent That embraces the Spanish spirit of sharing — surfboards for San Sebastián, boats for the Balearic Islands, and fiesta equipment for celebrations that last into the night.", videoId:"spain-rtrt" },
  { name:"South Korea", flag:"🇰🇷", abbr:"KR", img:"https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=300&h=120&fit=crop", story:"Where K-culture meets ancient temples and stunning mountain landscapes. South Korea's innovative spirit and community values align perfectly with Rent This Rent That — camping gear for Seoraksan, tech equipment for Seoul's creative scene, and outdoor gear for Jeju Island adventures.", videoId:"southkorea-rtrt" },
  { name:"India", flag:"🇮🇳", abbr:"IN", img:"https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=300&h=120&fit=crop", story:"A land of incredible diversity — from the Himalayas to tropical Kerala, bustling Mumbai to peaceful Rajasthan. India's tradition of community and sharing is timeless. Rent This Rent That connects Indian communities with outdoor gear, event equipment for festivals, and tools for building tomorrow.", videoId:"india-rtrt" },
  { name:"Philippines", flag:"🇵🇭", abbr:"PH", img:"https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?w=300&h=120&fit=crop", story:"Over 7,000 islands of paradise, incredible hospitality, and the warmth of the Filipino spirit. Rent This Rent That embraces the Bayanihan tradition of community cooperation — boats for island-hopping, diving gear for world-class reefs, and event equipment for every fiesta.", videoId:"philippines-rtrt" },
  { name:"Colombia", flag:"🇨🇴", abbr:"CO", img:"https://images.unsplash.com/photo-1526392060635-9d6019884377?w=300&h=120&fit=crop", story:"The land of magical realism, emerald landscapes, and the most welcoming people you'll ever meet. Colombia's transformation and vibrant culture inspire us all. Rent This Rent That connects Colombian communities with adventure gear, event supplies, and the tools to build bright futures together.", videoId:"colombia-rtrt" },
];

// Stock images for categories when listing has no photo
const CAT_IMAGES = {
  "Boats":"https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=600&h=400&fit=crop",
  "Jet Skis":"https://images.unsplash.com/photo-1626862858089-37939fa6c1f6?w=600&h=400&fit=crop",
  "Aircraft":"https://images.unsplash.com/photo-1474302770737-173ee21bab63?w=600&h=400&fit=crop",
  "Campers & RVs":"https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=600&h=400&fit=crop",
  "Automobiles":"https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=600&h=400&fit=crop",
  "Motorcycles":"https://images.unsplash.com/photo-1558981403-c5f9899a28bc?w=600&h=400&fit=crop",
  "Heavy Equipment":"https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=400&fit=crop",
  "Power Tools":"https://images.unsplash.com/photo-1504148455328-c376907d081c?w=600&h=400&fit=crop",
  "Outdoor Equipment":"https://images.unsplash.com/photo-1445307806294-bff7f67ff225?w=600&h=400&fit=crop",
  "Construction":"https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&h=400&fit=crop",
  "Garden":"https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&h=400&fit=crop",
  "Events":"https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600&h=400&fit=crop",
  "Electronics":"https://images.unsplash.com/photo-1498049794561-7780e7231661?w=600&h=400&fit=crop",
  "Sports & Recreation":"https://images.unsplash.com/photo-1461896836934-bd45ba6343f1?w=600&h=400&fit=crop",
  "Appliances":"https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&h=400&fit=crop",
  "Clothing & Fashion":"https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&h=400&fit=crop",
  "Costumes & Cosplay":"https://images.unsplash.com/photo-1509248961406-6fbd73ce1a36?w=600&h=400&fit=crop",
  "Furniture":"https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600&h=400&fit=crop",
  "Camera & Film":"https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=600&h=400&fit=crop",
  "Musical Instruments":"https://images.unsplash.com/photo-1511192336575-5a79af67a629?w=600&h=400&fit=crop",
  "Party & Entertainment":"https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&h=400&fit=crop",
  "Baby & Kids":"https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=600&h=400&fit=crop",
  "Wedding":"https://images.unsplash.com/photo-1519741497674-611481863552?w=600&h=400&fit=crop",
  "Camping & Hiking":"https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=600&h=400&fit=crop",
  "Fitness Equipment":"https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&h=400&fit=crop",
  "Drones":"https://images.unsplash.com/photo-1507582020474-9a35b7d455d9?w=600&h=400&fit=crop",
  "Trailers":"https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=400&fit=crop",
  "Storage & Moving":"https://images.unsplash.com/photo-1600518464441-9154a4dea21b?w=600&h=400&fit=crop",
  "Inflatables":"https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=600&h=400&fit=crop",
  "Luxury & Designer":"https://images.unsplash.com/photo-1490427712608-588e68359dab?w=600&h=400&fit=crop",
  "Seasonal & Holiday":"https://images.unsplash.com/photo-1512389142860-9c449e58a814?w=600&h=400&fit=crop",
  "Farm Equipment":"https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=600&h=400&fit=crop",
  "Medical Equipment":"https://images.unsplash.com/photo-1516549655169-df83a0774514?w=600&h=400&fit=crop",
  "AV & Sound":"https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=600&h=400&fit=crop",
  "Office Equipment":"https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop",
};

// ─── SHARED STYLE HELPERS (mobile-first) ─────────────────────────────────────
const S = {
  app:   { minHeight:"100vh", background:COLORS.bg, fontFamily:"'Georgia',serif", color:COLORS.text },
  header:{ background:COLORS.surface, borderBottom:`1px solid ${COLORS.border}`, padding:"8px 12px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8, position:"sticky", top:0, zIndex:100 },
  logo:  { fontFamily:"'Georgia',serif", fontSize:16, fontWeight:"bold", color:COLORS.accent, letterSpacing:1 },
  logoSub: { color:COLORS.muted, fontSize:9, letterSpacing:2, textTransform:"uppercase", display:"none" },
  main:  { maxWidth:1120, margin:"0 auto", padding:"16px 10px" },
  grid:  { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:12 },
  card:  { background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14, overflow:"hidden", cursor:"pointer", transition:"transform 0.2s, border-color 0.2s" },
  cardImg:{ width:"100%", height:160, background:`linear-gradient(135deg,#2A3245 0%,#1E2433 100%)`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:50, position:"relative" },
  cardBody:{ padding:10 },
  badge: c=>({ display:"inline-block", padding:"3px 8px", borderRadius:20, fontSize:11, fontWeight:700, background:c+"22", color:c, border:`1px solid ${c}44`, marginRight:4, marginBottom:3 }),
  btn:   (v="primary",sz="md")=>({
    padding: sz==="lg"?"14px 28px":sz==="sm"?"8px 14px":"10px 18px",
    borderRadius:8, border:"none", cursor:"pointer",
    fontSize:sz==="lg"?16:sz==="sm"?13:14, fontWeight:700,
    background: v==="primary"?COLORS.accent:v==="green"?COLORS.green:v==="red"?COLORS.red:v==="outline"?"transparent":v==="purple"?COLORS.purple:COLORS.card,
    color: ["primary","green","red"].includes(v)?"#000":v==="purple"?"#fff":v==="outline"?COLORS.accent:COLORS.text,
    border: v==="outline"?`1px solid ${COLORS.accent}`:"none",
    transition:"opacity 0.2s",
    whiteSpace:"nowrap",
  }),
  input: { width:"100%", background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:8, padding:"11px 13px", color:COLORS.text, fontSize:16, outline:"none", boxSizing:"border-box" },
  label: { fontSize:12, color:COLORS.muted, fontWeight:600, marginBottom:5, display:"block", textTransform:"uppercase", letterSpacing:1 },
  fg:    { marginBottom:17 },
  row:   { display:"flex", gap:13, flexWrap:"wrap" },
  modal: { position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:999, padding:10 },
  mbox:  { background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:16, padding:20, maxWidth:600, width:"100%", maxHeight:"90vh", overflowY:"auto" },
  chkRow:{ display:"flex", alignItems:"flex-start", gap:10, marginBottom:10, cursor:"pointer" },
  dlvBtn:a=>({ flex:1, padding:12, borderRadius:10, border:`2px solid ${a?COLORS.accent:COLORS.border}`, background:a?COLORS.accent+"11":COLORS.surface, cursor:"pointer", textAlign:"center", transition:"all 0.2s", minWidth:80 }),
};

// ─── IMAGE HELPERS ────────────────────────────────────────────────────────────
function parseImageUrls(imageUrl) {
  if (!imageUrl) return [];
  try { const parsed = JSON.parse(imageUrl); return Array.isArray(parsed) ? parsed : [imageUrl]; } catch { return [imageUrl]; }
}

function ImageCarousel({ images, height = 160 }) {
  const [idx, setIdx] = useState(0);
  const [failedSrcs, setFailedSrcs] = useState(new Set());
  const handleImgError = (e, src) => { e.target.onerror=null; setFailedSrcs(prev => new Set([...prev, src])); e.target.src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop"; };
  const validImages = images.filter(src => !failedSrcs.has(src));
  const displayImages = validImages.length > 0 ? validImages : ["https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop"];
  if (!images || images.length === 0) return null;
  if (displayImages.length === 1) return <img src={displayImages[0]} alt="" loading="lazy" style={{ width:"100%", height, objectFit:"cover" }} onError={e=>handleImgError(e,displayImages[0])} />;
  return (
    <div style={{ position:"relative", width:"100%", height }}>
      <img src={displayImages[idx % displayImages.length]} alt="" loading="lazy" style={{ width:"100%", height, objectFit:"cover" }} onError={e=>handleImgError(e,displayImages[idx % displayImages.length])} />
      {/* Left arrow */}
      <button onClick={e=>{ e.stopPropagation(); setIdx(i=>(i-1+images.length)%images.length); }} style={{ position:"absolute", left:4, top:"50%", transform:"translateY(-50%)", background:"rgba(0,0,0,0.55)", color:"#fff", border:"none", borderRadius:"50%", width:26, height:26, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
      {/* Right arrow */}
      <button onClick={e=>{ e.stopPropagation(); setIdx(i=>(i+1)%images.length); }} style={{ position:"absolute", right:4, top:"50%", transform:"translateY(-50%)", background:"rgba(0,0,0,0.55)", color:"#fff", border:"none", borderRadius:"50%", width:26, height:26, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>
      {/* Dots */}
      <div style={{ position:"absolute", bottom:6, left:"50%", transform:"translateX(-50%)", display:"flex", gap:4 }}>
        {images.map((_, i) => (
          <span key={i} style={{ width:6, height:6, borderRadius:"50%", background: i===idx ? "#fff" : "rgba(255,255,255,0.4)", transition:"background 0.2s" }} />
        ))}
      </div>
    </div>
  );
}

// ─── SEND BOOKING CONFIRMATION EMAILS ─────────────────────────────────────────
// ─── STAR RATING INPUT ───────────────────────────────────────────────────────
function StarRatingInput({ value, onChange, size=24 }) {
  const [hover, setHover] = useState(0);
  return (
    <span style={{ display:"inline-flex", gap:2, cursor:"pointer" }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ fontSize:size, color: i <= (hover||value) ? "#F4A235" : "#2A3245", transition:"color 0.15s" }}
          onMouseEnter={()=>setHover(i)} onMouseLeave={()=>setHover(0)} onClick={()=>onChange(i)}>★</span>
      ))}
    </span>
  );
}

// ─── REVIEW MODAL ────────────────────────────────────────────────────────────
function ReviewModal({ booking, user, onClose, onSaved }) {
  const [ratings, setRatings] = useState({ overall_rating:0, communication_rating:0, quality_rating:0, ontime_rating:0, value_rating:0 });
  const [reviewText, setReviewText] = useState("");
  const [saving, setSaving] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [ownerIdResolved, setOwnerIdResolved] = useState(null);

  const listingId = booking.listing_id || booking.listings?.id;
  const isOwner = booking.listings?.owner_id === user?.id;
  const reviewType = isOwner ? "owner_to_renter" : "renter_to_owner";
  const revieweeName = isOwner ? (booking.profiles?.full_name || "Renter") : (booking.listings?.profiles?.full_name || booking.listings?.owner || "Owner");

  useEffect(() => {
    (async () => {
      // Check for existing review
      const { data } = await supabase.from("reviews").select("*").eq("booking_id", booking.id).eq("reviewer_id", user.id).maybeSingle();
      if (data) {
        setExistingReview(data);
        setRatings({ overall_rating:data.overall_rating, communication_rating:data.communication_rating||0, quality_rating:data.quality_rating||0, ontime_rating:data.ontime_rating||0, value_rating:data.value_rating||0 });
        setReviewText(data.review_text||"");
      }
      // Resolve owner_id from listing if not available
      if (listingId && !booking.listings?.owner_id) {
        const { data:listing } = await supabase.from("listings").select("owner_id").eq("id", listingId).maybeSingle();
        if (listing) setOwnerIdResolved(listing.owner_id);
      }
    })();
  }, [booking.id, user.id, listingId]);

  const handleSubmit = async () => {
    if (ratings.overall_rating === 0) { setErrorMsg("Please rate at least the Overall category."); return; }
    setErrorMsg("");
    setSaving(true);
    const resolvedOwnerId = booking.listings?.owner_id || ownerIdResolved;
    const revieweeId = isOwner ? booking.renter_id : resolvedOwnerId;
    const reviewData = {
      booking_id: booking.id,
      listing_id: listingId,
      reviewer_id: user.id,
      reviewee_id: revieweeId || null,
      review_type: reviewType,
      ...ratings,
      review_text: reviewText.trim() || null,
    };
    let err;
    if (existingReview) {
      const { error } = await supabase.from("reviews").update(reviewData).eq("id", existingReview.id);
      err = error;
    } else {
      const { error } = await supabase.from("reviews").insert(reviewData);
      err = error;
    }
    setSaving(false);
    if (err) { console.error("Review error:", err); setErrorMsg(err.message || "Failed to save review. Please try again."); return; }
    if (onSaved) onSaved();
    onClose();
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mbox, maxWidth:480 }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:20, fontWeight:"bold" }}>{existingReview ? "Update Review" : "Leave a Review"}</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:COLORS.muted, fontSize:22, cursor:"pointer" }}>✕</button>
        </div>

        <div style={{ background:COLORS.surface, borderRadius:10, padding:12, marginBottom:18, border:`1px solid ${COLORS.border}` }}>
          <div style={{ fontWeight:700, fontSize:15, marginBottom:2 }}>{booking.listings?.title}</div>
          <div style={{ color:COLORS.muted, fontSize:12 }}>{booking.start_date} → {booking.end_date}</div>
          <div style={{ color:COLORS.muted, fontSize:12, marginTop:4 }}>Reviewing: <strong style={{ color:COLORS.text }}>{revieweeName}</strong> as {isOwner ? "renter" : "owner"}</div>
        </div>

        {RATING_CATEGORIES.map(cat => (
          <div key={cat.key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, padding:"8px 0", borderBottom:`1px solid ${COLORS.border}22` }}>
            <div style={{ fontSize:14, fontWeight:600 }}>{cat.icon} {cat.label} {cat.key==="overall_rating" && <span style={{ color:COLORS.red }}>*</span>}</div>
            <StarRatingInput value={ratings[cat.key]} onChange={v => setRatings(r=>({...r, [cat.key]:v}))} />
          </div>
        ))}

        <div style={S.fg}>
          <label style={S.label}>Written Review (optional)</label>
          <textarea value={reviewText} onChange={e=>setReviewText(e.target.value)} placeholder="Share your experience..." rows={3}
            style={{ ...S.input, resize:"vertical", minHeight:70, fontFamily:"inherit" }} />
        </div>

        {errorMsg && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:"8px 12px", marginBottom:10, color:COLORS.red, fontSize:13 }}>{errorMsg}</div>}

        <button onClick={handleSubmit} disabled={saving}
          style={{ ...S.btn(ratings.overall_rating>0?"primary":"outline","lg"), width:"100%", textAlign:"center", opacity: ratings.overall_rating===0?0.5:1 }}>
          {saving ? "Saving..." : existingReview ? "Update Review" : "Submit Review"}
        </button>
      </div>
    </div>
  );
}

// ─── REVIEWS DISPLAY COMPONENT ───────────────────────────────────────────────
function ReviewsSection({ listingId, ownerId }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      let query = supabase.from("reviews").select("*, profiles:reviewer_id(full_name, avatar_url)").order("created_at", { ascending:false });
      if (listingId) query = query.eq("listing_id", listingId);
      else if (ownerId) query = query.eq("reviewee_id", ownerId);
      const { data } = await query.limit(10);
      setReviews(data || []);
      setLoading(false);
    })();
  }, [listingId, ownerId]);

  if (loading) return <div style={{ color:COLORS.muted, fontSize:12, padding:8 }}>Loading reviews...</div>;
  if (reviews.length === 0) return <div style={{ color:COLORS.muted, fontSize:13, padding:8, textAlign:"center" }}>No reviews yet</div>;

  const avgOverall = reviews.reduce((s,r)=>s+r.overall_rating, 0) / reviews.length;
  const avgComm = reviews.filter(r=>r.communication_rating).reduce((s,r)=>s+(r.communication_rating||0), 0) / (reviews.filter(r=>r.communication_rating).length||1);
  const avgQuality = reviews.filter(r=>r.quality_rating).reduce((s,r)=>s+(r.quality_rating||0), 0) / (reviews.filter(r=>r.quality_rating).length||1);
  const avgOntime = reviews.filter(r=>r.ontime_rating).reduce((s,r)=>s+(r.ontime_rating||0), 0) / (reviews.filter(r=>r.ontime_rating).length||1);
  const avgValue = reviews.filter(r=>r.value_rating).reduce((s,r)=>s+(r.value_rating||0), 0) / (reviews.filter(r=>r.value_rating).length||1);

  const userBadge = getUserBadge(reviews.length, avgOverall);
  const specials = getSpecialBadges(avgOverall, avgOntime, avgQuality, reviews.length, reviews.length);

  return (
    <div>
      {/* Rating summary */}
      <div style={{ background:COLORS.surface, borderRadius:10, padding:14, marginBottom:14, border:`1px solid ${COLORS.border}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
          <div style={{ fontSize:32, fontWeight:"bold", color:COLORS.accent }}>{avgOverall.toFixed(1)}</div>
          <div>
            {renderStars(avgOverall, 18)}
            <div style={{ color:COLORS.muted, fontSize:12 }}>{reviews.length} review{reviews.length!==1?"s":""}</div>
          </div>
        </div>
        {/* Category breakdowns */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
          {[{l:"Communication",v:avgComm},{l:"Quality",v:avgQuality},{l:"On Time",v:avgOntime},{l:"Value",v:avgValue}].map(({l,v})=>(
            <div key={l} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12 }}>
              <span style={{ color:COLORS.muted }}>{l}</span>
              <span style={{ display:"flex", alignItems:"center", gap:4 }}>{renderStars(v, 10)} <span style={{ color:COLORS.text, fontWeight:600 }}>{v.toFixed(1)}</span></span>
            </div>
          ))}
        </div>
        {/* Badges */}
        {(specials.length > 0 || userBadge.id !== "newcomer") && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:10, paddingTop:10, borderTop:`1px solid ${COLORS.border}` }}>
            {userBadge.id !== "newcomer" && <span style={{ ...S.badge(userBadge.color), fontSize:11 }}>{userBadge.icon} {userBadge.label}</span>}
            {specials.map(b => <span key={b.id} style={{ ...S.badge(b.color), fontSize:11 }}>{b.icon} {b.label}</span>)}
          </div>
        )}
      </div>

      {/* Individual reviews */}
      {reviews.map(r => (
        <div key={r.id} style={{ background:COLORS.card, borderRadius:10, padding:12, marginBottom:8, border:`1px solid ${COLORS.border}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {r.profiles?.avatar_url ? (
                <img src={r.profiles.avatar_url} alt="" style={{ width:30, height:30, borderRadius:"50%", objectFit:"cover" }} />
              ) : (
                <div style={{ width:30, height:30, borderRadius:"50%", background:`linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#000" }}>
                  {(r.profiles?.full_name || "?")[0].toUpperCase()}
                </div>
              )}
              <div>
                <div style={{ fontWeight:700, fontSize:13 }}>{r.profiles?.full_name || "Anonymous"}</div>
                <div style={{ fontSize:10, color:COLORS.muted }}>{new Date(r.created_at).toLocaleDateString()}</div>
              </div>
            </div>
            {renderStars(r.overall_rating, 14)}
          </div>
          {r.review_text && <div style={{ color:COLORS.muted, fontSize:13, lineHeight:1.5, fontStyle:"italic" }}>"{r.review_text}"</div>}
          {/* Mini category ratings */}
          <div style={{ display:"flex", gap:10, marginTop:6, flexWrap:"wrap" }}>
            {r.communication_rating && <span style={{ fontSize:10, color:COLORS.muted }}>💬 {r.communication_rating}</span>}
            {r.quality_rating && <span style={{ fontSize:10, color:COLORS.muted }}>✨ {r.quality_rating}</span>}
            {r.ontime_rating && <span style={{ fontSize:10, color:COLORS.muted }}>⏰ {r.ontime_rating}</span>}
            {r.value_rating && <span style={{ fontSize:10, color:COLORS.muted }}>💰 {r.value_rating}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── EMAIL TEMPLATE SYSTEM ───────────────────────────────────────────────────
function emailWrapper(content) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f4f4f4;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f4;padding:20px 0;">
<tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="background:#1a1a2e;border-radius:12px;overflow:hidden;max-width:600px;width:100%;">
<tr><td style="background:linear-gradient(135deg,#F4A235,#e8912a);padding:24px 32px;text-align:center;">
<img src="https://rentthisrentthat.com/logo.png" alt="RTRT" width="40" height="40" style="vertical-align:middle;margin-right:10px;border-radius:8px;">
<span style="color:#1a1a2e;font-size:22px;font-weight:800;vertical-align:middle;">Rent This Rent That</span>
</td></tr>
<tr><td style="padding:32px;color:#e0e0e0;font-size:15px;line-height:1.7;">
${content}
</td></tr>
<tr><td style="padding:20px 32px;border-top:1px solid #2a2a4a;text-align:center;">
<a href="https://rentthisrentthat.com" style="color:#F4A235;text-decoration:none;font-size:13px;font-weight:600;">rentthisrentthat.com</a>
<div style="color:#666;font-size:11px;margin-top:8px;">Rent This Rent That LLC &bull; Peer-to-Peer Rental Platform</div>
</td></tr>
</table>
</td></tr></table></body></html>`;
}

function bookingConfirmationEmail({ recipientName, role, itemTitle, category, dates, days, deliveryLabel, address, rentalCost, platformFee, insuranceFee, deliveryFee, securityDeposit, total, otherPartyName }) {
  const isRenter = role === "renter";
  return emailWrapper(`
<h2 style="color:#F4A235;margin:0 0 8px;font-size:20px;">${isRenter ? "Booking Confirmed!" : "Your Item Was Rented!"}</h2>
<p style="margin:0 0 20px;color:#aaa;">${isRenter ? "Your rental has been confirmed. Here are the details:" : "Great news! Someone has rented your item."}</p>
<table width="100%" cellpadding="8" cellspacing="0" style="background:#16162b;border-radius:8px;margin-bottom:20px;">
<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Item</td><td style="color:#fff;font-size:14px;font-weight:600;border-bottom:1px solid #2a2a4a;">${itemTitle}</td></tr>
<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Category</td><td style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">${category}</td></tr>
<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Dates</td><td style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">${dates.start} &rarr; ${dates.end} (${days} day${days!==1?"s":""})</td></tr>
<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Fulfillment</td><td style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">${deliveryLabel}</td></tr>
${address?`<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Address</td><td style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">${address}</td></tr>`:""}
<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">${isRenter?"Owner":"Renter"}</td><td style="color:#F4A235;font-size:14px;font-weight:600;border-bottom:1px solid #2a2a4a;">${otherPartyName}</td></tr>
</table>
<table width="100%" cellpadding="8" cellspacing="0" style="background:#16162b;border-radius:8px;margin-bottom:20px;">
<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Rental (${days} day${days!==1?"s":""})</td><td align="right" style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">$${rentalCost}</td></tr>
${platformFee>0?`<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Service Fee</td><td align="right" style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">$${platformFee}</td></tr>`:""}
${insuranceFee>0?`<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Insurance</td><td align="right" style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">$${insuranceFee}</td></tr>`:""}
${deliveryFee>0?`<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Delivery</td><td align="right" style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">$${deliveryFee}</td></tr>`:""}
${securityDeposit>0?`<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Security Deposit (refundable)</td><td align="right" style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">$${securityDeposit}</td></tr>`:""}
<tr><td style="color:#F4A235;font-size:15px;font-weight:700;">Total</td><td align="right" style="color:#F4A235;font-size:18px;font-weight:800;">$${total}</td></tr>
</table>
<p style="color:#aaa;font-size:13px;margin:0;">${isRenter?"Funds are held in escrow by Rent This Rent That LLC until rental completion.":"Funds will be released upon successful completion of the rental period."}</p>
  `);
}

function welcomeEmail(name) {
  return emailWrapper(`
<h2 style="color:#F4A235;margin:0 0 8px;font-size:22px;">Welcome to Rent This Rent That!</h2>
<p>Hi ${name},</p>
<p>Thanks for joining the peer-to-peer rental marketplace. Whether you want to rent something or earn money from items you already own, you're in the right place.</p>
<h3 style="color:#F4A235;font-size:16px;margin:24px 0 12px;">Get Started</h3>
<table width="100%" cellpadding="12" cellspacing="0" style="background:#16162b;border-radius:8px;margin-bottom:20px;">
<tr><td style="border-bottom:1px solid #2a2a4a;">
<strong style="color:#fff;">Browse Listings</strong><br><span style="color:#aaa;font-size:13px;">Find boats, jet skis, RVs, tools, vehicles and more from owners near you.</span>
</td></tr>
<tr><td style="border-bottom:1px solid #2a2a4a;">
<strong style="color:#fff;">List Your Items</strong><br><span style="color:#aaa;font-size:13px;">Got something sitting in the garage? List it and start earning.</span>
</td></tr>
<tr><td>
<strong style="color:#fff;">Secure Payments</strong><br><span style="color:#aaa;font-size:13px;">All transactions are protected with escrow and optional insurance.</span>
</td></tr>
</table>
<div style="text-align:center;margin:24px 0;">
<a href="https://rentthisrentthat.com" style="background:#F4A235;color:#1a1a2e;padding:14px 32px;border-radius:8px;text-decoration:none;font-weight:700;font-size:15px;display:inline-block;">Start Browsing</a>
</div>
  `);
}

function rentalReminderEmail({ recipientName, itemTitle, startDate, endDate, deliveryLabel, address, role, type }) {
  const isStart = type === "start";
  const isRenter = role === "renter";
  return emailWrapper(`
<h2 style="color:#F4A235;margin:0 0 8px;font-size:20px;">${isStart ? "Your Rental Starts Tomorrow!" : "Rental Ending Soon"}</h2>
<p>Hi ${recipientName},</p>
<p>${isStart
  ? (isRenter ? `Your rental of <strong style="color:#fff;">${itemTitle}</strong> starts tomorrow (${startDate}).` : `Your item <strong style="color:#fff;">${itemTitle}</strong> is being rented starting tomorrow (${startDate}).`)
  : (isRenter ? `Your rental of <strong style="color:#fff;">${itemTitle}</strong> ends tomorrow (${endDate}). Please arrange to return the item.` : `The rental of your item <strong style="color:#fff;">${itemTitle}</strong> ends tomorrow (${endDate}).`)
}</p>
<table width="100%" cellpadding="8" cellspacing="0" style="background:#16162b;border-radius:8px;margin:16px 0;">
<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Item</td><td style="color:#fff;font-size:14px;font-weight:600;border-bottom:1px solid #2a2a4a;">${itemTitle}</td></tr>
<tr><td style="color:#888;font-size:13px;border-bottom:1px solid #2a2a4a;">Rental Period</td><td style="color:#e0e0e0;font-size:14px;border-bottom:1px solid #2a2a4a;">${startDate} &rarr; ${endDate}</td></tr>
<tr><td style="color:#888;font-size:13px;">Fulfillment</td><td style="color:#e0e0e0;font-size:14px;">${deliveryLabel}${address?" &bull; "+address:""}</td></tr>
</table>
${isStart && isRenter ? `<p style="color:#aaa;font-size:13px;">Make sure to inspect the item upon receipt and report any issues within 24 hours.</p>` : ""}
${!isStart && isRenter ? `<p style="color:#aaa;font-size:13px;">Please return the item in the same condition it was received. Any damage should be reported through the platform.</p>` : ""}
${!isStart && !isRenter ? `<p style="color:#aaa;font-size:13px;">Inspect the item upon return. If there are any issues, you can file a damage claim through your dashboard.</p>` : ""}
  `);
}

function reviewRequestEmail({ recipientName, itemTitle, otherPartyName, role }) {
  return emailWrapper(`
<h2 style="color:#F4A235;margin:0 0 8px;font-size:20px;">How Was Your Rental?</h2>
<p>Hi ${recipientName},</p>
<p>Your rental of <strong style="color:#fff;">${itemTitle}</strong> with ${otherPartyName} has been completed. We'd love to hear how it went!</p>
<p>Reviews help build trust in the community and help other users make better decisions.</p>
<div style="text-align:center;margin:24px 0;">
<a href="https://rentthisrentthat.com" style="background:#F4A235;color:#1a1a2e;padding:14px 32px;border-radius:8px;text-decoration:none;font-weight:700;font-size:15px;display:inline-block;">Leave a Review</a>
</div>
<p style="color:#aaa;font-size:13px;">Sign in and go to "My Rentals" to submit your review.</p>
  `);
}

async function sendEmail({ to, subject, body, html }) {
  try {
    await fetch('/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to, subject, body, html }),
    });
  } catch(err) { console.warn("Email delivery failed for", to, err); }
}

async function sendBookingEmails({ item, renterProfile, dates, delivery, address, total, deliveryFee, insuranceFee, platformFee, securityDeposit, days }) {
  const ownerName = item.profiles?.full_name || item.owner || "Lister";
  const ownerEmail = item.profiles?.email || null;
  const renterName = renterProfile?.full_name || "Renter";
  const renterEmail = renterProfile?.email || null;
  const platformEmail = "rentthisrentthat@proton.me";
  const deliveryLabel = delivery==="self"?"Self Pickup & Return":delivery==="dropoff"?"Drop-Off & Pick-Up":"Full Service";
  const rentalCost = item.price_per_day * days;

  const bookingDetails = `Item: ${item.title} (${item.category})\nDates: ${dates.start} to ${dates.end} (${days} day${days!==1?"s":""})\nFulfillment: ${deliveryLabel}${address?"\nAddress: "+address:""}\nRental: $${rentalCost}\nService Fee: $${platformFee||0}\nInsurance: $${insuranceFee}\n${deliveryFee>0?"Delivery: $"+deliveryFee+"\n":""}Security Deposit: $${securityDeposit||0} (refundable)\nTotal Charged: $${total}`;

  const emailArgs = { itemTitle:item.title, category:item.category, dates, days, deliveryLabel, address, rentalCost, platformFee:platformFee||0, insuranceFee, deliveryFee, securityDeposit:securityDeposit||0, total };

  try {
    const notifications = [
      { recipient_type:"platform", recipient_email:platformEmail, subject:`New Booking: ${item.title}`, body:`New booking from ${renterName}.\n\nOwner: ${ownerName}\n${bookingDetails}`, status:"pending" },
      renterEmail ? { recipient_type:"renter", recipient_email:renterEmail, subject:`Booking Confirmed: ${item.title}`, body: bookingDetails, html: bookingConfirmationEmail({ recipientName:renterName, role:"renter", ...emailArgs, otherPartyName:ownerName }), status:"pending" } : null,
      ownerEmail ? { recipient_type:"owner", recipient_email:ownerEmail, subject:`Your Item Was Rented: ${item.title}`, body: bookingDetails, html: bookingConfirmationEmail({ recipientName:ownerName, role:"owner", ...emailArgs, otherPartyName:renterName }), status:"pending" } : null,
    ].filter(Boolean);

    await supabase.from("booking_notifications").insert(notifications.map(n => ({ recipient_type:n.recipient_type, recipient_email:n.recipient_email, subject:n.subject, body:n.body, status:n.status })));

    for (const notif of notifications) {
      await sendEmail({ to:notif.recipient_email, subject:notif.subject, body:notif.body, html:notif.html });
    }
  } catch(e) { console.error("Email notification error:", e); }
}

async function sendWelcomeEmail(email, name) {
  if (!email) return;
  try {
    await sendEmail({ to:email, subject:"Welcome to Rent This Rent That!", html: welcomeEmail(name||"there") });
  } catch(e) { console.warn("Welcome email error:", e); }
}

async function sendRentalReminderEmail({ recipientEmail, recipientName, itemTitle, startDate, endDate, deliveryLabel, address, role, type }) {
  if (!recipientEmail) return;
  const subj = type==="start" ? `Reminder: Your Rental of ${itemTitle} Starts Tomorrow` : `Reminder: Your Rental of ${itemTitle} Ends Tomorrow`;
  try {
    await sendEmail({ to:recipientEmail, subject:subj, html: rentalReminderEmail({ recipientName, itemTitle, startDate, endDate, deliveryLabel, address, role, type }) });
  } catch(e) { console.warn("Reminder email error:", e); }
}

async function sendReviewRequestEmail({ recipientEmail, recipientName, itemTitle, otherPartyName, role }) {
  if (!recipientEmail) return;
  try {
    await sendEmail({ to:recipientEmail, subject:`How was your rental of ${itemTitle}?`, html: reviewRequestEmail({ recipientName, itemTitle, otherPartyName, role }) });
  } catch(e) { console.warn("Review request email error:", e); }
}

// ─── AVAILABILITY CALENDAR COMPONENT ──────────────────────────────────────────
function AvailabilityCalendar({ listingId, blockedDates=[], bookedDates=[], onToggleDate, editable=false, onSelectRange, selectedStart, selectedEnd }) {
  const [currentMonth, setCurrentMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const monthName = currentMonth.toLocaleString("default",{month:"long",year:"numeric"});
  const today = new Date(); today.setHours(0,0,0,0);

  const fmt = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

  const isBlocked = dateStr => blockedDates.includes(dateStr);
  const isBooked = dateStr => bookedDates.includes(dateStr);
  const isPast = dateStr => new Date(dateStr+"T00:00:00") < today;

  const isInRange = dateStr => {
    if (!selectedStart || !selectedEnd) return false;
    return dateStr >= selectedStart && dateStr <= selectedEnd;
  };

  const handleDayClick = (dateStr) => {
    if (isPast(dateStr)) return;
    if (editable) {
      if (onToggleDate) onToggleDate(dateStr);
    } else if (onSelectRange) {
      onSelectRange(dateStr);
    }
  };

  const prevMonth = () => setCurrentMonth(new Date(year, month-1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month+1, 1));

  const cells = [];
  for (let i=0; i<firstDay; i++) cells.push(null);
  for (let d=1; d<=daysInMonth; d++) cells.push(d);

  return (
    <div style={{ background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:14 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <button onClick={prevMonth} style={{ background:"none", border:"none", color:COLORS.text, fontSize:18, cursor:"pointer", padding:"4px 8px" }}>‹</button>
        <div style={{ fontWeight:700, fontSize:14, color:COLORS.text }}>{monthName}</div>
        <button onClick={nextMonth} style={{ background:"none", border:"none", color:COLORS.text, fontSize:18, cursor:"pointer", padding:"4px 8px" }}>›</button>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2, textAlign:"center", marginBottom:6 }}>
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d=>(
          <div key={d} style={{ fontSize:10, fontWeight:700, color:COLORS.muted, padding:4 }}>{d}</div>
        ))}
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const dateStr = fmt(new Date(year, month, day));
          const past = isPast(dateStr);
          const blocked = isBlocked(dateStr);
          const booked = isBooked(dateStr);
          const inRange = isInRange(dateStr);
          const isStart = dateStr === selectedStart;
          const isEnd = dateStr === selectedEnd;
          const unavailable = blocked || booked || past;

          let bg = "transparent", color = COLORS.text, border = "2px solid transparent";
          if (past) { color = COLORS.muted+"66"; }
          else if (blocked) { bg = COLORS.red+"22"; color = COLORS.red; border = `2px solid ${COLORS.red}44`; }
          else if (booked) { bg = "#f59e0b22"; color = "#f59e0b"; border = "2px solid #f59e0b44"; }
          else if (isStart || isEnd) { bg = COLORS.accent; color = "#fff"; border = `2px solid ${COLORS.accent}`; }
          else if (inRange) { bg = COLORS.accent+"22"; color = COLORS.accent; border = `2px solid ${COLORS.accent}44`; }

          return (
            <div key={dateStr} onClick={()=>!past&&handleDayClick(dateStr)}
              style={{ padding:"6px 2px", borderRadius:8, fontSize:12, fontWeight:600, cursor:past?"default":"pointer", background:bg, color, border, textAlign:"center", position:"relative", transition:"all 0.15s", minHeight:32, display:"flex", alignItems:"center", justifyContent:"center" }}>
              {day}
              {blocked && <span style={{ position:"absolute", bottom:1, left:"50%", transform:"translateX(-50%)", fontSize:7 }}>🚫</span>}
              {booked && !blocked && <span style={{ position:"absolute", bottom:1, left:"50%", transform:"translateX(-50%)", fontSize:7 }}>📅</span>}
            </div>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:12, marginTop:10, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:COLORS.muted }}><span style={{ width:10, height:10, borderRadius:3, background:COLORS.accent, display:"inline-block" }}/> Selected</div>
        {editable && <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:COLORS.muted }}><span style={{ width:10, height:10, borderRadius:3, background:COLORS.red+"44", border:`1px solid ${COLORS.red}`, display:"inline-block" }}/> Blocked</div>}
        <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:COLORS.muted }}><span style={{ width:10, height:10, borderRadius:3, background:"#f59e0b44", border:"1px solid #f59e0b", display:"inline-block" }}/> Booked</div>
        <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, color:COLORS.muted }}><span style={{ width:10, height:10, borderRadius:3, background:COLORS.muted+"22", display:"inline-block" }}/> Past</div>
      </div>
    </div>
  );
}

// ─── MANAGE AVAILABILITY MODAL ────────────────────────────────────────────────
function ManageAvailabilityModal({ item, onClose }) {
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookedDates, setBookedDates]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Load blocked dates
        const { data:blocked } = await supabase.from("listing_blocked_dates").select("blocked_date").eq("listing_id", item.id);
        setBlockedDates((blocked||[]).map(b=>b.blocked_date));

        // Load booked dates from bookings
        const { data:bookings } = await supabase.from("bookings").select("start_date, end_date").eq("listing_id", item.id).in("status",["pending","confirmed"]);
        const booked = [];
        (bookings||[]).forEach(b => {
          let d = new Date(b.start_date+"T00:00:00");
          const end = new Date(b.end_date+"T00:00:00");
          while (d <= end) {
            booked.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
            d.setDate(d.getDate()+1);
          }
        });
        setBookedDates(booked);
      } catch(e) { console.error("Load availability error:",e); }
      setLoading(false);
    };
    load();
  }, [item.id]);

  const toggleDate = (dateStr) => {
    if (bookedDates.includes(dateStr)) return; // can't block a booked date
    setBlockedDates(prev => prev.includes(dateStr) ? prev.filter(d=>d!==dateStr) : [...prev, dateStr]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Delete all existing blocked dates for this listing
      await supabase.from("listing_blocked_dates").delete().eq("listing_id", item.id);
      // Insert new blocked dates
      if (blockedDates.length > 0) {
        await supabase.from("listing_blocked_dates").insert(
          blockedDates.map(d => ({ listing_id: item.id, blocked_date: d }))
        );
      }
      onClose();
    } catch(e) {
      alert("Save failed: " + e.message);
    }
    setSaving(false);
  };

  return (
    <div style={S.modal}>
      <div style={{ ...S.mbox, maxWidth:420 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <div>
            <div style={{ fontWeight:"bold", fontSize:17 }}>📅 Manage Availability</div>
            <div style={{ color:COLORS.muted, fontSize:12 }}>{item.title}</div>
          </div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>

        <div style={{ background:COLORS.accent+"11", border:`1px solid ${COLORS.accent}44`, borderRadius:8, padding:10, marginBottom:14, fontSize:11, color:COLORS.muted, lineHeight:1.5 }}>
          <strong style={{ color:COLORS.accent }}>How it works:</strong> Tap any date to block or unblock it. Blocked dates (🚫 red) prevent renters from booking. Booked dates (📅 yellow) are already reserved and cannot be changed here.
        </div>

        {loading ? (
          <div style={{ textAlign:"center", padding:40, color:COLORS.muted }}>Loading calendar...</div>
        ) : (
          <AvailabilityCalendar
            listingId={item.id}
            blockedDates={blockedDates}
            bookedDates={bookedDates}
            onToggleDate={toggleDate}
            editable={true}
          />
        )}

        <div style={{ marginTop:14, display:"flex", gap:8 }}>
          <div style={{ flex:1, background:COLORS.surface, borderRadius:8, padding:10, textAlign:"center" }}>
            <div style={{ fontSize:20, fontWeight:700, color:COLORS.red }}>{blockedDates.length}</div>
            <div style={{ fontSize:10, color:COLORS.muted }}>Blocked</div>
          </div>
          <div style={{ flex:1, background:COLORS.surface, borderRadius:8, padding:10, textAlign:"center" }}>
            <div style={{ fontSize:20, fontWeight:700, color:"#f59e0b" }}>{bookedDates.length}</div>
            <div style={{ fontSize:10, color:COLORS.muted }}>Booked</div>
          </div>
        </div>

        <div style={{ ...S.row, marginTop:16 }}>
          <button onClick={onClose} style={{ ...S.btn("outline"), flex:1 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ ...S.btn("green"), flex:2 }}>{saving?"⏳ Saving...":"💾 Save Availability"}</button>
        </div>
      </div>
    </div>
  );
}

// ─── LISTING DETAIL MODAL ─────────────────────────────────────────────────────
// ─── SOCIAL SHARING ─────────────────────────────────────────────────────────
function ShareButtons({ item, size = "sm" }) {
  const [copied, setCopied] = useState(false);
  const url = `https://rentthisrentthat.com/listing/${item.id}`;
  const title = `${item.title} — $${item.price_per_day || item.priceDay}/day on Rent This Rent That`;
  const text = `Check out "${item.title}" for rent at $${item.price_per_day || item.priceDay}/day on Rent This Rent That!`;

  const share = (platform) => {
    const urls = {
      facebook:  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter:   `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      sms:       `sms:?&body=${encodeURIComponent(text + " " + url)}`,
      email:     `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + "\n\n" + url)}`,
    };
    if (urls[platform]) window.open(urls[platform], "_blank", "noopener,noreferrer");
  };

  const copyLink = () => {
    try { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const btnStyle = {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: size === "sm" ? "5px 8px" : "8px 12px",
    cursor: "pointer",
    fontSize: size === "sm" ? 14 : 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.2s, border-color 0.2s",
    minWidth: size === "sm" ? 32 : 38,
    height: size === "sm" ? 32 : 38,
  };

  return (
    <div style={{ display:"flex", gap:6, alignItems:"center" }} onClick={e => e.stopPropagation()}>
      <button onClick={() => share("facebook")} style={btnStyle} title="Share on Facebook">📘</button>
      <button onClick={() => share("twitter")} style={btnStyle} title="Share on X / Twitter">🐦</button>
      <button onClick={() => share("sms")} style={btnStyle} title="Share via text">💬</button>
      <button onClick={() => share("email")} style={btnStyle} title="Share via email">📧</button>
      <button onClick={copyLink} style={{ ...btnStyle, minWidth: size === "sm" ? 60 : 80, fontSize: size === "sm" ? 11 : 12, color: copied ? COLORS.green : COLORS.muted, fontWeight: 600 }} title="Copy link">
        {copied ? "✅ Copied" : "🔗 Copy"}
      </button>
    </div>
  );
}

function ListingDetailModal({ item, onClose, onRentNow }) {
  const images = [...parseImageUrls(item.image_url), ...(parseImageUrls(item.image_url).length === 0 ? [CAT_IMAGES[item.category] || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop"] : [])];
  const [imgIdx, setImgIdx] = useState(0);
  const licReq = LICENSE_REQUIREMENTS[item.category];
  const priceDay = item.price_per_day || item.priceDay || 0;
  const priceWeek = item.price_per_week || item.priceWeek || 0;
  const ownerName = item.profiles?.full_name || item.owner || "Lister";

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mbox, maxWidth:500, padding:0 }} onClick={e=>e.stopPropagation()}>
        {/* Main image */}
        <div style={{ position:"relative", width:"100%", height:280, background:COLORS.surface }}>
          <img src={images[imgIdx]} alt={item.title} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
          {images.length > 1 && <>
            <button onClick={()=>setImgIdx(i=>(i-1+images.length)%images.length)} style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", background:"rgba(0,0,0,0.6)", color:"#fff", border:"none", borderRadius:"50%", width:36, height:36, fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
            <button onClick={()=>setImgIdx(i=>(i+1)%images.length)} style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"rgba(0,0,0,0.6)", color:"#fff", border:"none", borderRadius:"50%", width:36, height:36, fontSize:20, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>›</button>
            <div style={{ position:"absolute", bottom:10, left:"50%", transform:"translateX(-50%)", display:"flex", gap:6 }}>
              {images.map((_, i) => (
                <span key={i} onClick={()=>setImgIdx(i)} style={{ width:8, height:8, borderRadius:"50%", background: i===imgIdx ? "#fff" : "rgba(255,255,255,0.4)", cursor:"pointer", transition:"background 0.2s" }} />
              ))}
            </div>
            <span style={{ position:"absolute", top:10, left:10, background:"rgba(0,0,0,0.6)", color:"#fff", fontSize:12, fontWeight:600, padding:"4px 10px", borderRadius:20 }}>{imgIdx+1} / {images.length}</span>
          </>}
          <button onClick={onClose} style={{ position:"absolute", top:10, right:10, background:"rgba(0,0,0,0.6)", color:"#fff", border:"none", borderRadius:"50%", width:32, height:32, fontSize:16, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
        </div>

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div style={{ display:"flex", gap:4, padding:"8px 12px", overflowX:"auto", background:COLORS.surface }}>
            {images.map((url, i) => (
              <img key={i} src={url} alt="" onClick={()=>setImgIdx(i)} style={{ width:52, height:52, objectFit:"cover", borderRadius:6, cursor:"pointer", border: i===imgIdx ? `2px solid ${COLORS.accent}` : `2px solid transparent`, opacity: i===imgIdx ? 1 : 0.6, transition:"all 0.2s", flexShrink:0 }} />
            ))}
          </div>
        )}

        {/* Details */}
        <div style={{ padding:16 }}>
          <div style={{ fontWeight:700, fontSize:20, marginBottom:4, lineHeight:1.3 }}>{item.title}</div>
          <div style={{ color:COLORS.muted, fontSize:13, marginBottom:10, display:"flex", alignItems:"center", gap:6 }}>
            {item.profiles?.avatar_url ? (
              <img src={item.profiles.avatar_url} alt="" style={{ width:22, height:22, borderRadius:"50%", objectFit:"cover" }} />
            ) : (
              <span style={{ width:22, height:22, borderRadius:"50%", background:`${COLORS.accent}33`, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:COLORS.accent }}>{ownerName[0]}</span>
            )}
            Listed by {ownerName}
          </div>

          <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginBottom:12 }}>
            <span style={S.badge(COLORS.blue)}>{item.category}</span>
            {(item.delivery_available||item.delivery) && <span style={S.badge(COLORS.green)}>🚚 Delivery</span>}
            {licReq?.required && <span style={S.badge(COLORS.purple)}>🪪 License Required</span>}
            <span style={S.badge(COLORS.green)}>🛡️ Insured</span>
            {item.zip_code && <span style={S.badge(COLORS.muted)}>📍 {item.zip_code}</span>}
          </div>

          {/* Share buttons */}
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:COLORS.muted, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Share this listing</div>
            <ShareButtons item={item} size="md" />
          </div>

          {item.description && <div style={{ color:COLORS.muted, fontSize:13, lineHeight:1.5, marginBottom:14, borderTop:`1px solid ${COLORS.border}`, paddingTop:12 }}>{item.description}</div>}

          <div style={{ display:"flex", alignItems:"baseline", gap:12, marginBottom:16 }}>
            <div><span style={{ fontSize:26, fontWeight:"bold", color:COLORS.accent }}>${priceDay}</span><span style={{ color:COLORS.muted, fontSize:14 }}> /day</span></div>
            {priceWeek > 0 && <div><span style={{ fontSize:18, fontWeight:"bold", color:COLORS.text }}>${priceWeek}</span><span style={{ color:COLORS.muted, fontSize:13 }}> /week</span></div>}
          </div>

          <button onClick={()=>{ onClose(); onRentNow(item); }} style={{ ...S.btn("primary","lg"), width:"100%", textAlign:"center", fontSize:16 }}>
            Rent This Item
          </button>

          {/* Reviews section */}
          <div style={{ marginTop:18, paddingTop:14, borderTop:`1px solid ${COLORS.border}` }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:10 }}>Reviews & Ratings</div>
            <ReviewsSection listingId={item.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── AUTH MODAL ────────────────────────────────────────────────────────────────
function AuthModal({ onClose, onAuth }) {
  const [mode, setMode]         = useState("login"); // login | signup | forgot
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [name, setName]         = useState("");
  const [zip, setZip]           = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [resetSent, setResetSent] = useState(false);

  const handle = async () => {
    setLoading(true); setError("");
    try {
      if (mode === "forgot") {
        await resetPassword(email);
        setResetSent(true);
      } else if (mode === "login") {
        const { user } = await signIn(email, password);
        const profile = await getProfile(user.id);
        onAuth(user, profile);
        onClose();
      } else {
        const { user } = await signUp(email, password, name, zip);
        const profile = await getProfile(user.id);
        onAuth(user, profile);
        sendWelcomeEmail(email, name);
        onClose();
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.modal}>
      <div style={S.mbox}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontWeight:"bold", fontSize:19 }}>
            {mode==="login"?"Welcome Back 👋":mode==="signup"?"Create Account 🚀":"Reset Password 🔑"}
          </div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>

        {/* Tab toggle — hide when in forgot mode */}
        {mode !== "forgot" && (
          <div style={{ display:"flex", borderBottom:`1px solid ${COLORS.border}`, marginBottom:20 }}>
            {[["login","Log In"],["signup","Sign Up"]].map(([k,label])=>(
              <button key={k} onClick={()=>setMode(k)} style={{ padding:"10px 20px", border:"none", background:"none", cursor:"pointer", fontSize:13, fontWeight:mode===k?700:500, color:mode===k?COLORS.accent:COLORS.muted, borderBottom:mode===k?`2px solid ${COLORS.accent}`:"2px solid transparent", marginBottom:-1, transition:"all 0.2s" }}>{label}</button>
            ))}
          </div>
        )}

        {/* Forgot password flow */}
        {mode === "forgot" && !resetSent && (
          <>
            <div style={{ color:COLORS.muted, fontSize:13, marginBottom:18, lineHeight:1.6 }}>
              Enter the email address associated with your account and we'll send you a link to reset your password.
            </div>
            <div style={S.fg}><label style={S.label}>Email</label><input style={S.input} type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} /></div>
            {error && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:"10px 14px", fontSize:13, color:COLORS.red, marginBottom:16 }}>{error}</div>}
            <button onClick={handle} disabled={loading||!email} style={{ ...S.btn("primary","lg"), width:"100%", opacity:email?1:0.4 }}>
              {loading?"⏳ Sending...":"Send Reset Link →"}
            </button>
            <div style={{ textAlign:"center", marginTop:14, fontSize:12, color:COLORS.muted }}>
              Remember your password?{" "}
              <span style={{ color:COLORS.accent, cursor:"pointer", fontWeight:700 }} onClick={()=>{setMode("login");setError("");}}>Back to Log In</span>
            </div>
          </>
        )}

        {/* Reset email sent confirmation */}
        {mode === "forgot" && resetSent && (
          <>
            <div style={{ textAlign:"center", padding:"20px 0" }}>
              <div style={{ fontSize:48, marginBottom:12 }}>📧</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:COLORS.text }}>Check Your Email</div>
              <div style={{ color:COLORS.muted, fontSize:13, lineHeight:1.6, marginBottom:20 }}>
                We sent a password reset link to <strong style={{ color:COLORS.text }}>{email}</strong>. Click the link in the email to set a new password.
              </div>
              <div style={{ color:COLORS.muted, fontSize:12, marginBottom:20 }}>
                Didn't get the email? Check your spam folder or{" "}
                <span style={{ color:COLORS.accent, cursor:"pointer", fontWeight:600 }} onClick={()=>{setResetSent(false);setError("");}}>try again</span>.
              </div>
            </div>
            <button onClick={()=>{setMode("login");setResetSent(false);setError("");}} style={{ ...S.btn("primary","lg"), width:"100%" }}>
              Back to Log In →
            </button>
          </>
        )}

        {/* Normal login / signup form */}
        {mode !== "forgot" && (
          <>
            {mode==="signup" && (
              <>
                <div style={S.fg}><label style={S.label}>Full Name</label><input style={S.input} placeholder="Your full name" value={name} onChange={e=>setName(e.target.value)} /></div>
                <div style={S.fg}><label style={S.label}>ZIP Code</label><input style={S.input} placeholder="Your ZIP code" value={zip} onChange={e=>setZip(e.target.value)} maxLength={5} /></div>
              </>
            )}
            <div style={S.fg}><label style={S.label}>Email</label><input style={S.input} type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} /></div>
            <div style={S.fg}><label style={S.label}>Password</label><input style={S.input} type="password" placeholder="••••••••" value={password} onChange={e=>setPassword(e.target.value)} /></div>

            {/* Forgot password link — only on login tab */}
            {mode==="login" && (
              <div style={{ textAlign:"right", marginTop:-8, marginBottom:12 }}>
                <span style={{ color:COLORS.accent, cursor:"pointer", fontSize:12, fontWeight:600 }} onClick={()=>{setMode("forgot");setError("");}}>
                  Forgot password?
                </span>
              </div>
            )}

            {error && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:"10px 14px", fontSize:13, color:COLORS.red, marginBottom:16 }}>{error}</div>}

            <button onClick={handle} disabled={loading||!email||!password} style={{ ...S.btn("primary","lg"), width:"100%", opacity:email&&password?1:0.4 }}>
              {loading?"⏳ Please wait...":(mode==="login"?"Log In →":"Create Account →")}
            </button>

            <div style={{ textAlign:"center", marginTop:14, fontSize:12, color:COLORS.muted }}>
              {mode==="login"?"Don't have an account? ":"Already have an account? "}
              <span style={{ color:COLORS.accent, cursor:"pointer", fontWeight:700 }} onClick={()=>setMode(mode==="login"?"signup":"login")}>
                {mode==="login"?"Sign Up":"Log In"}
              </span>
            </div>

            <div style={{ background:COLORS.accent+"11", border:`1px solid ${COLORS.accent}33`, borderRadius:8, padding:10, marginTop:14, fontSize:11, color:COLORS.muted }}>
              🔒 By creating an account you agree to our broker agreement and platform terms.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── SET NEW PASSWORD MODAL ───────────────────────────────────────────────────
function SetNewPasswordModal({ onClose, onSuccess }) {
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    if (newPass.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (newPass !== confirmPass) { setError("Passwords don't match."); return; }
    setLoading(true); setError("");
    try {
      await updatePassword(newPass);
      setDone(true);
      if (onSuccess) onSuccess();
      // Auto-close after 1.5 seconds so user can start using the app immediately
      setTimeout(() => { if (onClose) onClose(); }, 1500);
    } catch (err) {
      setError(err.message || "Failed to update password. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.modal}>
      <div style={S.mbox}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontWeight:"bold", fontSize:19 }}>Set New Password 🔐</div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>

        {!done ? (
          <>
            <div style={{ color:COLORS.muted, fontSize:13, marginBottom:18, lineHeight:1.6 }}>
              Enter your new password below. Make sure it's at least 6 characters long.
            </div>
            <div style={S.fg}>
              <label style={S.label}>New Password</label>
              <input style={S.input} type="password" placeholder="••••••••" value={newPass} onChange={e=>setNewPass(e.target.value)} />
            </div>
            <div style={S.fg}>
              <label style={S.label}>Confirm Password</label>
              <input style={S.input} type="password" placeholder="••••••••" value={confirmPass} onChange={e=>setConfirmPass(e.target.value)} />
            </div>
            {error && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:"10px 14px", fontSize:13, color:COLORS.red, marginBottom:16 }}>{error}</div>}
            <button onClick={handleSubmit} disabled={loading||!newPass||!confirmPass} style={{ ...S.btn("primary","lg"), width:"100%", opacity:newPass&&confirmPass?1:0.4 }}>
              {loading ? "⏳ Updating..." : "Update Password →"}
            </button>
          </>
        ) : (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:8, color:COLORS.text }}>Password Updated!</div>
            <div style={{ color:COLORS.muted, fontSize:13, lineHeight:1.6, marginBottom:20 }}>
              Your password has been successfully changed. You're all set!
            </div>
            <button onClick={onClose} style={{ ...S.btn("primary","lg"), width:"100%" }}>
              Continue →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TRAINING MODAL ────────────────────────────────────────────────────────────
function TrainingModal({ item, userId, onComplete, onClose }) {
  const steps = TRAINING_STEPS[item.category] || TRAINING_STEPS["Power Tools"];
  const [step, setStep]       = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [correct, setCorrect]   = useState(false);
  const [allDone, setAllDone]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const cur = steps[step];
  const pct = Math.round(((step+(allDone?1:0))/steps.length)*100);

  const handleAnswer = opt => {
    if (answered) return;
    const letter = opt.split(")")[0].trim();
    setSelected(opt); setAnswered(true); setCorrect(letter===cur.a);
  };
  const next = () => {
    if (step < steps.length-1) { setStep(s=>s+1); setSelected(null); setAnswered(false); setCorrect(false); }
    else setAllDone(true);
  };
  const handleComplete = async () => {
    setSaving(true);
    try {
      if (userId) await saveTrainingCompletion(userId, item.category, 100);
    } catch(e) { console.error("Training save error:", e); }
    setSaving(false);
    onComplete();
  };

  return (
    <div style={S.modal}>
      <div style={S.mbox}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div><div style={{ fontWeight:"bold", fontSize:18, marginBottom:2 }}>🎓 Safety Training</div><div style={{ color:COLORS.muted, fontSize:12 }}>{item.title}</div></div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>
        <div style={{ height:7, borderRadius:4, background:COLORS.border, overflow:"hidden", marginBottom:4 }}><div style={{ height:"100%", width:`${pct}%`, background:COLORS.accent, borderRadius:4, transition:"width 0.5s" }} /></div>
        <div style={{ color:COLORS.muted, fontSize:11, marginBottom:16 }}>Step {Math.min(step+1,steps.length)} of {steps.length} — {pct}% complete</div>
        {!allDone ? (
          <div style={{ background:COLORS.surface, border:`1px solid ${COLORS.accent}44`, borderRadius:10, padding:18, marginBottom:14 }}>
            <div style={{ fontWeight:700, fontSize:15, marginBottom:6, color:COLORS.accent }}>{cur.title}</div>
            <div style={{ color:COLORS.muted, marginBottom:14, lineHeight:1.65, fontSize:13 }}>{cur.desc}</div>
            <div style={{ fontWeight:600, marginBottom:10, fontSize:13 }}>{cur.q}</div>
            <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
              {cur.opts.map(opt=>{
                const letter=opt.split(")")[0].trim();
                let bg=COLORS.card, border=COLORS.border;
                if(answered&&selected===opt){bg=correct?COLORS.green+"22":COLORS.red+"22";border=correct?COLORS.green:COLORS.red;}
                else if(answered&&letter===cur.a){bg=COLORS.green+"11";border=COLORS.green+"66";}
                return <div key={opt} onClick={()=>handleAnswer(opt)} style={{ padding:"9px 14px", borderRadius:8, border:`1px solid ${border}`, background:bg, cursor:answered?"default":"pointer", fontSize:13 }}>{opt}</div>;
              })}
            </div>
            {answered&&<div style={{ marginTop:12, padding:10, borderRadius:8, background:correct?COLORS.green+"22":COLORS.red+"22", color:correct?COLORS.green:COLORS.red, fontWeight:600, fontSize:13 }}>{correct?"✅ Correct!":"❌ Correct: "+cur.opts.find(o=>o.startsWith(cur.a))}</div>}
            {answered&&<div style={{ textAlign:"right", marginTop:12 }}><button onClick={next} style={S.btn("primary")}>{step<steps.length-1?"Next →":"Finish Training"}</button></div>}
          </div>
        ) : (
          <div style={{ textAlign:"center", padding:"14px 0" }}>
            <div style={{ fontSize:50, marginBottom:10 }}>🏆</div>
            <div style={{ fontSize:19, fontWeight:"bold", marginBottom:6 }}>Training Complete!</div>
            <div style={{ color:COLORS.muted, marginBottom:18, fontSize:13 }}>Safety certification for <strong>{item.category}</strong> logged to your account.</div>
            <button onClick={handleComplete} disabled={saving} style={{ ...S.btn("green","lg"), width:"100%" }}>{saving?"Saving...":"Continue to Checkout →"}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LICENSE MODAL ─────────────────────────────────────────────────────────────
function LicenseModal({ item, userId, onVerified, onClose }) {
  const req = LICENSE_REQUIREMENTS[item.category];
  const icon = LICENSE_ICONS[req.licenseType] || "📄";
  const fileInputRef = useRef(null);
  const [file, setFile]     = useState(null);
  const [licNum, setLicNum] = useState("");
  const [expiry, setExpiry] = useState("");
  const [issuer, setIssuer] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified]   = useState(false);
  const [error, setError]         = useState("");
  const [uploadProgress, setUploadProgress] = useState("");

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const maxSize = 10 * 1024 * 1024;
    if (selected.size > maxSize) {
      setError("File too large. Maximum size is 10 MB. Please compress or re-scan your document.");
      return;
    }
    const allowedTypes = ["image/jpeg","image/png","image/gif","image/webp","image/heic","application/pdf"];
    if (!allowedTypes.includes(selected.type) && !selected.name.match(/\.(jpg|jpeg|png|gif|webp|heic|pdf)$/i)) {
      setError("Invalid file type. Please upload a JPG, PNG, or PDF file.");
      return;
    }
    setError("");
    setFile(selected);
  };

  const handleVerify = async () => {
    if (!file||!licNum||!expiry||!agreed) return;
    setVerifying(true); setError(""); setUploadProgress("Uploading document...");
    try {
      let documentPath = null;
      if (userId) {
        setUploadProgress("Uploading document securely...");
        documentPath = await uploadLicenseDocument(file, userId, req.licenseType);
        setUploadProgress("Saving verification record...");
        await submitLicenseVerification({
          userId, licenseType:req.licenseType, licenseLabel:req.licenseLabel,
          licenseNumber:licNum, expirationDate:expiry,
          issuingAuthority:issuer, documentPath,
        });
      }
      setUploadProgress("Complete!");
      setTimeout(()=>{ setVerifying(false); setVerified(true); }, 600);
    } catch(err) {
      console.error("License upload error:", err);
      setError("Upload failed — " + (err.message || "please check your connection and try again."));
      setVerifying(false); setUploadProgress("");
    }
  };

  const canSubmit = file && licNum && expiry && agreed;

  return (
    <div style={S.modal}>
      <div style={S.mbox}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <div><div style={{ fontWeight:"bold", fontSize:18, marginBottom:3 }}>{icon} License Verification Required</div><div style={{ color:COLORS.muted, fontSize:12 }}>{item.category} — {item.title}</div></div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>

        {!verified ? <>
          <div style={{ background:COLORS.surface, border:`2px solid ${COLORS.purple}44`, borderRadius:12, padding:18, marginBottom:18 }}>
            <div style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:10 }}>
              <span style={{ fontSize:32 }}>{icon}</span>
              <div><div style={{ fontWeight:700, color:COLORS.accent, fontSize:14 }}>{req.licenseLabel}</div><div style={{ color:COLORS.muted, fontSize:11, marginTop:2 }}>Required before booking proceeds</div></div>
            </div>
            <div style={{ fontSize:13, color:COLORS.muted, lineHeight:1.65 }}>{req.licenseDesc}</div>
          </div>

          {req.licenseType==="pilot" && (
            <div style={{ background:COLORS.red+"11", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:11, marginBottom:14, fontSize:12, color:COLORS.muted }}>
              ✈️ <strong style={{ color:COLORS.red }}>IMPORTANT:</strong> Aircraft rentals also require a current FAA medical. An in-person checkout flight may be required prior to solo operation.
            </div>
          )}

          <div style={{ background:"#6c5ce711", border:`1px solid #6c5ce744`, borderRadius:8, padding:10, marginBottom:14, fontSize:11, color:COLORS.muted, lineHeight:1.5 }}>
            🔒 <strong style={{ color:COLORS.text }}>Secure Verification:</strong> Your documents are encrypted and stored securely. They are reviewed by our verification team and are never shared with other users or third parties. Verification is typically completed within 24 hours.
          </div>

          <div style={S.fg}>
            <label style={S.label}>Upload License / Certificate <span style={{ color:COLORS.red }}>*</span></label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,.heic,application/pdf,.jpg,.jpeg,.png,.pdf"
              style={{ display:"none" }}
              onChange={handleFileSelect}
            />
            <div
              onClick={() => { if (fileInputRef.current) fileInputRef.current.click(); }}
              style={{ border:`2px dashed ${file?COLORS.green:COLORS.border}`, borderRadius:10, padding:22, textAlign:"center", cursor:"pointer", background:file?COLORS.green+"08":"transparent", transition:"all 0.2s" }}
            >
              {file ? <><div style={{ fontSize:30, marginBottom:5 }}>✅</div><div style={{ color:COLORS.green, fontWeight:700, fontSize:13 }}>{file.name}</div><div style={{ color:COLORS.muted, fontSize:11 }}>{(file.size/1024/1024).toFixed(1)} MB — Click to replace</div></>
                     : <><div style={{ fontSize:30, marginBottom:5 }}>📎</div><div style={{ color:COLORS.text, fontWeight:600, fontSize:13 }}>Tap to Upload Document</div><div style={{ color:COLORS.muted, fontSize:11 }}>JPG, PNG, or PDF · Max 10 MB</div><div style={{ color:COLORS.muted, fontSize:10, marginTop:4 }}>Front & back if applicable · Photo of physical license accepted</div></>}
            </div>
          </div>

          <div style={S.row}>
            <div style={{ ...S.fg, flex:2 }}><label style={S.label}>License / Certificate Number <span style={{ color:COLORS.red }}>*</span></label><input style={S.input} placeholder="e.g. DL-12345678" value={licNum} onChange={e=>setLicNum(e.target.value)} /></div>
            <div style={{ ...S.fg, flex:1 }}><label style={S.label}>Expiration Date <span style={{ color:COLORS.red }}>*</span></label><input style={S.input} type="date" value={expiry} onChange={e=>setExpiry(e.target.value)} /></div>
          </div>

          <div style={S.fg}><label style={S.label}>{req.licenseType==="pilot"?"FAA Issuing Authority":req.licenseType==="boating"?"Issuing State / Provider":"Issuing State"}</label><input style={S.input} placeholder="e.g. California, Florida, FAA" value={issuer} onChange={e=>setIssuer(e.target.value)} /></div>

          <div onClick={()=>setAgreed(!agreed)} style={S.chkRow}>
            <span style={{ fontSize:18, color:agreed?COLORS.green:COLORS.muted }}>{agreed?"☑":"☐"}</span>
            <span style={{ fontSize:12 }}>I certify this license is valid, current, and belongs to me. I understand that submitting false, fraudulent, or expired documents is a violation of federal and state law and grounds for immediate account termination and potential legal action.</span>
          </div>

          {error && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:10, fontSize:12, color:COLORS.red, marginBottom:12 }}>⚠️ {error}</div>}

          <div style={{ ...S.row, marginTop:14 }}>
            <button onClick={onClose} style={{ ...S.btn("outline"), flex:1 }}>Cancel</button>
            <button onClick={handleVerify} disabled={!canSubmit||verifying} style={{ background:COLORS.purple, color:"#fff", border:"none", borderRadius:8, cursor:"pointer", padding:"9px 18px", fontSize:13, fontWeight:700, flex:2, opacity:canSubmit&&!verifying?1:0.4 }}>
              {verifying ? `⏳ ${uploadProgress}` : "🔍 Submit for Verification"}
            </button>
          </div>
        </> : (
          <div style={{ textAlign:"center", padding:"12px 0" }}>
            <div style={{ fontSize:52, marginBottom:10 }}>✅</div>
            <div style={{ fontSize:19, fontWeight:"bold", marginBottom:6 }}>License Submitted Successfully!</div>
            <div style={{ color:COLORS.muted, marginBottom:14, fontSize:13, lineHeight:1.6 }}>
              Your <strong style={{ color:COLORS.text }}>{req.licenseLabel}</strong> has been submitted for verification.
            </div>

            <div style={{ background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:10, padding:14, marginBottom:14, textAlign:"left" }}>
              <div style={{ fontSize:12, fontWeight:700, color:COLORS.text, marginBottom:8 }}>📋 Verification Details</div>
              <div style={{ fontSize:11, color:COLORS.muted, lineHeight:1.8 }}>
                <div><strong>License #:</strong> {licNum}</div>
                <div><strong>Type:</strong> {req.licenseLabel}</div>
                <div><strong>Expires:</strong> {expiry}</div>
                {issuer && <div><strong>Issuer:</strong> {issuer}</div>}
                <div><strong>Document:</strong> {file?.name} ({(file?.size/1024/1024).toFixed(1)} MB)</div>
                <div><strong>Status:</strong> <span style={{ color:"#f59e0b", fontWeight:700 }}>⏳ Pending Review</span></div>
              </div>
            </div>

            <div style={{ background:COLORS.accent+"11", border:`1px solid ${COLORS.accent}44`, borderRadius:8, padding:12, marginBottom:14, fontSize:11, color:COLORS.muted, lineHeight:1.5, textAlign:"left" }}>
              <strong style={{ color:COLORS.accent }}>What happens next:</strong><br/>
              • Your document is securely stored and encrypted<br/>
              • Our verification team will review within 24 hours<br/>
              • You'll receive an email notification with the result<br/>
              • For this booking, you're pre-approved to proceed<br/>
              • Your verified license will be saved for future rentals
            </div>

            <div style={{ background:COLORS.green+"11", border:`1px solid ${COLORS.green}44`, borderRadius:8, padding:11, marginBottom:18, fontSize:12, color:COLORS.green }}>
              🛡️ License #{licNum} — Logged by Rent This Rent That LLC
            </div>
            <button onClick={onVerified} style={{ ...S.btn("green","lg"), width:"100%" }}>Continue to Booking →</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BROKER AGREEMENT MODAL ────────────────────────────────────────────────────
function BrokerAgreementModal({ onAccept, onClose }) {
  const [c1,setC1]=useState(false),[c2,setC2]=useState(false),[c3,setC3]=useState(false),[c4,setC4]=useState(false),[c5,setC5]=useState(false);
  const sec = { color:COLORS.text, fontSize:13, fontWeight:"bold", marginTop:12, display:"block" };
  const p = { marginBottom:10 };
  return (
    <div style={S.modal}>
      <div style={{ ...S.mbox, maxWidth:600 }}>
        <div style={{ fontWeight:"bold", fontSize:18, marginBottom:3 }}>📋 Third-Party Broker Agreement</div>
        <div style={{ color:COLORS.muted, fontSize:12, marginBottom:4 }}>Rent This Rent That LLC — Platform Terms of Service</div>
        <div style={{ color:COLORS.muted, fontSize:10, marginBottom:16 }}>Last Updated: April 7, 2026 &nbsp;|&nbsp; Effective Upon Acceptance</div>
        <div style={{ background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:10, padding:18, fontSize:11, color:COLORS.muted, lineHeight:1.8, maxHeight:320, overflowY:"auto", marginBottom:14 }}>

          <strong style={{ color:COLORS.text, fontSize:14 }}>RENTAL PLATFORM BROKER AGREEMENT & TERMS OF SERVICE</strong><br/><br/>

          <span style={p}>This Agreement ("Agreement") is a legally binding contract entered into between the individual or entity using this platform ("User," "Renter," or "You"), the owner of the listed item ("Owner"), and <strong style={{ color:COLORS.text }}>Rent This Rent That LLC</strong>, a limited liability company ("Company," "Platform," "We," or "Us"), effective upon your acceptance of these terms through the platform interface. By accessing or using this platform you agree to be bound by this Agreement in its entirety.</span><br/><br/>

          <strong style={sec}>1. PLATFORM ROLE — THIRD-PARTY BROKER ONLY</strong><br/>
          <span style={p}>Rent This Rent That LLC operates solely as an intermediary third-party broker facilitating peer-to-peer rental transactions between Owners and Renters. <strong style={{ color:COLORS.text }}>The Company is NOT a party to, nor a guarantor of, any rental transaction.</strong> The Company does not own, possess, control, inspect, maintain, insure, or have custody of any listed items. The Company does not verify the condition, safety, legality, or fitness for any particular purpose of any listed item. All rental agreements are strictly between the Owner and the Renter. The Company acts solely as a venue and facilitator and assumes no responsibility for the performance, outcome, or consequences of any transaction conducted through this platform.</span><br/><br/>

          <strong style={sec}>2. ESCROW & PAYMENT PROCESSING</strong><br/>
          <span style={p}>All rental payments are processed through the platform and held in a segregated escrow account until rental completion and confirmation by both parties. The Company reserves the right to hold, release, or refund escrowed funds in accordance with this Agreement and applicable law. Escrow funds are not interest-bearing to the User. The Company may engage third-party payment processors subject to their own terms of service. Payment processing is subject to all applicable federal and state money transmission laws, including but not limited to the Bank Secrecy Act (BSA), FinCEN regulations, and applicable state money transmitter licensing requirements.</span><br/><br/>

          <strong style={sec}>3. MANDATORY INSURANCE REQUIREMENT</strong><br/>
          <span style={p}>All transactions processed through this platform require rental protection insurance through our designated insurance partner(s). This requirement is <strong style={{ color:COLORS.text }}>non-waivable</strong>. Insurance fees are calculated as a percentage of the total rental amount and are non-refundable once coverage is bound. Insurance coverage is subject to the terms, conditions, limitations, and exclusions of the applicable insurance policy, which may vary by item category, jurisdiction, and rental duration. <strong style={{ color:COLORS.text }}>The Company makes no representations or warranties regarding the scope, adequacy, or sufficiency of any insurance coverage.</strong> Users are strongly encouraged to maintain their own personal liability and property insurance. Insurance coverage may not be available in all jurisdictions; where coverage is unavailable, the transaction may not proceed.</span><br/><br/>

          <strong style={sec}>4. LICENSE, CREDENTIAL & REGULATORY VERIFICATION</strong><br/>
          <span style={p}>Certain items listed on this platform may require valid licenses, permits, certifications, or credentials to legally operate or possess under federal, state, provincial, local, or international law. These include but are not limited to: driver's licenses with appropriate endorsements, boating licenses, pilot certificates (FAA or equivalent), commercial driver's licenses (CDL), professional equipment certifications, firearms licenses, special permits, and any other credentials required by law. <strong style={{ color:COLORS.text }}>The Renter is solely responsible for ensuring they hold all required licenses and credentials valid in their jurisdiction before renting any item.</strong> The Company's verification process is a convenience only and does not constitute legal validation. Submission of fraudulent, expired, revoked, or invalid credentials constitutes a material breach of this Agreement and will result in immediate account termination, forfeiture of all fees, and may be reported to appropriate law enforcement authorities. The Company does not guarantee that its verification process captures all legal requirements in every jurisdiction.</span><br/><br/>

          <strong style={sec}>5. SAFETY TRAINING & CERTIFICATION</strong><br/>
          <span style={p}>Certain items require mandatory safety training or certification before rental completion. Completion of platform-provided safety training does not constitute professional instruction, licensing, or certification recognized by any governmental or regulatory authority. <strong style={{ color:COLORS.text }}>Users assume all risk associated with the operation of rented items, regardless of any safety training completed through this platform.</strong> The Company is not a certified training provider and makes no representations about the sufficiency of any training materials provided.</span><br/><br/>

          <strong style={sec}>6. ASSUMPTION OF RISK & RELEASE</strong><br/>
          <span style={p}><strong style={{ color:COLORS.text }}>BY USING THIS PLATFORM AND RENTING ANY ITEM, YOU EXPRESSLY ACKNOWLEDGE AND VOLUNTARILY ASSUME ALL RISKS</strong> associated with the rental, use, operation, transport, storage, and return of any item, including but not limited to: risk of personal injury, death, property damage, theft, loss, mechanical failure, product defect, environmental damage, and any other foreseeable or unforeseeable risk. You hereby release, waive, discharge, and hold harmless the Company, its members, managers, officers, employees, agents, affiliates, successors, assigns, and any related entities ("Released Parties") from any and all claims, demands, actions, causes of action, liabilities, damages, losses, costs, and expenses (including attorney's fees) arising out of or related to your use of this platform or any rental transaction, <strong style={{ color:COLORS.text }}>to the maximum extent permitted by applicable law</strong>. This release applies regardless of whether such claims are based on negligence, strict liability, breach of warranty, breach of contract, or any other legal theory.</span><br/><br/>

          <strong style={sec}>7. LIMITATION OF LIABILITY</strong><br/>
          <span style={p}><strong style={{ color:COLORS.text }}>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL RENT THIS RENT THAT LLC, ITS MEMBERS, MANAGERS, OFFICERS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES</strong>, including but not limited to damages for loss of profits, goodwill, use, data, or other intangible losses, regardless of whether such damages were foreseeable or whether the Company was advised of the possibility thereof. <strong style={{ color:COLORS.text }}>THE COMPANY'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THIS AGREEMENT OR ANY TRANSACTION SHALL NOT EXCEED THE TOTAL FEES ACTUALLY PAID BY YOU TO THE COMPANY IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.</strong> Some jurisdictions do not allow the exclusion or limitation of certain damages; in such jurisdictions, liability shall be limited to the fullest extent permitted by law. Nothing in this Agreement shall exclude or limit liability for death or personal injury caused by the Company's gross negligence or willful misconduct, or any other liability that cannot be excluded or limited by applicable law.</span><br/><br/>

          <strong style={sec}>8. INDEMNIFICATION</strong><br/>
          <span style={p}>You agree to indemnify, defend, and hold harmless the Company and its members, managers, officers, employees, agents, affiliates, successors, and assigns from and against any and all claims, demands, actions, damages, losses, costs, liabilities, and expenses (including reasonable attorneys' fees and court costs) arising out of or related to: (a) your use of the platform or any rented item; (b) your breach of this Agreement or any applicable law; (c) your violation of any third party's rights, including intellectual property, privacy, or contractual rights; (d) any injury, death, or property damage arising from your use of any rented item; (e) any tax liability arising from your transactions; (f) any regulatory violations committed by you. This indemnification obligation shall survive the termination of this Agreement and your account.</span><br/><br/>

          <strong style={sec}>9. RENTER RESPONSIBILITIES & ITEM CONDITION</strong><br/>
          <span style={p}>The Renter shall: (a) inspect all items upon receipt and report any pre-existing damage within 24 hours via the platform; (b) use all items solely for their intended purpose and in accordance with all applicable laws and manufacturer guidelines; (c) return all items on or before the agreed return date in the same condition as received, reasonable wear and tear excepted; (d) immediately report any damage, malfunction, accident, or loss to both the Owner and the Company. <strong style={{ color:COLORS.text }}>Late returns shall be charged at 1.5× the daily rental rate for each day past the agreed return date.</strong> In the event of loss, theft, or irreparable damage, the Renter shall be liable for the fair market replacement value of the item as determined by the Company in its reasonable discretion, less any applicable insurance proceeds.</span><br/><br/>

          <strong style={sec}>10. OWNER RESPONSIBILITIES & REPRESENTATIONS</strong><br/>
          <span style={p}>The Owner represents and warrants that: (a) they are the legal owner or authorized agent for the listed item; (b) the item is in safe, functional, working condition and free from known defects; (c) the listing description is accurate and not misleading; (d) the item complies with all applicable safety standards, recalls, and regulations; (e) they have the legal right to rent the item and such rental does not violate any law, regulation, lease, HOA rule, insurance policy, or other agreement; (f) they will disclose all known hazards or material defects. Owners are solely responsible for compliance with all tax obligations arising from rental income, including federal, state, and local income taxes, sales taxes, occupancy taxes, and any other applicable taxes.</span><br/><br/>

          <strong style={sec}>11. PROHIBITED ITEMS & ACTIVITIES</strong><br/>
          <span style={p}>The following items and activities are strictly prohibited on this platform: (a) any item whose rental or use is illegal under federal, state, local, or international law; (b) stolen property or property subject to liens, encumbrances, or legal disputes; (c) controlled substances, drug paraphernalia, or items primarily used for illegal activity; (d) weapons, explosives, or hazardous materials except where expressly permitted by law and approved by the Company; (e) items subject to active product recalls unless the recall has been satisfied; (f) any fraudulent, misleading, or deceptive listings or transactions. The Company reserves the right to remove any listing and terminate any account at its sole discretion without notice or liability.</span><br/><br/>

          <strong style={sec}>12. DISPUTE RESOLUTION & BINDING ARBITRATION</strong><br/>
          <span style={p}><strong style={{ color:COLORS.text }}>PLEASE READ THIS SECTION CAREFULLY — IT AFFECTS YOUR LEGAL RIGHTS.</strong> Any dispute, claim, or controversy arising out of or relating to this Agreement, any transaction, or your use of the platform ("Dispute") shall be resolved through binding individual arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules, except that either party may seek injunctive or equitable relief in any court of competent jurisdiction. <strong style={{ color:COLORS.text }}>YOU AND THE COMPANY EACH WAIVE THE RIGHT TO A TRIAL BY JURY.</strong> <strong style={{ color:COLORS.text }}>YOU AND THE COMPANY EACH WAIVE THE RIGHT TO PARTICIPATE IN A CLASS ACTION, CLASS-WIDE ARBITRATION, OR ANY CONSOLIDATED OR REPRESENTATIVE PROCEEDING.</strong> The arbitrator shall have exclusive authority to resolve all disputes, including issues of arbitrability. The arbitration shall be conducted in the English language. The arbitrator's award shall be final and binding and may be entered as a judgment in any court of competent jurisdiction. Notwithstanding the foregoing, claims within the jurisdiction of a small claims court may be brought in small claims court. For Users outside the United States, this arbitration clause shall be enforced to the maximum extent permitted under the laws of the User's jurisdiction; where binding arbitration is not enforceable, disputes shall be resolved in the courts specified in Section 13.</span><br/><br/>

          <strong style={sec}>13. GOVERNING LAW & JURISDICTION</strong><br/>
          <span style={p}>This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, United States of America, without regard to its conflict of law provisions. For any claims not subject to arbitration, the exclusive jurisdiction and venue shall be the state and federal courts located in Wilmington, Delaware. <strong style={{ color:COLORS.text }}>For international Users:</strong> This Agreement is subject to all applicable international laws and treaties, including but not limited to consumer protection laws of your local jurisdiction where such laws cannot be waived by contract. Nothing in this Agreement shall deprive consumers of the protection afforded by mandatory provisions of the law of their country of habitual residence where applicable, including but not limited to the EU Consumer Rights Directive (2011/83/EU), the UK Consumer Rights Act 2015, the Australian Consumer Law, or equivalent local consumer protection statutes.</span><br/><br/>

          <strong style={sec}>14. PRIVACY, DATA PROTECTION & COMPLIANCE</strong><br/>
          <span style={p}>The Company collects, processes, and stores personal data in accordance with its Privacy Policy (available on the platform). By using this platform, you consent to the collection and processing of your personal data as described therein. The Company complies with applicable data protection laws including but not limited to: the California Consumer Privacy Act (CCPA/CPRA), the Virginia Consumer Data Protection Act (VCDPA), the Colorado Privacy Act (CPA), the General Data Protection Regulation (GDPR) for EU/EEA users, the UK Data Protection Act 2018, the Personal Information Protection and Electronic Documents Act (PIPEDA) for Canadian users, and applicable state and international data privacy laws. You have the right to access, correct, delete, or port your personal data as provided by applicable law. The Company will not sell your personal information without your explicit consent. Data will be retained only as long as necessary for the purposes described or as required by law.</span><br/><br/>

          <strong style={sec}>15. INTELLECTUAL PROPERTY</strong><br/>
          <span style={p}>All content, trademarks, service marks, logos, trade names, and intellectual property displayed on this platform are the property of Rent This Rent That LLC or their respective owners and are protected by United States and international intellectual property laws. Users retain ownership of content they upload but grant the Company a non-exclusive, worldwide, royalty-free license to use, display, and distribute such content for platform operation purposes. Users represent that they have the right to upload any content they submit and that such content does not infringe upon any third party's rights.</span><br/><br/>

          <strong style={sec}>16. TAX OBLIGATIONS & COMPLIANCE</strong><br/>
          <span style={p}>Users are solely responsible for determining and fulfilling all tax obligations arising from their use of this platform, including but not limited to: federal, state, provincial, and local income taxes; sales and use taxes; goods and services taxes (GST/VAT); occupancy taxes; and any other taxes or fees imposed by any governmental authority. The Company may be required by law to report transaction information to tax authorities and will issue applicable tax forms (e.g., IRS Form 1099-K) where required. <strong style={{ color:COLORS.text }}>The Company does not provide tax advice.</strong> Users should consult a qualified tax professional regarding their specific tax obligations.</span><br/><br/>

          <strong style={sec}>17. CONSUMER PROTECTION NOTICES</strong><br/>
          <span style={p}><strong style={{ color:COLORS.text }}>United States:</strong> This platform operates in compliance with the Federal Trade Commission Act, the Consumer Financial Protection Act, state consumer protection statutes (including but not limited to state unfair and deceptive acts and practices statutes), the Electronic Signatures in Global and National Commerce Act (E-SIGN), and the Uniform Electronic Transactions Act (UETA). Consumers may have additional rights under their state's consumer protection laws that cannot be waived.<br/><br/>
          <strong style={{ color:COLORS.text }}>California Residents:</strong> Under California Civil Code Section 1789.3, California users are entitled to the following consumer rights notice: If you have a complaint, you may contact the Complaint Assistance Unit of the Division of Consumer Services of the California Department of Consumer Affairs in writing at 1625 N. Market Blvd., Suite N112, Sacramento, CA 95834, or by telephone at (800) 952-5210.<br/><br/>
          <strong style={{ color:COLORS.text }}>European Union / EEA Residents:</strong> You may have rights under the EU Consumer Rights Directive including a 14-day right of withdrawal for certain services. The European Commission provides an online dispute resolution platform at https://ec.europa.eu/consumers/odr.<br/><br/>
          <strong style={{ color:COLORS.text }}>United Kingdom Residents:</strong> Your rights under the Consumer Rights Act 2015, the Consumer Contracts Regulations 2013, and other applicable UK consumer protection legislation are not affected by this Agreement.<br/><br/>
          <strong style={{ color:COLORS.text }}>Canadian Residents:</strong> This Agreement is subject to applicable provincial consumer protection legislation including the Consumer Protection Act (Ontario), the Consumer Protection Act (Quebec), and equivalent provincial statutes. Residents of Quebec may have additional language and consumer protection rights.<br/><br/>
          <strong style={{ color:COLORS.text }}>Australian Residents:</strong> Nothing in this Agreement excludes, restricts, or modifies any consumer guarantee, right, or remedy conferred by the Australian Consumer Law (Schedule 2 of the Competition and Consumer Act 2010) that cannot be excluded, restricted, or modified by agreement.</span><br/><br/>

          <strong style={sec}>18. FORCE MAJEURE</strong><br/>
          <span style={p}>Neither the Company nor any User shall be liable for any failure or delay in performance due to circumstances beyond their reasonable control, including but not limited to: acts of God, natural disasters, pandemics, epidemics, government actions or restrictions, war, terrorism, civil unrest, labor disputes, power failures, internet or telecommunications failures, or cyberattacks.</span><br/><br/>

          <strong style={sec}>19. ELECTRONIC COMMUNICATIONS CONSENT</strong><br/>
          <span style={p}>By using this platform, you consent to receive electronic communications from the Company, including but not limited to: transaction confirmations, account notifications, legal notices, policy updates, and marketing communications (from which you may opt out). Electronic communications satisfy any legal requirement that such communications be in writing, to the extent permitted by applicable law.</span><br/><br/>

          <strong style={sec}>20. MODIFICATIONS TO AGREEMENT</strong><br/>
          <span style={p}>The Company reserves the right to modify, amend, or update this Agreement at any time by posting the revised version on the platform with a new "Last Updated" date. Material changes will be communicated via email or platform notification at least thirty (30) days before taking effect. Your continued use of the platform after such changes constitutes acceptance of the modified Agreement. If you do not agree to any modification, you must discontinue use of the platform immediately.</span><br/><br/>

          <strong style={sec}>21. TERMINATION</strong><br/>
          <span style={p}>Either party may terminate this Agreement at any time. The Company may suspend or terminate your account immediately, without prior notice, for any breach of this Agreement, fraudulent activity, or at its sole discretion. Upon termination: (a) all outstanding rental obligations remain in full force; (b) escrowed funds will be disbursed in accordance with this Agreement; (c) Sections 6, 7, 8, 12, 13, and 14 shall survive termination indefinitely.</span><br/><br/>

          <strong style={sec}>22. SEVERABILITY & ENTIRE AGREEMENT</strong><br/>
          <span style={p}>If any provision of this Agreement is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, such provision shall be modified to the minimum extent necessary to make it valid and enforceable, or if modification is not possible, shall be severed, and the remaining provisions shall continue in full force and effect. This Agreement, together with the Privacy Policy and any supplemental terms, constitutes the entire agreement between you and the Company and supersedes all prior or contemporaneous agreements, representations, and understandings.</span><br/><br/>

          <strong style={sec}>23. CONTACT INFORMATION</strong><br/>
          <span style={p}>For questions, concerns, or legal notices regarding this Agreement, please contact:<br/>
          Rent This Rent That LLC<br/>
          Email: rentthisrentthat@proton.me<br/>
          Website: www.rentthisrentthat.com</span><br/><br/>

          <strong style={{ color:COLORS.muted, fontSize:10, display:"block", marginTop:16, borderTop:`1px solid ${COLORS.border}`, paddingTop:12 }}>
          NOTICE: THIS AGREEMENT CONTAINS A BINDING ARBITRATION CLAUSE AND CLASS ACTION WAIVER (SECTION 12) THAT AFFECT YOUR LEGAL RIGHTS. BY ACCEPTING THIS AGREEMENT, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY ALL TERMS AND CONDITIONS HEREIN. IF YOU DO NOT AGREE, DO NOT USE THIS PLATFORM. THIS AGREEMENT DOES NOT CONSTITUTE LEGAL ADVICE. THE COMPANY RECOMMENDS THAT YOU CONSULT WITH AN ATTORNEY BEFORE ACCEPTING. THE COMPANY SHALL NOT BE HELD LIABLE FOR ANY RENTAL TRANSACTION, ITEM CONDITION, USER CONDUCT, OR ANY DAMAGES ARISING FROM USE OF THIS PLATFORM TO THE MAXIMUM EXTENT PERMITTED BY LAW.
          </strong>
        </div>
        {[
          [c1,setC1,"I acknowledge that Rent This Rent That LLC acts solely as a third-party broker, NOT as a party to any rental transaction, and that all funds are held in escrow."],
          [c2,setC2,"I authorize the mandatory insurance fee and understand that coverage is subject to the insurer's terms, conditions, and exclusions."],
          [c3,setC3,"I agree to provide all required licenses and credentials, complete applicable safety training, and assume full responsibility for verifying legal requirements in my jurisdiction."],
          [c4,setC4,"I have read and agree to the Assumption of Risk, Release, Limitation of Liability, Indemnification, and Binding Arbitration provisions (Sections 6–8, 12)."],
          [c5,setC5,"I have read, understand, and agree to be bound by the entire Broker Agreement & Terms of Service, including the class action waiver, governing law, and all disclaimers."]
        ].map(([val,setter,text],i)=>(
          <div key={i} onClick={()=>setter(!val)} style={S.chkRow}>
            <span style={{ fontSize:18, color:val?COLORS.green:COLORS.muted }}>{val?"☑":"☐"}</span>
            <span style={{ fontSize:11, lineHeight:1.4 }}>{text}</span>
          </div>
        ))}
        <div style={{ ...S.row, marginTop:16 }}>
          <button onClick={onClose} style={{ ...S.btn("outline"), flex:1 }}>Cancel</button>
          <button onClick={onAccept} disabled={!c1||!c2||!c3||!c4||!c5} style={{ ...S.btn("primary"), flex:2, opacity:c1&&c2&&c3&&c4&&c5?1:0.4 }}>I Accept All Terms & Conditions →</button>
        </div>
        <div style={{ fontSize:9, color:COLORS.muted, textAlign:"center", marginTop:8, lineHeight:1.4 }}>
          By clicking "I Accept All Terms & Conditions," you are entering into a legally binding agreement. This constitutes your electronic signature under the E-SIGN Act and UETA.
        </div>
      </div>
    </div>
  );
}

// ─── CHECKOUT MODAL ────────────────────────────────────────────────────────────
function CheckoutModal({ item, user, profile, onClose, onComplete, onNeedAuth }) {
  const licReq     = LICENSE_REQUIREMENTS[item.category];
  const needsLicense  = licReq?.required;
  const needsTraining = item.training_required || item.trainingRequired;
  const [step, setStep]       = useState("agreement");
  const [delivery, setDelivery] = useState(null);
  const [zip, setZip]           = useState("");
  const [address, setAddress]   = useState("");
  const [city, setCity]         = useState("");
  const [stateAddr, setStateAddr] = useState("");
  const [dates, setDates]       = useState({ start:"", end:"" });
  const [saving, setSaving]     = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [bookedDates, setBookedDates]   = useState([]);
  const [protectionTier, setProtectionTier] = useState("standard"); // Feature 2: Protection tier
  // Stripe payment state
  const [stripeError, setStripeError] = useState(null);
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const stripeCardRef = useRef(null);
  const stripeInstanceRef = useRef(null);

  // Load Stripe.js and mount card element when payment step is reached
  useEffect(() => {
    if (step !== "payment") return;
    let mounted = true;
    const STRIPE_PK = window.__RTRT_STRIPE_PK || 'pk_test_placeholder';
    const loadStripe = async () => {
      try {
        if (!window.Stripe) {
          const s = document.createElement('script');
          s.src = 'https://js.stripe.com/v3/';
          s.async = true;
          await new Promise((res, rej) => { s.onload = res; s.onerror = rej; document.head.appendChild(s); });
        }
        if (!mounted) return;
        const stripe = window.Stripe(STRIPE_PK);
        stripeInstanceRef.current = stripe;
        const elements = stripe.elements();
        const card = elements.create('card', {
          style: {
            base: { color: '#F0F2F7', fontFamily: 'Georgia, serif', fontSize: '16px', '::placeholder': { color: '#8A93A8' } },
            invalid: { color: '#E74C3C' },
          },
        });
        const el = document.getElementById('stripe-card-element');
        if (el && mounted) {
          el.innerHTML = '';
          card.mount(el);
          stripeCardRef.current = card;
        }
      } catch(e) {
        console.error('Stripe load error:', e);
        if (mounted) setStripeError('Failed to load payment form. Please refresh and try again.');
      }
    };
    loadStripe();
    return () => { mounted = false; if (stripeCardRef.current) { try { stripeCardRef.current.unmount(); } catch(e){} } };
  }, [step]);

  const handleStripePayment = async () => {
    if (!stripeInstanceRef.current || !stripeCardRef.current) {
      // Fallback: if Stripe isn't loaded (test/dev mode), just confirm booking
      await confirmBooking();
      return;
    }
    setStripeProcessing(true);
    setStripeError(null);
    try {
      // Create payment intent on backend
      const res = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Math.round(totalDueNow * 100), // cents
          description: `Rental: ${item.title} (${days} day${days!==1?'s':''})`,
          metadata: { listing_id: item.id, renter_id: profile?.id, days: String(days) },
        }),
      });
      const { clientSecret, error: apiError } = await res.json();
      if (apiError) throw new Error(apiError);

      const { error: stripeErr } = await stripeInstanceRef.current.confirmCardPayment(clientSecret, {
        payment_method: { card: stripeCardRef.current },
      });
      if (stripeErr) throw new Error(stripeErr.message);

      // Payment succeeded — create the booking record
      await confirmBooking();
    } catch(err) {
      setStripeError(err.message || 'Payment failed. Please try again.');
    } finally {
      setStripeProcessing(false);
    }
  };

  // Load blocked and booked dates for this listing
  useEffect(() => {
    const loadAvailability = async () => {
      try {
        const { data:blocked } = await supabase.from("listing_blocked_dates").select("blocked_date").eq("listing_id", item.id);
        setBlockedDates((blocked||[]).map(b=>b.blocked_date));

        const { data:bookings } = await supabase.from("bookings").select("start_date, end_date").eq("listing_id", item.id).in("status",["pending","confirmed"]);
        const booked = [];
        (bookings||[]).forEach(b => {
          let d = new Date(b.start_date+"T00:00:00");
          const end = new Date(b.end_date+"T00:00:00");
          while (d <= end) {
            booked.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`);
            d.setDate(d.getDate()+1);
          }
        });
        setBookedDates(booked);
      } catch(e) { console.error("Load availability error:",e); }
    };
    loadAvailability();
  }, [item.id]);

  const handleCalendarSelect = (dateStr) => {
    if (blockedDates.includes(dateStr) || bookedDates.includes(dateStr)) return;
    if (!dates.start || (dates.start && dates.end)) {
      setDates({ start: dateStr, end: "" });
    } else {
      if (dateStr < dates.start) {
        setDates({ start: dateStr, end: "" });
      } else {
        // Check for blocked/booked dates in range
        let d = new Date(dates.start+"T00:00:00");
        const end = new Date(dateStr+"T00:00:00");
        let hasConflict = false;
        while (d <= end) {
          const ds = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
          if (blockedDates.includes(ds) || bookedDates.includes(ds)) { hasConflict = true; break; }
          d.setDate(d.getDate()+1);
        }
        if (hasConflict) {
          setDates({ start: dateStr, end: "" });
        } else {
          setDates({ start: dates.start, end: dateStr });
        }
      }
    }
  };

  const days = (()=>{ if(!dates.start||!dates.end) return 1; const d=(new Date(dates.end)-new Date(dates.start))/86400000; return Math.max(1,d); })();
  const priceDay       = item.price_per_day || item.priceDay || 0;
  const rentalSubtotal = priceDay * days;
  const platformFee    = Math.round(rentalSubtotal * PLATFORM_FEE_RATE);

  // Feature 2: Protection tier-based insurance calculation
  const protectionTiers = {
    basic:    { rate: Math.max(8, Math.round(rentalSubtotal * 0.06)), coverage: 500 },
    standard: { rate: Math.round(rentalSubtotal * 0.10), coverage: 2500 },
    premium:  { rate: Math.round(rentalSubtotal * 0.15), coverage: 10000 },
  };
  const selectedTier = protectionTiers[protectionTier] || protectionTiers.standard;
  const insuranceFee = Math.max(INSURANCE_MIN_FEE, selectedTier.rate);
  const insuranceBrokerPortion  = Math.round(insuranceFee * (INSURANCE_BROKER_MARKUP / (INSURANCE_BROKER_MARKUP + 0.08)));
  const insuranceProviderPortion = insuranceFee - insuranceBrokerPortion;
  const deliveryFee    = delivery==="fullservice"?49:delivery==="dropoff"?25:0;
  const securityDeposit = Math.max(25, Math.round(rentalSubtotal * SECURITY_DEPOSIT_RATE));
  const totalDueNow    = rentalSubtotal + platformFee + insuranceFee + deliveryFee + securityDeposit;
  const total          = totalDueNow; // alias for backward compat

  if (!user) return (
    <div style={S.modal}><div style={S.mbox}>
      <div style={{ textAlign:"center", padding:"24px 0" }}>
        <div style={{ fontSize:44, marginBottom:12 }}>🔐</div>
        <div style={{ fontSize:18, fontWeight:"bold", marginBottom:8 }}>Sign In to Book</div>
        <div style={{ color:COLORS.muted, marginBottom:22, fontSize:13 }}>Create a free account to rent items on Rent This Rent That.</div>
        <div style={S.row}>
          <button onClick={onClose} style={{ ...S.btn("outline"), flex:1 }}>Cancel</button>
          <button onClick={onNeedAuth} style={{ ...S.btn("primary"), flex:2 }}>Sign In / Sign Up →</button>
        </div>
      </div>
    </div></div>
  );

  if (step==="agreement") return <BrokerAgreementModal onAccept={()=>setStep(needsLicense?"license":needsTraining?"training":"delivery")} onClose={onClose} />;
  if (step==="license")   return <LicenseModal item={item} userId={profile?.id} onVerified={()=>setStep(needsTraining?"training":"delivery")} onClose={onClose} />;
  if (step==="training")  return <TrainingModal item={item} userId={profile?.id} onComplete={()=>setStep("delivery")} onClose={onClose} />;

  const stepLabels = [["✅","Agreement"], needsLicense?["✅","License"]:null, needsTraining?["✅","Training"]:null, [step==="delivery"?"▶":["payment","confirm"].includes(step)?"✅":"○","Delivery"], [step==="payment"?"▶":step==="confirm"?"✅":"○","Payment"], [step==="confirm"?"▶":"○","Done"]].filter(Boolean);

  const confirmBooking = async () => {
    setSaving(true);
    try {
      await createBooking({
        listingId: item.id,
        renterId:  profile.id,
        startDate: dates.start,
        endDate:   dates.end,
        deliveryType: delivery,
        deliveryZip:  delivery==="self"?null:zip,
        deliveryAddress: delivery==="self"?null:`${address}, ${city}, ${stateAddr} ${zip}`,
        subtotal:     rentalSubtotal,
        platformFee,
        insuranceFee,
        insuranceBrokerPortion,
        insuranceProviderPortion,
        deliveryFee,
        securityDeposit,
        totalAmount: totalDueNow,
      });
      // Send email notifications to platform, renter, and owner
      await sendBookingEmails({
        item, renterProfile:profile, dates, delivery,
        address: delivery!=="self"?`${address}, ${city}, ${stateAddr} ${zip}`:null,
        total: totalDueNow, deliveryFee, insuranceFee, platformFee, securityDeposit, days,
      });
      setStep("confirmed");
    } catch(err) {
      alert("Booking error: "+err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.modal}>
      <div style={S.mbox}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <div style={{ fontWeight:"bold", fontSize:17 }}>Checkout — {CAT_ICONS[item.category]||"📦"} {item.title}</div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:4, marginBottom:20, fontSize:11, color:COLORS.muted, flexWrap:"wrap" }}>
          {stepLabels.map(([icon,label],i)=><span key={label}>{icon} {label}{i<stepLabels.length-1?" › ":""}</span>)}
        </div>

        {step==="delivery" && <>
          <div style={S.fg}>
            <label style={S.label}>Select Rental Dates</label>
            <div style={{ fontSize:11, color:COLORS.muted, marginBottom:8 }}>Tap a start date, then tap an end date. Blocked (🚫) and booked (📅) dates are unavailable.</div>
            <AvailabilityCalendar
              listingId={item.id}
              blockedDates={blockedDates}
              bookedDates={bookedDates}
              onSelectRange={handleCalendarSelect}
              selectedStart={dates.start}
              selectedEnd={dates.end}
            />
            {dates.start && (
              <div style={{ marginTop:8, fontSize:12, color:COLORS.accent, fontWeight:600 }}>
                {dates.end ? `📅 ${dates.start} → ${dates.end} (${days} day${days!==1?"s":""})` : `📅 Start: ${dates.start} — now tap an end date`}
              </div>
            )}
          </div>

          <div style={S.fg}>
            <label style={S.label}>How would you like to get the item?</label>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>

              {/* Self Pickup & Return */}
              <div style={{ ...S.dlvBtn(delivery==="self"), display:"flex", alignItems:"flex-start", gap:12, textAlign:"left", padding:14 }} onClick={()=>setDelivery("self")}>
                <div style={{ fontSize:28, flexShrink:0 }}>🚗</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:delivery==="self"?COLORS.accent:COLORS.text }}>Self Pickup & Return</div>
                  <div style={{ color:COLORS.muted, fontSize:11, lineHeight:1.5, marginTop:3 }}>You pick up the item from the owner's location and return it yourself when the rental period ends.</div>
                  <div style={{ color:COLORS.green, fontWeight:700, fontSize:12, marginTop:4 }}>FREE</div>
                </div>
              </div>

              {/* Drop-Off & Pick-Up */}
              {(item.delivery_available||item.delivery) &&
              <div style={{ ...S.dlvBtn(delivery==="dropoff"), display:"flex", alignItems:"flex-start", gap:12, textAlign:"left", padding:14 }} onClick={()=>setDelivery("dropoff")}>
                <div style={{ fontSize:28, flexShrink:0 }}>📦</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:delivery==="dropoff"?COLORS.accent:COLORS.text }}>Drop-Off & Pick-Up</div>
                  <div style={{ color:COLORS.muted, fontSize:11, lineHeight:1.5, marginTop:3 }}>The owner drops off the item to your address at the start of the rental and picks it up from you when the rental period ends. You are responsible for keeping the item safe at your location.</div>
                  <div style={{ color:COLORS.accent, fontWeight:700, fontSize:12, marginTop:4 }}>+$25</div>
                </div>
              </div>}

              {/* Full Service */}
              {(item.delivery_available||item.delivery) &&
              <div style={{ ...S.dlvBtn(delivery==="fullservice"), display:"flex", alignItems:"flex-start", gap:12, textAlign:"left", padding:14 }} onClick={()=>setDelivery("fullservice")}>
                <div style={{ fontSize:28, flexShrink:0 }}>🚚</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:delivery==="fullservice"?COLORS.accent:COLORS.text }}>Full Service</div>
                  <div style={{ color:COLORS.muted, fontSize:11, lineHeight:1.5, marginTop:3 }}>White-glove experience — the owner delivers the item to your address, provides a walkthrough or setup assistance, and picks it up when the rental is complete. Includes any assembly, hookup, or launch assistance where applicable.</div>
                  <div style={{ color:COLORS.accent, fontWeight:700, fontSize:12, marginTop:4 }}>+$49</div>
                </div>
              </div>}

            </div>
          </div>

          {/* Feature 2: Protection Tier Selector */}
          <div style={S.fg}>
            <label style={S.label}>🛡️ Damage Protection Coverage</label>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {[
                { id:"basic", name:"Basic Protection", rate:"6%", cost:"$"+Math.max(8, Math.round(rentalSubtotal * 0.06)), coverage:"up to $500" },
                { id:"standard", name:"Standard Protection", rate:"10%", cost:"$"+Math.round(rentalSubtotal * 0.10), coverage:"up to $2,500" },
                { id:"premium", name:"Premium Protection", rate:"15%", cost:"$"+Math.round(rentalSubtotal * 0.15), coverage:"up to $10,000 + theft" },
              ].map(tier => (
                <div key={tier.id} style={{ ...S.dlvBtn(protectionTier===tier.id), display:"flex", alignItems:"flex-start", gap:12, textAlign:"left", padding:12 }} onClick={()=>setProtectionTier(tier.id)}>
                  <div style={{ fontSize:20, flexShrink:0 }}>🛡️</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:12, color:protectionTier===tier.id?COLORS.accent:COLORS.text }}>{tier.name}</div>
                    <div style={{ color:COLORS.muted, fontSize:10, marginTop:2 }}>Coverage: {tier.coverage}</div>
                  </div>
                  <div style={{ textAlign:"right", minWidth:60, flexShrink:0 }}>
                    <div style={{ fontWeight:700, fontSize:12, color:COLORS.accent }}>{tier.cost}</div>
                    <div style={{ color:COLORS.muted, fontSize:9 }}>{tier.rate} of rental</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Address fields for Drop-Off & Full Service */}
          {delivery && delivery!=="self" && <>
            <div style={{ background:COLORS.accent+"11", border:`1px solid ${COLORS.accent}44`, borderRadius:8, padding:10, marginBottom:6, fontSize:11, color:COLORS.muted }}>
              📍 <strong style={{ color:COLORS.accent }}>{delivery==="dropoff"?"Drop-Off":"Full Service"} Address:</strong> Enter the address where you'd like the item {delivery==="dropoff"?"dropped off and picked up from":"delivered and serviced"}.
            </div>
            <div style={S.fg}>
              <label style={S.label}>Street Address <span style={{ color:COLORS.red }}>*</span></label>
              <input style={S.input} placeholder="e.g. 123 Main Street, Apt 4B" value={address} onChange={e=>setAddress(e.target.value)} />
            </div>
            <div style={S.row}>
              <div style={{ ...S.fg, flex:2 }}>
                <label style={S.label}>City <span style={{ color:COLORS.red }}>*</span></label>
                <input style={S.input} placeholder="e.g. Austin" value={city} onChange={e=>setCity(e.target.value)} />
              </div>
              <div style={{ ...S.fg, flex:1 }}>
                <label style={S.label}>State <span style={{ color:COLORS.red }}>*</span></label>
                <input style={S.input} placeholder="e.g. TX" value={stateAddr} onChange={e=>setStateAddr(e.target.value)} maxLength={2} />
              </div>
              <div style={{ ...S.fg, flex:1 }}>
                <label style={S.label}>ZIP <span style={{ color:COLORS.red }}>*</span></label>
                <input style={S.input} placeholder="ZIP" value={zip} onChange={e=>setZip(e.target.value)} maxLength={5} />
              </div>
            </div>
            {address && city && stateAddr && zip.length===5 && (
              <div style={{ background:COLORS.green+"11", border:`1px solid ${COLORS.green}44`, borderRadius:8, padding:10, marginBottom:6, fontSize:11, color:COLORS.green }}>
                ✅ <strong>{delivery==="dropoff"?"Drop-off & pickup":"Full service"} address:</strong> {address}, {city}, {stateAddr} {zip}
              </div>
            )}
          </>}

          <hr style={{ borderColor:COLORS.border, margin:"18px 0" }} />
          <div style={{ background:COLORS.surface, borderRadius:10, padding:14, marginBottom:18 }}>
            <div style={{ fontWeight:700, marginBottom:9, fontSize:13 }}>Order Summary</div>
            {[
              [`Rental (${days}d × $${priceDay})`,`$${rentalSubtotal}`],
              [`💼 Service Fee (${Math.round(PLATFORM_FEE_RATE*100)}%)`,`$${platformFee}`],
              [`🛡️ ${protectionTier.charAt(0).toUpperCase()+protectionTier.slice(1)} Protection`,`$${insuranceFee}`],
              delivery==="dropoff"?["📦 Drop-Off & Pick-Up",`$${deliveryFee}`]:null,
              delivery==="fullservice"?["🚚 Full Service (Delivery + Setup + Return)",`$${deliveryFee}`]:null,
            ].filter(Boolean).map(([l,v])=>(
              <div key={l} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:7, color:COLORS.muted }}><span>{l}</span><span style={{ color:COLORS.text }}>{v}</span></div>
            ))}
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:7, color:COLORS.muted }}>
              <span>🔒 Security Deposit <span style={{ fontSize:10, color:COLORS.green }}>(refundable)</span></span>
              <span style={{ color:COLORS.text }}>${securityDeposit}</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontWeight:"bold", fontSize:15, borderTop:`1px solid ${COLORS.border}`, paddingTop:9, marginTop:3 }}><span>Total Due Now</span><span style={{ color:COLORS.accent }}>${totalDueNow}</span></div>
            <div style={{ fontSize:10, color:COLORS.muted, marginTop:6 }}>Security deposit of ${securityDeposit} refunded within 48 hours after successful return.</div>
          </div>
          <button onClick={()=>setStep("payment")} disabled={!delivery||!dates.start||!dates.end||(delivery!=="self"&&(!address||!city||!stateAddr||zip.length<5))} style={{ ...S.btn("primary","lg"), width:"100%", opacity:delivery&&dates.start&&dates.end&&(delivery==="self"||(address&&city&&stateAddr&&zip.length===5))?1:0.4 }}>Proceed to Payment →</button>
        </>}

        {step==="payment" && <>
          <div style={{ background:COLORS.surface, borderRadius:10, padding:14, marginBottom:18, textAlign:"center" }}>
            <div style={{ fontSize:12, color:COLORS.muted, marginBottom:3 }}>Total Due Today</div>
            <div style={{ fontSize:32, fontWeight:"bold", color:COLORS.accent }}>${totalDueNow}</div>
            <div style={{ fontSize:11, color:COLORS.green, marginTop:4 }}>Includes ${securityDeposit} refundable security deposit</div>
          </div>
          {/* Stripe Card Element container */}
          <div style={S.fg}>
            <label style={S.label}>Card Details</label>
            <div id="stripe-card-element" style={{ ...S.input, padding:14, minHeight:44 }}>
              {/* Stripe Elements mounts here — see StripeCardMount below */}
            </div>
            {stripeError && <div style={{ color:COLORS.red, fontSize:12, marginTop:6 }}>{stripeError}</div>}
          </div>
          <div style={{ background:COLORS.accent+"11", border:`1px solid ${COLORS.accent}44`, borderRadius:8, padding:10, marginBottom:18, fontSize:11, color:COLORS.muted }}>
            🔒 Funds held in escrow by <strong style={{ color:COLORS.text }}>Rent This Rent That LLC</strong> until rental completion. Security deposit refunded within 48 hours of successful return.
          </div>
          <div style={S.row}>
            <button onClick={()=>setStep("delivery")} style={{ ...S.btn("outline"), flex:1 }}>← Back</button>
            <button onClick={handleStripePayment} disabled={saving||stripeProcessing} style={{ ...S.btn("green"), flex:2 }}>{stripeProcessing?"⏳ Processing...":saving?"⏳ Saving...":"🔒 Confirm & Pay $"+totalDueNow}</button>
          </div>
        </>}

        {step==="confirmed" && <div style={{ textAlign:"center", padding:"10px 0" }}>
          <div style={{ fontSize:50, marginBottom:10 }}>🎉</div>
          <div style={{ fontSize:19, fontWeight:"bold", marginBottom:6 }}>Rental Confirmed!</div>
          <div style={{ color:COLORS.muted, marginBottom:18, fontSize:13, lineHeight:1.6 }}>
            <strong>{item.title}</strong> is booked and saved to your account. Funds held by Rent This Rent That LLC.
          </div>
          <div style={{ background:COLORS.surface, borderRadius:10, padding:18, marginBottom:18, textAlign:"left" }}>
            {[
              ["Item",item.title],
              ["Owner",item.profiles?.full_name||item.owner],
              ["Dates",`${dates.start} to ${dates.end} (${days} day${days!==1?"s":""})`],
              ["Fulfillment", delivery==="self"?"Self Pickup & Return":delivery==="dropoff"?"Drop-Off & Pick-Up":delivery==="fullservice"?"Full Service (Delivery + Setup + Return)":"—"],
              delivery!=="self"?["Address",`${address}, ${city}, ${stateAddr} ${zip}`]:null,
              ["Rental Subtotal",`$${rentalSubtotal}`],
              ["Service Fee",`$${platformFee}`],
              [`${protectionTier.charAt(0).toUpperCase()+protectionTier.slice(1)} Protection`,"SecureRent Partners™ — $"+insuranceFee],
              deliveryFee>0?["Delivery",`$${deliveryFee}`]:null,
              ["Security Deposit",`$${securityDeposit} (refundable)`],
              ["Escrow","Rent This Rent That LLC"],
              ["Total Charged",`$${totalDueNow}`],
            ].filter(Boolean).map(([k,v])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:9 }}>
                <span style={{ color:COLORS.muted }}>{k}</span>
                <span style={{ color:k==="Insurance"?COLORS.green:k==="Total Charged"?COLORS.accent:k==="Security Deposit"?COLORS.blue:COLORS.text, textAlign:"right", maxWidth:"60%" }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ background:COLORS.green+"11", border:`1px solid ${COLORS.green}44`, borderRadius:8, padding:10, marginBottom:14, fontSize:11, color:COLORS.green }}>
            🔒 Your ${securityDeposit} security deposit will be refunded within 48 hours after the item is returned in good condition.
          </div>
          <button onClick={onComplete} style={{ ...S.btn("primary","lg"), width:"100%" }}>Back to Listings</button>
        </div>}
      </div>
    </div>
  );
}

// ─── LIST ITEM MODAL ───────────────────────────────────────────────────────────
function ListItemModal({ user, profile, onClose, onSave, onNeedAuth, editItem, onRemove }) {
  const isEdit = !!editItem;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Remove from UI immediately (optimistic update)
      if (onRemove) onRemove(editItem.id);
      onClose();
      // Then clean up in background: blocked dates, then listing
      await supabase.from('listing_blocked_dates').delete().eq('listing_id', editItem.id);
      const { error } = await supabase.from('listings').delete().eq('id', editItem.id);
      if (error) console.error("Delete listing error:", error);
    } catch(err) {
      console.error("Failed to remove listing:", err);
    }
  };
  const [form, setForm] = useState(editItem ? {
    title: editItem.title || "",
    category: editItem.category || "",
    priceDay: editItem.price_per_day || "",
    priceWeek: editItem.price_per_week || "",
    zip: editItem.zip_code || "",
    delivery: editItem.delivery_available || false,
    trainingRequired: editItem.training_required || false,
    description: editItem.description || "",
    instantBook: editItem.instant_book || false, // Feature 5: Instant book toggle
  } : { title:"", category:"", priceDay:"", priceWeek:"", zip:"", delivery:false, trainingRequired:false, description:"", instantBook:false });
  const [photoFiles, setPhotoFiles] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState(() => {
    if (!editItem?.image_url) return [];
    try { const parsed = JSON.parse(editItem.image_url); return Array.isArray(parsed) ? parsed : [editItem.image_url]; } catch { return [editItem.image_url]; }
  });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const selReq     = form.category ? LICENSE_REQUIREMENTS[form.category] : null;
  const autoLicense = selReq?.required || false;
  const totalPhotos = existingPhotos.length + photoFiles.length;
  const [uploadProgress, setUploadProgress] = useState("");

  // Compress image before upload — resize to max 1200px and convert to JPEG
  const compressImage = (file) => new Promise((resolve) => {
    const maxSize = 1200;
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width > height) { height = Math.round(height * maxSize / width); width = maxSize; }
        else { width = Math.round(width * maxSize / height); height = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" }));
      }, "image/jpeg", 0.75);
    };
    img.src = URL.createObjectURL(file);
  });

  if (!user) return (
    <div style={S.modal}><div style={S.mbox}>
      <div style={{ textAlign:"center", padding:"24px 0" }}>
        <div style={{ fontSize:44, marginBottom:12 }}>🔐</div>
        <div style={{ fontSize:18, fontWeight:"bold", marginBottom:8 }}>Sign In to List</div>
        <div style={{ color:COLORS.muted, marginBottom:22, fontSize:13 }}>Create a free account to list your items on Rent This Rent That.</div>
        <div style={S.row}>
          <button onClick={onClose} style={{ ...S.btn("outline"), flex:1 }}>Cancel</button>
          <button onClick={onNeedAuth} style={{ ...S.btn("primary"), flex:2 }}>Sign In / Sign Up →</button>
        </div>
      </div>
    </div></div>
  );

  const handleSave = async () => {
    if (!form.title||!form.category||!form.priceDay) return;
    if (!profile?.id) { setError("Profile not loaded yet. Please sign out and sign back in."); return; }
    setSaving(true); setError(""); setUploadProgress(photoFiles.length > 0 ? "Compressing photos..." : "");
    try {
      // Compress and upload all photos in parallel
      const uploadPhotos = async (listingId) => {
        if (photoFiles.length === 0) return [];
        setUploadProgress("Compressing photos...");
        const compressed = await Promise.all(photoFiles.map(f => compressImage(f)));
        setUploadProgress(`Uploading ${compressed.length} photo${compressed.length>1?"s":""}...`);
        const urls = await Promise.all(compressed.map((file, i) =>
          uploadListingPhoto(file, listingId + "_" + Date.now() + "_" + i)
        ));
        setUploadProgress("");
        return urls;
      };

      if (isEdit) {
        const updates = {
          title: form.title,
          category: form.category,
          price_per_day: parseFloat(form.priceDay),
          price_per_week: form.priceWeek ? parseFloat(form.priceWeek) : null,
          zip_code: form.zip,
          delivery_available: form.delivery,
          training_required: form.trainingRequired,
          description: form.description,
          license_required: autoLicense,
          instant_book: form.instantBook, // Feature 5
        };
        const newUrls = await uploadPhotos(editItem.id);
        const allUrls = [...existingPhotos, ...newUrls];
        if (allUrls.length > 0) {
          updates.image_url = allUrls.length === 1 ? allUrls[0] : JSON.stringify(allUrls);
        }
        const { data, error: updateErr } = await supabase.from('listings').update(updates).eq('id', editItem.id).select().single();
        if (updateErr) throw updateErr;
        onSave(data);
      } else {
        const listing = await createListing({ ...form, licenseRequired:autoLicense }, profile.id);
        const newUrls = await uploadPhotos(listing.id);
        if (newUrls.length > 0) {
          const imageVal = newUrls.length === 1 ? newUrls[0] : JSON.stringify(newUrls);
          await supabase.from('listings').update({ image_url: imageVal }).eq('id', listing.id);
          listing.image_url = imageVal;
        }
        onSave(listing);
      }
      onClose();
    } catch(err) {
      setError((isEdit ? "Failed to update listing: " : "Failed to create listing: ")+err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.modal}>
      <div style={S.mbox}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
          <div style={{ fontWeight:"bold", fontSize:18 }}>{isEdit ? "Edit Listing" : "List an Item for Rent"}</div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>
        <div style={S.fg}><label style={S.label}>Item Title</label><input style={S.input} placeholder="e.g. 22ft Pontoon Boat, Cessna 172..." value={form.title} onChange={e=>set("title",e.target.value)} /></div>
        <div style={S.fg}>
          <label style={S.label}>Category</label>
          <select style={S.input} value={form.category} onChange={e=>set("category",e.target.value)}>
            <option value="">Select category...</option>
            <optgroup label="🌊 Watercraft"><option value="Boats">⛵ Boats</option><option value="Jet Skis">💦 Jet Skis</option></optgroup>
            <optgroup label="✈️ Aviation"><option value="Aircraft">✈️ Aircraft</option></optgroup>
            <optgroup label="🚗 Vehicles"><option value="Automobiles">🚗 Automobiles</option><option value="Motorcycles">🏍️ Motorcycles</option><option value="Campers & RVs">🚐 Campers & RVs</option></optgroup>
            <optgroup label="🏗️ Equipment"><option value="Heavy Equipment">🏗️ Heavy Equipment</option><option value="Power Tools">🔧 Power Tools</option><option value="Construction">🪜 Construction</option><option value="Outdoor Equipment">💧 Outdoor Equipment</option></optgroup>
            <optgroup label="🏠 Home & Lifestyle"><option value="Garden">🌿 Garden</option><option value="Appliances">🏠 Appliances</option><option value="Electronics">📱 Electronics</option><option value="Events">⛺ Events</option><option value="Sports & Recreation">⚽ Sports & Recreation</option></optgroup>
          </select>
        </div>
        {form.category && autoLicense && <div style={{ background:COLORS.purple+"11", border:`1px solid ${COLORS.purple}44`, borderRadius:8, padding:10, marginBottom:14, fontSize:12, color:COLORS.muted }}>{LICENSE_ICONS[selReq.licenseType]} <strong style={{ color:COLORS.purple }}>License auto-required:</strong> Renters of {form.category} must verify a {selReq.licenseLabel}.</div>}
        <div style={S.fg}><label style={S.label}>Description</label><textarea style={{ ...S.input, height:70, resize:"vertical" }} placeholder="Year, condition, capacity, features..." value={form.description} onChange={e=>set("description",e.target.value)} /></div>
        <div style={S.row}>
          <div style={{ ...S.fg, flex:1 }}><label style={S.label}>Price / Day ($)</label><input style={S.input} type="number" placeholder="0.00" value={form.priceDay} onChange={e=>set("priceDay",e.target.value)} /></div>
          <div style={{ ...S.fg, flex:1 }}><label style={S.label}>Price / Week ($)</label><input style={S.input} type="number" placeholder="0.00" value={form.priceWeek} onChange={e=>set("priceWeek",e.target.value)} /></div>
        </div>

        {/* Feature 4: Pricing Suggestion */}
        {form.category && form.priceDay && <PricingSuggestion category={form.category} price={form.priceDay} />}

        <div style={S.fg}><label style={S.label}>ZIP Code</label><input style={S.input} placeholder="e.g. 90210" value={form.zip} onChange={e=>set("zip",e.target.value)} maxLength={5} /></div>

        {/* Photo upload — multiple */}
        <div style={S.fg}>
          <label style={S.label}>Listing Photos (up to 8)</label>
          {/* Existing photos (edit mode) */}
          {existingPhotos.length > 0 && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
              {existingPhotos.map((url, i) => (
                <div key={i} style={{ position:"relative", width:72, height:72, borderRadius:8, overflow:"hidden", border:`2px solid ${COLORS.border}` }}>
                  <img src={url} alt={`Photo ${i+1}`} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  <button onClick={()=>setExistingPhotos(p=>p.filter((_,j)=>j!==i))} style={{ position:"absolute", top:2, right:2, background:"rgba(0,0,0,0.7)", color:"#fff", border:"none", borderRadius:"50%", width:20, height:20, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                  {i===0 && <span style={{ position:"absolute", bottom:2, left:2, background:COLORS.accent, color:"#000", fontSize:8, fontWeight:700, padding:"1px 4px", borderRadius:4 }}>MAIN</span>}
                </div>
              ))}
            </div>
          )}
          {/* New photo files to upload */}
          {photoFiles.length > 0 && (
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10 }}>
              {photoFiles.map((file, i) => (
                <div key={i} style={{ position:"relative", width:72, height:72, borderRadius:8, overflow:"hidden", border:`2px solid ${COLORS.green}` }}>
                  <img src={URL.createObjectURL(file)} alt={`New ${i+1}`} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  <button onClick={()=>setPhotoFiles(p=>p.filter((_,j)=>j!==i))} style={{ position:"absolute", top:2, right:2, background:"rgba(0,0,0,0.7)", color:"#fff", border:"none", borderRadius:"50%", width:20, height:20, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                  <span style={{ position:"absolute", bottom:2, left:2, background:COLORS.green, color:"#000", fontSize:8, fontWeight:700, padding:"1px 4px", borderRadius:4 }}>NEW</span>
                </div>
              ))}
            </div>
          )}
          {totalPhotos < 8 && (
            <div onClick={()=>document.getElementById('listingPhoto').click()} style={{ border:`2px dashed ${totalPhotos>0?COLORS.green:COLORS.border}`, borderRadius:10, padding:14, textAlign:"center", cursor:"pointer", background:totalPhotos>0?COLORS.green+"08":"transparent", transition:"all 0.2s" }}>
              <input id="listingPhoto" type="file" accept="image/*" multiple style={{ display:"none" }} onChange={e=>{
                const files = Array.from(e.target.files).slice(0, 8 - totalPhotos);
                setPhotoFiles(p=>[...p, ...files]);
                e.target.value = "";
              }} />
              <div style={{ fontSize:22, marginBottom:2 }}>📷</div>
              <div style={{ fontSize:13, color:COLORS.muted }}>{totalPhotos > 0 ? `Add more photos (${totalPhotos}/8)` : "Upload photos of your item"}</div>
            </div>
          )}
        </div>

        <div style={{ display:"flex", gap:20, marginBottom:14, flexWrap:"wrap" }}>
          {[["delivery","🚚 Offer Delivery"],["trainingRequired","🎓 Requires Safety Training"],["instantBook","⚡ Instant Book"]].map(([k,label])=>(
            <div key={k} onClick={()=>set(k,!form[k])} style={{ display:"flex", alignItems:"center", gap:7, cursor:"pointer", fontSize:13 }}>
              <span style={{ fontSize:18, color:form[k]?COLORS.green:COLORS.muted }}>{form[k]?"☑":"☐"}</span>
              <span>{label}</span>
            </div>
          ))}
        </div>
        {error && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:10, fontSize:12, color:COLORS.red, marginBottom:12 }}>{error}</div>}

        {isEdit && !showDeleteConfirm && (
          <button onClick={()=>setShowDeleteConfirm(true)} style={{ background:"transparent", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:"10px 14px", color:COLORS.red, fontSize:12, fontWeight:600, cursor:"pointer", width:"100%", marginBottom:12, transition:"all 0.2s" }}>
            🗑️ Remove This Listing
          </button>
        )}

        {isEdit && showDeleteConfirm && (
          <div style={{ background:COLORS.red+"11", border:`1px solid ${COLORS.red}44`, borderRadius:10, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:700, color:COLORS.red, marginBottom:6 }}>⚠️ Are you sure?</div>
            <div style={{ fontSize:11, color:COLORS.muted, marginBottom:12, lineHeight:1.5 }}>
              This will permanently remove <strong style={{ color:COLORS.text }}>"{editItem.title}"</strong> from the platform. Any active bookings will be cancelled. This action cannot be undone.
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>setShowDeleteConfirm(false)} style={{ ...S.btn("outline"), flex:1, fontSize:12 }}>Keep Listing</button>
              <button onClick={handleDelete} disabled={deleting} style={{ background:COLORS.red, color:"#fff", border:"none", borderRadius:8, padding:"8px 14px", fontSize:12, fontWeight:700, cursor:"pointer", flex:1 }}>
                {deleting ? "⏳ Removing..." : "🗑️ Yes, Remove Permanently"}
              </button>
            </div>
          </div>
        )}

        <div style={S.row}>
          <button onClick={onClose} style={{ ...S.btn("outline"), flex:1 }}>Cancel</button>
          <button onClick={handleSave} disabled={!form.title||!form.category||!form.priceDay||saving} style={{ ...S.btn("primary"), flex:2, opacity:form.title&&form.category&&form.priceDay?1:0.4 }}>
            {saving ? (uploadProgress || (isEdit ? "⏳ Saving..." : "⏳ Publishing...")) : (isEdit ? "💾 Save Changes" : "📤 Publish Listing")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LOCATION VIDEO MODAL (Animated Commercial for each State/Country) ──────
function LocationVideoModal({ location, onClose }) {
  const [phase, setPhase] = useState(0); // 0=intro, 1=story, 2=cta, 3=done
  const [textOpacity, setTextOpacity] = useState(0);
  const [slideIn, setSlideIn] = useState(false);

  useEffect(() => {
    // Animate intro
    setTimeout(() => setTextOpacity(1), 300);
    setTimeout(() => setSlideIn(true), 600);
    // Transition phases
    const t1 = setTimeout(() => setPhase(1), 3000);
    const t2 = setTimeout(() => setPhase(2), 9000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const bgGradients = [
    `linear-gradient(135deg, ${COLORS.surface} 0%, #1a2332 50%, ${COLORS.card} 100%)`,
    `linear-gradient(135deg, #0f1923 0%, #162030 50%, #1a2840 100%)`,
    `linear-gradient(135deg, #1a1205 0%, #2a1f0a 50%, #1a1205 100%)`,
  ];

  return (
    <div style={{ position:"fixed", inset:0, zIndex:10000, background:"rgba(0,0,0,0.92)", display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ width:"100%", maxWidth:720, margin:"0 16px", borderRadius:16, overflow:"hidden", position:"relative", background:bgGradients[phase] || bgGradients[0], border:`1px solid ${COLORS.border}`, boxShadow:"0 20px 60px rgba(0,0,0,0.8)", transition:"background 1.5s ease" }} onClick={e=>e.stopPropagation()}>

        {/* Close button */}
        <button onClick={onClose} style={{ position:"absolute", top:12, right:12, zIndex:10, background:"rgba(0,0,0,0.6)", border:"none", color:"#fff", fontSize:20, width:36, height:36, borderRadius:"50%", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>

        {/* Video-like aspect ratio container */}
        <div style={{ position:"relative", width:"100%", paddingTop:"56.25%", overflow:"hidden" }}>
          {/* Background image with Ken Burns effect */}
          <img src={location.img.replace("w=300&h=120", "w=1280&h=720")} alt={location.name} style={{
            position:"absolute", inset:"-10%", width:"120%", height:"120%", objectFit:"cover",
            animation:"kenburns 15s ease-in-out infinite alternate",
            filter:"brightness(0.5) saturate(1.2)",
            transition:"filter 1.5s ease"
          }} />

          {/* Overlay gradient */}
          <div style={{ position:"absolute", inset:0, background:"linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.5) 100%)" }} />

          {/* Content phases */}
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", justifyContent:"flex-end", padding:"30px 28px" }}>

            {/* Phase 0: Intro - Flag & Location Name */}
            {phase === 0 && (
              <div style={{ textAlign:"center", opacity:textOpacity, transition:"opacity 0.8s ease", transform:slideIn?"translateY(0)":"translateY(30px)", transitionProperty:"opacity, transform", transitionDuration:"0.8s" }}>
                <div style={{ fontSize:64, marginBottom:12, filter:"drop-shadow(0 4px 12px rgba(0,0,0,0.5))" }}>{location.flag}</div>
                <div style={{ fontFamily:"'Georgia',serif", fontSize:36, fontWeight:"bold", color:"#fff", textShadow:"0 2px 20px rgba(0,0,0,0.8)", marginBottom:8, letterSpacing:2 }}>{location.name}</div>
                <div style={{ color:COLORS.accent, fontSize:14, fontWeight:700, textTransform:"uppercase", letterSpacing:4 }}>Rent This Rent That</div>
                <div style={{ width:60, height:3, background:COLORS.accent, margin:"16px auto 0", borderRadius:2 }} />
              </div>
            )}

            {/* Phase 1: The Story */}
            {phase === 1 && (
              <div style={{ animation:"fadeSlideUp 1s ease forwards" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <span style={{ fontSize:28 }}>{location.flag}</span>
                  <span style={{ fontFamily:"'Georgia',serif", fontSize:20, fontWeight:"bold", color:"#fff" }}>{location.name}</span>
                </div>
                <div style={{ color:"#e0e0e0", fontSize:15, lineHeight:1.8, maxHeight:180, overflowY:"auto", paddingRight:8, textShadow:"0 1px 4px rgba(0,0,0,0.5)" }}>
                  {location.story}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:16 }}>
                  <div style={{ width:40, height:3, background:COLORS.accent, borderRadius:2 }} />
                  <span style={{ color:COLORS.accent, fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:2 }}>Our Story</span>
                </div>
              </div>
            )}

            {/* Phase 2: Call to Action */}
            {phase === 2 && (
              <div style={{ textAlign:"center", animation:"fadeSlideUp 1s ease forwards" }}>
                <div style={{ fontSize:48, marginBottom:8 }}>{location.flag}</div>
                <div style={{ fontFamily:"'Georgia',serif", fontSize:24, fontWeight:"bold", color:"#fff", marginBottom:8 }}>Start Renting in {location.name}</div>
                <div style={{ color:COLORS.muted, fontSize:14, marginBottom:20, lineHeight:1.6 }}>Join the Rent This Rent That community.<br/>Rent what you need. Earn from what you own.</div>
                <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
                  <button onClick={onClose} style={{ padding:"12px 28px", borderRadius:10, border:"none", background:COLORS.accent, color:"#000", fontWeight:700, fontSize:15, cursor:"pointer", boxShadow:"0 4px 20px rgba(244,162,53,0.4)" }}>Browse Listings</button>
                  <button onClick={onClose} style={{ padding:"12px 28px", borderRadius:10, border:`1px solid ${COLORS.accent}`, background:"transparent", color:COLORS.accent, fontWeight:700, fontSize:15, cursor:"pointer" }}>List Your Item</button>
                </div>
                <div style={{ marginTop:20, color:COLORS.muted, fontSize:11 }}>
                  🌐 rentthisrentthat.com &nbsp;|&nbsp; 📧 rentthisrentthat@proton.me
                </div>
              </div>
            )}
          </div>

          {/* Progress bar */}
          <div style={{ position:"absolute", bottom:0, left:0, right:0, height:3, background:"rgba(255,255,255,0.1)" }}>
            <div style={{
              height:"100%", background:COLORS.accent, borderRadius:2,
              width: phase === 0 ? "33%" : phase === 1 ? "66%" : "100%",
              transition:"width 3s linear"
            }} />
          </div>

          {/* Play indicator */}
          <div style={{ position:"absolute", top:14, left:14, display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#E74C3C", animation:"pulse 1.5s ease infinite" }} />
            <span style={{ color:"rgba(255,255,255,0.7)", fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:1 }}>RTRT Commercial</span>
          </div>

          {/* Phase dots */}
          <div style={{ position:"absolute", bottom:12, left:"50%", transform:"translateX(-50%)", display:"flex", gap:8 }}>
            {[0,1,2].map(p=>(
              <div key={p} onClick={(e)=>{e.stopPropagation(); setPhase(p);}} style={{ width:phase===p?20:8, height:8, borderRadius:4, background:phase===p?COLORS.accent:"rgba(255,255,255,0.3)", cursor:"pointer", transition:"all 0.3s ease" }} />
            ))}
          </div>
        </div>

        {/* Bottom info bar */}
        <div style={{ padding:"14px 20px", background:COLORS.surface, borderTop:`1px solid ${COLORS.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:20 }}>{location.flag}</span>
            <div>
              <div style={{ color:COLORS.text, fontSize:13, fontWeight:700 }}>{location.name}</div>
              <div style={{ color:COLORS.muted, fontSize:11 }}>Rent This Rent That Community</div>
            </div>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={()=>setPhase(p=>p>0?p-1:0)} style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${COLORS.border}`, background:"transparent", color:COLORS.muted, cursor:"pointer", fontSize:12 }}>⏮ Prev</button>
            <button onClick={()=>setPhase(p=>p<2?p+1:2)} style={{ padding:"6px 12px", borderRadius:6, border:`1px solid ${COLORS.border}`, background:"transparent", color:COLORS.muted, cursor:"pointer", fontSize:12 }}>Next ⏭</button>
          </div>
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes kenburns { 0% { transform: scale(1) translate(0,0); } 100% { transform: scale(1.15) translate(-2%, -1%); } }
        @keyframes fadeSlideUp { 0% { opacity:0; transform:translateY(20px); } 100% { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
      `}</style>
    </div>
  );
}

// ─── IN-APP MESSAGING ────────────────────────────────────────────────────────
function MessagingModal({ user, otherUserId, otherUserName, listingTitle, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const conversationId = [user.id, otherUserId].sort().join("_");

  useEffect(() => {
    const fetchMessages = async () => {
      const { data } = await supabase.from("messages").select("*")
        .eq("conversation_id", conversationId).order("created_at", { ascending: true });
      if (data) setMessages(data);
      setLoading(false);
    };
    fetchMessages();
    // Real-time subscription
    const channel = supabase.channel(`msgs_${conversationId}`)
      .on("postgres_changes", { event:"INSERT", schema:"public", table:"messages", filter:`conversation_id=eq.${conversationId}` },
        payload => setMessages(prev => [...prev, payload.new]))
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [conversationId]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    const msg = { conversation_id: conversationId, sender_id: user.id, receiver_id: otherUserId, content: newMsg.trim(), listing_title: listingTitle };
    await supabase.from("messages").insert(msg);
    setNewMsg("");
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,0.8)", display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
      <div style={{ width:"100%", maxWidth:480, maxHeight:"80vh", margin:16, background:COLORS.surface, borderRadius:16, border:`1px solid ${COLORS.border}`, display:"flex", flexDirection:"column", overflow:"hidden" }} onClick={e=>e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding:"14px 18px", borderBottom:`1px solid ${COLORS.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ color:COLORS.text, fontWeight:700, fontSize:15 }}>💬 {otherUserName}</div>
            {listingTitle && <div style={{ color:COLORS.muted, fontSize:11 }}>Re: {listingTitle}</div>}
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:COLORS.muted, fontSize:20, cursor:"pointer" }}>✕</button>
        </div>
        {/* Messages */}
        <div style={{ flex:1, overflowY:"auto", padding:16, display:"flex", flexDirection:"column", gap:8, minHeight:200, maxHeight:400 }}>
          {loading ? <div style={{ color:COLORS.muted, textAlign:"center", padding:30 }}>Loading messages...</div> :
            messages.length === 0 ? <div style={{ color:COLORS.muted, textAlign:"center", padding:30, fontSize:13 }}>No messages yet. Say hello!</div> :
            messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.sender_id === user.id ? "flex-end" : "flex-start", maxWidth:"75%" }}>
                <div style={{ background: m.sender_id === user.id ? COLORS.accent : COLORS.card, color: m.sender_id === user.id ? "#000" : COLORS.text, padding:"10px 14px", borderRadius:14, fontSize:13, lineHeight:1.5 }}>{m.content}</div>
                <div style={{ fontSize:9, color:COLORS.muted, marginTop:2, textAlign: m.sender_id === user.id ? "right" : "left" }}>{new Date(m.created_at).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}</div>
              </div>
            ))
          }
          <div ref={messagesEndRef} />
        </div>
        {/* Input */}
        <div style={{ padding:12, borderTop:`1px solid ${COLORS.border}`, display:"flex", gap:8 }}>
          <input value={newMsg} onChange={e=>setNewMsg(e.target.value)} onKeyDown={e=>e.key==="Enter"&&sendMessage()} placeholder="Type a message..." style={{ flex:1, padding:"10px 14px", borderRadius:10, border:`1px solid ${COLORS.border}`, background:COLORS.card, color:COLORS.text, fontSize:13, outline:"none" }} />
          <button onClick={sendMessage} style={{ padding:"10px 16px", borderRadius:10, border:"none", background:COLORS.accent, color:"#000", fontWeight:700, cursor:"pointer", fontSize:13 }}>Send</button>
        </div>
      </div>
    </div>
  );
}

// ─── OWNER ANALYTICS DASHBOARD ───────────────────────────────────────────────
function OwnerDashboard({ myListings, myBookings, user, claims, onFileClaim, onViewClaim }) {
  const totalListings = myListings.length;
  const activeListings = myListings.filter(l => l.status === "active").length;
  const ownerBookings = myBookings.filter(b => myListings.some(l => l.id === b.listing_id));
  const totalBookings = ownerBookings.length;
  const totalEarnings = ownerBookings.reduce((sum, b) => sum + (b.total_price || 0), 0);
  const avgRating = myListings.length > 0 ? (myListings.reduce((s, l) => s + (l.avg_rating || 0), 0) / myListings.length).toFixed(1) : "N/A";
  const activeClaims = (claims || []).filter(c => c.claimant_id === user?.id && !["resolved","withdrawn","denied"].includes(c.status));

  const stats = [
    { label:"Total Listings", value:totalListings, icon:"📦", color:COLORS.blue },
    { label:"Active", value:activeListings, icon:"✅", color:COLORS.green },
    { label:"Bookings", value:totalBookings, icon:"📅", color:COLORS.purple },
    { label:"Earnings", value:`$${totalEarnings.toLocaleString()}`, icon:"💰", color:COLORS.accent },
    { label:"Avg Rating", value:avgRating, icon:"⭐", color:"#FFD700" },
  ];

  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ fontSize:18, fontWeight:700, color:COLORS.text, marginBottom:14 }}>📊 Your Dashboard</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:10 }}>
        {stats.map(s=>(
          <div key={s.label} style={{ background:COLORS.card, borderRadius:12, padding:"16px 14px", border:`1px solid ${COLORS.border}`, textAlign:"center" }}>
            <div style={{ fontSize:28, marginBottom:6 }}>{s.icon}</div>
            <div style={{ fontSize:22, fontWeight:"bold", color:s.color }}>{s.value}</div>
            <div style={{ fontSize:11, color:COLORS.muted, marginTop:4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Recent activity with File Claim buttons */}
      <div style={{ marginTop:16, background:COLORS.card, borderRadius:12, padding:16, border:`1px solid ${COLORS.border}` }}>
        <div style={{ fontSize:13, fontWeight:700, color:COLORS.text, marginBottom:10 }}>📈 Recent Bookings</div>
        {ownerBookings.slice(0, 5).map((b, i) => {
          const listing = myListings.find(l => l.id === b.listing_id);
          const bookingClaims = (claims || []).filter(c => c.booking_id === b.id);
          const isCompleted = b.status === "completed" || b.status === "confirmed";
          return (
            <div key={i} style={{ padding:"10px 0", borderBottom: i < 4 ? `1px solid ${COLORS.border}` : "none" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ color:COLORS.text, fontSize:13, fontWeight:600 }}>{listing?.title || "Rental"}</div>
                  <div style={{ color:COLORS.muted, fontSize:11 }}>{b.start_date} → {b.end_date} • {b.profiles?.full_name || "Renter"}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <span style={S.badge(b.status==="confirmed"?COLORS.green:b.status==="pending"?COLORS.accent:COLORS.muted)}>{b.status}</span>
                  <div style={{ color:COLORS.green, fontWeight:700, fontSize:13, marginTop:2 }}>+${b.total_amount || b.total_price || 0}</div>
                </div>
              </div>
              {/* Existing claims on this booking */}
              {bookingClaims.length > 0 && (
                <div style={{ marginTop:6, display:"flex", gap:6, flexWrap:"wrap" }}>
                  {bookingClaims.map(c => (
                    <div key={c.id} style={{ fontSize:11, display:"flex", alignItems:"center", gap:4 }}>
                      <ClaimStatusBadge status={c.status} />
                      <span style={{ color:COLORS.muted }}>${c.amount_requested}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* File Claim button (show for completed/confirmed bookings without active claims) */}
              {isCompleted && bookingClaims.filter(c=>!["denied","withdrawn"].includes(c.status)).length === 0 && onFileClaim && (
                <button onClick={() => onFileClaim(b, listing)} style={{ ...S.btn("red","sm"), marginTop:8, fontSize:11 }}>🛡️ File Damage Claim</button>
              )}
            </div>
          );
        })}
        {ownerBookings.length === 0 && (
          <div style={{ color:COLORS.muted, fontSize:13, textAlign:"center", padding:16 }}>No bookings yet. Share your listings to start earning!</div>
        )}
      </div>

      {/* Active Claims Section */}
      {activeClaims.length > 0 && (
        <div style={{ marginTop:16, background:COLORS.card, borderRadius:12, padding:16, border:`1px solid ${COLORS.border}` }}>
          <div style={{ fontSize:13, fontWeight:700, color:COLORS.text, marginBottom:10 }}>🛡️ Active Claims ({activeClaims.length})</div>
          {activeClaims.map(c => (
            <div key={c.id} style={{ background:COLORS.surface, borderRadius:8, padding:12, marginBottom:8, fontSize:12 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                <span style={{ fontWeight:700 }}>{c.listings?.title || "Item"}</span>
                <ClaimStatusBadge status={c.status} />
              </div>
              <div style={{ color:COLORS.muted, fontSize:11, marginBottom:4 }}>{CLAIM_TYPES.find(t=>t.id===c.claim_type)?.icon} {CLAIM_TYPES.find(t=>t.id===c.claim_type)?.label} • ${c.amount_requested}</div>
              {c.response_text && <div style={{ color:COLORS.muted, fontSize:11 }}>Renter response: "{c.response_text.slice(0,100)}{c.response_text.length > 100 ? "..." : ""}"</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── BLOG / TIPS SECTION ─────────────────────────────────────────────────────
const BLOG_POSTS = [
  { id:1, title:"How to Prep Your Boat for Renters", category:"Boats", icon:"⛵", excerpt:"Make your boat rental-ready with this comprehensive checklist covering safety gear, cleaning, documentation, and insurance tips.", readTime:"5 min", date:"Mar 2026",
    content:"Getting your boat ready for renters takes a bit of preparation, but it pays off with great reviews and repeat customers.\n\nSafety First: Ensure all life jackets are in good condition and you have enough for the max capacity. Check fire extinguishers, flares, and first aid kits. Make sure navigation lights work.\n\nCleaning & Presentation: A spotless boat gets 5-star reviews. Wash the hull, clean the interior, and remove personal items. Take great photos in golden-hour light.\n\nDocumentation: Have your registration, insurance certificate, and rental agreement ready. Include a laminated quick-start guide for renters who may be unfamiliar with your vessel.\n\nPricing: Research similar boats in your area on Rent This Rent That. Factor in fuel, cleaning time, and wear. Most boat owners charge $150-$500/day depending on size and type.\n\nInsurance: Our platform includes protection for every rental, but make sure your personal boat insurance allows peer-to-peer rentals. Contact your provider to add a rider if needed." },
  { id:2, title:"Top 10 Things to Rent for a Backyard Wedding", category:"Wedding", icon:"💍", excerpt:"From elegant lighting to tables, chairs, and a sound system — save thousands by renting instead of buying for your special day.", readTime:"4 min", date:"Mar 2026",
    content:"A beautiful backyard wedding doesn't have to break the bank. Here are the top 10 items couples rent on our platform:\n\n1. String Lights & Lanterns — Create magical ambiance for $30-60/day\n2. Tables & Chairs — Sets of 8 typically rent for $40-80/day\n3. Tent or Canopy — Weather protection for $100-300/day depending on size\n4. Sound System & Speakers — Professional PA systems from $50-150/day\n5. Dance Floor — Portable dance floors from $100-200/day\n6. Photo Booth — Complete setups with props from $75-150/day\n7. Arch or Arbor — Ceremony backdrops from $40-100/day\n8. Catering Equipment — Chafing dishes, serving trays from $30-60/day\n9. Linens & Table Decor — Tablecloths, runners, centerpiece vases from $20-50/day\n10. Coolers & Beverage Stations — Keep drinks cold for $15-30/day\n\nTotal savings vs. a venue: typically $3,000-$8,000. Search 'Wedding' on Rent This Rent That to find these items near you!" },
  { id:3, title:"First-Time Renter? Here's What You Need to Know", category:"Guide", icon:"📖", excerpt:"Your complete guide to renting on Rent This Rent That — from creating an account to picking up your rental and leaving a review.", readTime:"6 min", date:"Feb 2026",
    content:"Welcome to Rent This Rent That! Here's everything you need for your first rental:\n\nStep 1: Create Your Account — Sign up with your email, add a profile photo, and verify your identity. This builds trust with owners.\n\nStep 2: Browse & Search — Use categories or search to find what you need. Filter by location, price, and availability. Check ratings and reviews from other renters.\n\nStep 3: Book Your Rental — Select your dates, choose a protection plan, and pick your delivery option (pickup, drop-off, or full-service). You'll see the full cost breakdown before confirming.\n\nStep 4: Communicate — Use our messaging system to coordinate pickup details, ask questions, and confirm timing with the owner.\n\nStep 5: Pick Up & Inspect — When you receive the item, inspect it together with the owner. Note any existing wear. Take photos for your records.\n\nStep 6: Enjoy & Return — Use the item, return it on time and in the same condition. Late returns may incur extra charges.\n\nStep 7: Leave a Review — Help the community by sharing your experience. Honest reviews help other renters make informed decisions.\n\nTip: Items with a license requirement (boats, vehicles, aircraft) will ask you to upload your credentials before booking." },
  { id:4, title:"Earn $1,000/Month Renting Your Power Tools", category:"Tools", icon:"🔧", excerpt:"Your garage is a goldmine. Learn how top earners on our platform turn idle tools into a steady income stream.", readTime:"4 min", date:"Feb 2026",
    content:"Your garage could be earning you serious money. Here's how top earners on Rent This Rent That make $1,000+ per month from tools:\n\nMost Popular Rentals: Pressure washers ($40-60/day), table saws ($35-50/day), concrete mixers ($45-70/day), generators ($50-80/day), and nail guns ($20-35/day).\n\nPricing Strategy: Check what similar items rent for in your area. Start slightly below market to build reviews, then raise prices once you have 5+ five-star ratings.\n\nAvailability: The more days your tools are available, the more you earn. Weekend-only availability can still net $400-600/month for popular items.\n\nMaintenance: Keep tools clean, sharp, and serviced. Replace worn parts promptly. Well-maintained tools get better reviews and command higher prices.\n\nPhotos Matter: Take clear, well-lit photos showing the tool from multiple angles. Include brand, model, and any accessories. Good photos increase booking rates by 3x.\n\nBundle Deals: Offer weekly rates at a 15-20% discount. Many contractors need tools for multi-day projects and prefer bundled pricing." },
  { id:5, title:"The Complete RV Road Trip Checklist", category:"RVs", icon:"🚐", excerpt:"Before you hit the road, make sure you've covered these essentials — from safety inspections to campsite reservations.", readTime:"5 min", date:"Jan 2026",
    content:"Planning an RV trip? Whether you're renting or listing your RV on Rent This Rent That, this checklist has you covered:\n\nBefore You Go: Check tire pressure and tread depth. Test all lights (headlights, brake, turn signals). Verify propane levels. Test the water system and check for leaks. Charge the house batteries.\n\nSafety Equipment: Fire extinguisher (check expiration), smoke and CO detectors, first aid kit, roadside emergency kit, jumper cables.\n\nKitchen & Living: Stock paper towels, trash bags, dish soap, and basic cookware. Bring leveling blocks and wheel chocks for setup at camp.\n\nCampsite Prep: Book campsites in advance during peak season. Know your RV dimensions (length, height) for site compatibility. Download offline maps for areas without cell service.\n\nDriving Tips: Take corners slowly, allow extra braking distance, use mirrors constantly. Avoid driving at night in unfamiliar areas. Plan fuel stops — not all stations accommodate large RVs.\n\nReturn Checklist: Empty holding tanks, refill propane to the level you received it, clean the interior, and document the odometer reading." },
  { id:6, title:"How Peer-to-Peer Renting Helps the Environment", category:"Sustainability", icon:"🌍", excerpt:"Sharing instead of buying reduces waste, lowers carbon footprints, and builds community. Here's the data behind the movement.", readTime:"3 min", date:"Jan 2026",
    content:"The sharing economy isn't just about saving money — it's a powerful force for environmental good.\n\nLess Manufacturing Waste: The average power drill is used for only 13 minutes in its entire lifetime. When we share instead of each buying our own, we dramatically reduce demand for manufacturing, raw materials, and shipping.\n\nSmaller Carbon Footprint: Manufacturing a single car produces about 6 tons of CO2. Car-sharing and peer-to-peer vehicle rentals keep existing cars in use longer and reduce the number of new vehicles needed.\n\nReduced Landfill Waste: Americans throw away 9.8 million tons of furniture annually. Renting furniture, event supplies, and seasonal items keeps them in circulation instead of landfills.\n\nCommunity Building: Peer-to-peer renting connects neighbors and builds trust. Studies show that communities with active sharing economies report higher social cohesion and neighborhood satisfaction.\n\nEvery rental on Rent This Rent That is a vote for a more sustainable future. Together, our community has prevented an estimated 200+ tons of waste from entering landfills." },
  { id:7, title:"Protect Yourself: Insurance & Rental Agreements", category:"Safety", icon:"🛡️", excerpt:"Understanding broker agreements, insurance coverage, and your rights as a renter or owner on the platform.", readTime:"5 min", date:"Dec 2025",
    content:"Understanding protection on Rent This Rent That keeps both renters and owners safe.\n\nInsurance Coverage: Every booking includes insurance protection. We offer three tiers — Basic (6%), Standard (10%), and Premium (15%) — calculated on the rental subtotal. Higher tiers cover more types of damage and have lower deductibles.\n\nWhat's Covered: Accidental damage during normal use, theft during the rental period (with a police report), and transit damage during delivery. Intentional damage, illegal activity, and unauthorized use are not covered.\n\nSecurity Deposits: A 20% refundable security deposit (minimum $25) is held at booking and returned within 48 hours after a successful return with no damage claims.\n\nBroker Agreement: Our insurance is brokered through licensed partners. The broker agreement is presented during checkout and covers the specific terms of your rental period.\n\nFor Owners: Document your item's condition with photos before and after each rental. This protects you in case of a dispute. Respond to damage claims within 72 hours.\n\nFor Renters: Inspect items at pickup and note any pre-existing issues. Take photos. This protects you from being charged for damage you didn't cause." },
  { id:8, title:"Holiday Party on a Budget: Rent Everything", category:"Events", icon:"🎉", excerpt:"Throw an unforgettable holiday party without breaking the bank. Rent decorations, sound systems, bounce houses, and more.", readTime:"3 min", date:"Dec 2025",
    content:"Hosting a memorable holiday party doesn't require buying everything new. Here's how to throw an amazing celebration on a budget:\n\nDecor: Rent string lights, wreaths, table centerpieces, and themed decorations. Cost: $30-80 vs. $200+ to buy.\n\nEntertainment: A quality Bluetooth speaker or full PA system rental costs $30-100/day. Add a karaoke machine for $40-60/day. Photo booth setups with props run $75-150/day.\n\nFor Kids: Bounce houses ($100-200/day), cotton candy machines ($50-75/day), and popcorn makers ($30-50/day) are crowd favorites.\n\nSeating & Tables: Folding tables and chairs for 20+ guests can be rented for $60-120, vs. $300+ to purchase.\n\nOutdoor Heating: Patio heaters rent for $30-50/day — essential for winter outdoor gatherings.\n\nPro Tips: Book at least 2 weeks ahead during holiday season. Ask owners about multi-day discounts. Leave great reviews to build your reputation for future rentals.\n\nSearch 'Party & Entertainment' or 'Events' on Rent This Rent That to find everything you need nearby." },
];

// ─── TESTIMONIALS ────────────────────────────────────────────────────────────
const TESTIMONIALS = [
  { name:"Marcus J.", location:"Miami, FL", avatar:"👨‍✈️", rating:5, text:"We rented a pontoon boat for my daughter's birthday. The owner was amazing — met us at the dock, gave us a quick tour, and the kids had the best day ever. Will definitely use RTRT again!", category:"Boats" },
  { name:"Sarah & Tom K.", location:"Austin, TX", avatar:"👫", rating:5, text:"Rented a full BBQ setup for our block party — smoker, tables, chairs, even string lights. Saved us over $2,000 compared to a catering company. The community here is so generous.", category:"Events" },
  { name:"David L.", location:"Denver, CO", avatar:"🧔", rating:5, text:"I rent out my snowboard gear and camping equipment when I'm not using it. Made $3,400 last season just from things sitting in my garage. This platform changed my side hustle game.", category:"Outdoor Equipment" },
  { name:"Maria G.", location:"San Diego, CA", avatar:"👩", rating:5, text:"We rented this buggy for a Christmas parade — it allowed us to celebrate in style and provide Christmas money to the person renting it to us!", category:"Automobiles" },
  { name:"James W.", location:"Nashville, TN", avatar:"🎸", rating:5, text:"Rented a full PA system and stage lighting for my band's show. Professional quality gear at a fraction of the cost. The owner even helped us set up!", category:"AV & Sound" },
  { name:"Linda P.", location:"Portland, OR", avatar:"👩‍🌾", rating:5, text:"I needed a rototiller for one weekend. Why buy a $500 machine for a single use? Found one on RTRT for $35/day from a neighbor two blocks away. Brilliant.", category:"Garden" },
  { name:"Chen W.", location:"Seattle, WA", avatar:"📸", rating:5, text:"Rented a professional camera and drone for my engagement photos. The owner included lenses and even gave me photography tips. Saved us $800 vs hiring a photographer.", category:"Camera & Film" },
  { name:"The Rodriguez Family", location:"Phoenix, AZ", avatar:"👨‍👩‍👧‍👦", rating:5, text:"We rent our bounce house and cotton candy machine on weekends. It pays for itself 10x over. Other families get to make memories and we earn extra income. Win-win!", category:"Party & Entertainment" },
];

// ─── MONTHLY ROTATING PROMOS (changes every month, showcases actual categories) ─
function getSeasonalPromos() {
  const month = new Date().getMonth(); // 0=Jan ... 11=Dec
  const MONTHLY_PROMOS = {
    0: [ // January
      { title:"🎿 New Year Adventure Gear", desc:"Kick off the year — rent ski, snowboard, and snowmobile gear from local owners.", category:"Outdoor Equipment", color:"#60A5FA", bg:"linear-gradient(135deg, #1E40AF 0%, #60A5FA 100%)" },
      { title:"🏋️ Fitness Equipment Rentals", desc:"New year, new goals — rent treadmills, weights, and gym equipment monthly.", category:"Fitness Equipment", color:"#10B981", bg:"linear-gradient(135deg, #047857 0%, #10B981 100%)" },
    ],
    1: [ // February
      { title:"💝 Valentine's Event Rentals", desc:"Photo booths, string lights, and sound systems for a romantic night.", category:"Party & Entertainment", color:"#EC4899", bg:"linear-gradient(135deg, #9D174D 0%, #EC4899 100%)" },
      { title:"📸 Camera & Film Gear", desc:"Capture every moment — rent pro cameras, lenses, and drones.", category:"Camera & Film", color:"#8B5CF6", bg:"linear-gradient(135deg, #5B21B6 0%, #8B5CF6 100%)" },
    ],
    2: [ // March
      { title:"🌸 Spring Cleaning Tools", desc:"Pressure washers, garden tillers, and power tools — rent for the weekend!", category:"Power Tools", color:"#A855F7", bg:"linear-gradient(135deg, #7E22CE 0%, #A855F7 100%)" },
      { title:"🌿 Garden Equipment", desc:"Mowers, trimmers, tillers — get your yard ready without buying.", category:"Garden", color:"#16A34A", bg:"linear-gradient(135deg, #15803D 0%, #22C55E 100%)" },
    ],
    3: [ // April
      { title:"🏕️ Camping & Hiking Season", desc:"Tents, backpacks, and trail gear from experienced local adventurers.", category:"Camping & Hiking", color:"#059669", bg:"linear-gradient(135deg, #065F46 0%, #34D399 100%)" },
      { title:"🚐 Campers & RV Rentals", desc:"Hit the open road — rent a camper for your spring getaway.", category:"Campers & RVs", color:"#F59E0B", bg:"linear-gradient(135deg, #B45309 0%, #F59E0B 100%)" },
    ],
    4: [ // May
      { title:"💒 Wedding Season", desc:"Tents, chairs, lighting, and decor — make your dream wedding affordable.", category:"Wedding", color:"#EC4899", bg:"linear-gradient(135deg, #9D174D 0%, #EC4899 100%)" },
      { title:"🎸 Musical Instruments", desc:"Rent instruments for performances, lessons, or jam sessions.", category:"Musical Instruments", color:"#6366F1", bg:"linear-gradient(135deg, #4338CA 0%, #6366F1 100%)" },
    ],
    5: [ // June
      { title:"☀️ Summer Boat Specials", desc:"Boats, jet skis, and pontoons — make waves this summer!", category:"Boats", color:"#0EA5E9", bg:"linear-gradient(135deg, #0369A1 0%, #0EA5E9 100%)" },
      { title:"🏄 Outdoor Equipment", desc:"Surfboards, paddleboards, kayaks — summer fun without the price tag.", category:"Outdoor Equipment", color:"#14B8A6", bg:"linear-gradient(135deg, #0D9488 0%, #2DD4BF 100%)" },
    ],
    6: [ // July
      { title:"🎆 4th of July Party Gear", desc:"Grills, tents, speakers, and party supplies for your celebration.", category:"Party & Entertainment", color:"#EF4444", bg:"linear-gradient(135deg, #991B1B 0%, #EF4444 100%)" },
      { title:"🚤 Jet Ski Rentals", desc:"Beat the heat on the water — rent jet skis from owners near you.", category:"Jet Skis", color:"#0EA5E9", bg:"linear-gradient(135deg, #0369A1 0%, #38BDF8 100%)" },
    ],
    7: [ // August
      { title:"📚 Back to School Gear", desc:"Laptops, cameras, and electronics — rent what you need for the semester.", category:"Electronics", color:"#3B82F6", bg:"linear-gradient(135deg, #1D4ED8 0%, #3B82F6 100%)" },
      { title:"🚗 Road Trip Vehicles", desc:"Rent a car, truck, or RV for your last summer road trip.", category:"Automobiles", color:"#F97316", bg:"linear-gradient(135deg, #C2410C 0%, #F97316 100%)" },
    ],
    8: [ // September
      { title:"🏈 Tailgate & Game Day", desc:"Grills, tents, coolers, and speakers — gear up for the season.", category:"Events", color:"#EA580C", bg:"linear-gradient(135deg, #9A3412 0%, #F97316 100%)" },
      { title:"🔧 Fall Home Projects", desc:"Power tools, ladders, and construction equipment for your projects.", category:"Construction", color:"#78716C", bg:"linear-gradient(135deg, #44403C 0%, #A8A29E 100%)" },
    ],
    9: [ // October
      { title:"🎃 Halloween Costume Rentals", desc:"Why buy? Rent premium costumes, props, and decor from $15/day.", category:"Costumes & Cosplay", color:"#F97316", bg:"linear-gradient(135deg, #9A3412 0%, #F97316 100%)" },
      { title:"🍂 Fall Festival Gear", desc:"Event tents, heaters, and party supplies for autumn gatherings.", category:"Events", color:"#EAB308", bg:"linear-gradient(135deg, #A16207 0%, #EAB308 100%)" },
    ],
    10: [ // November
      { title:"🦃 Holiday Hosting Essentials", desc:"Tables, chairs, serving sets, and warming trays — host without the hassle.", category:"Events", color:"#D97706", bg:"linear-gradient(135deg, #92400E 0%, #F59E0B 100%)" },
      { title:"🛋️ Furniture Rentals", desc:"Stage your home or furnish a guest room for the holidays.", category:"Furniture", color:"#8B5CF6", bg:"linear-gradient(135deg, #5B21B6 0%, #A78BFA 100%)" },
    ],
    11: [ // December
      { title:"🎄 Holiday Party Rentals", desc:"Decorations, sound systems, photo booths — rent it all!", category:"Party & Entertainment", color:"#EF4444", bg:"linear-gradient(135deg, #991B1B 0%, #EF4444 100%)" },
      { title:"🎿 Winter Adventure Gear", desc:"Ski, snowboard, and snowmobile rentals at the best rates.", category:"Outdoor Equipment", color:"#60A5FA", bg:"linear-gradient(135deg, #1E40AF 0%, #60A5FA 100%)" },
    ],
  };
  return MONTHLY_PROMOS[month] || MONTHLY_PROMOS[0];
}

// ─── ZIP CODE → LAT/LNG LOOKUP (common US ZIPs) ─────────────────────────────
// For a production app, use a geocoding API. This covers major metro areas for demo.
const ZIP_COORDS = {
  "10001":[40.7484,-73.9967],"10002":[40.7157,-73.9863],"10003":[40.7317,-73.9893],"10010":[40.7390,-73.9826],
  "10019":[40.7654,-73.9851],"10021":[40.7693,-73.9588],"10036":[40.7590,-73.9845],
  "20001":[38.9101,-77.0147],"20002":[38.9050,-76.9822],"20009":[38.9193,-77.0376],
  "30301":[33.7716,-84.3856],"30303":[33.7539,-84.3900],"30305":[33.8347,-84.3806],
  "33101":[25.7748,-80.1977],"33109":[25.7582,-80.1361],"33130":[25.7690,-80.2057],"33139":[25.7907,-80.1300],
  "37201":[36.1627,-86.7816],"37203":[36.1548,-86.7958],
  "60601":[41.8862,-87.6186],"60602":[41.8827,-87.6291],"60614":[41.9215,-87.6513],
  "70112":[29.9572,-90.0751],"70116":[29.9621,-90.0600],
  "73301":[30.2672,-97.7431],"75201":[32.7872,-96.7985],"77001":[29.7604,-95.3698],
  "80201":[39.7392,-104.9903],"80202":[39.7530,-105.0003],
  "85001":[33.4484,-112.0740],"85281":[33.4152,-111.8315],
  "90001":[33.9425,-118.2551],"90002":[33.9491,-118.2468],"90005":[34.0585,-118.3095],
  "90012":[34.0665,-118.2395],"90028":[34.0985,-118.3268],"90036":[34.0694,-118.3471],
  "90046":[34.1137,-118.3615],"90068":[34.1267,-118.3254],"90210":[34.0901,-118.4065],
  "90401":[34.0185,-118.4912],"90802":[33.7675,-118.1894],
  "92101":[32.7197,-117.1628],"92103":[32.7468,-117.1724],
  "94102":[37.7813,-122.4132],"94103":[37.7726,-122.4113],"94110":[37.7486,-122.4157],
  "94117":[37.7708,-122.4483],"94122":[37.7590,-122.4830],
  "95112":[37.3382,-121.8863],"95814":[38.5781,-121.4944],
  "97201":[45.5152,-122.6784],"97209":[45.5315,-122.6845],
  "98101":[47.6062,-122.3321],"98103":[47.6711,-122.3428],"98109":[47.6316,-122.3477],
};

// Approximate coords from ZIP (fallback: hash-based scatter across US)
function zipToCoords(zip) {
  if (!zip) return null;
  const z = String(zip).slice(0,5);
  if (ZIP_COORDS[z]) return ZIP_COORDS[z];
  // Fallback: use first 2 digits to estimate region
  const prefix = parseInt(z.slice(0,2), 10);
  // Very rough US ZIP prefix → lat/lng mapping
  const lat = 25 + (prefix / 99) * 24 + (parseInt(z.slice(2), 10) % 100) * 0.05;
  const lng = -125 + (prefix / 99) * 55 + (parseInt(z.slice(3), 10) % 100) * 0.1;
  return [lat, lng];
}

// ─── MAP VIEW COMPONENT (Split layout — Airbnb-style) ──────────────────────
function MapView({ listings, onSelectListing, favorites, toggleFavorite, highlightedId, onHoverListing }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const [leafletLoaded, setLeafletLoaded] = useState(false);

  // Load Leaflet CSS & JS from CDN
  useEffect(() => {
    if (window.L) { setLeafletLoaded(true); return; }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => setLeafletLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialize map once Leaflet loads
  useEffect(() => {
    if (!leafletLoaded || !mapRef.current || mapInstanceRef.current) return;
    const L = window.L;
    const map = L.map(mapRef.current, { zoomControl: false }).setView([39.8283, -98.5795], 4);
    L.control.zoom({ position: "topright" }).addTo(map);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => { map.remove(); mapInstanceRef.current = null; };
  }, [leafletLoaded]);

  // Build marker HTML helper
  const markerHtml = (priceDay, isFav, isHighlighted) => {
    const bg = isHighlighted ? "#000" : COLORS.accent;
    const fg = isHighlighted ? COLORS.accent : "#000";
    const scale = isHighlighted ? "scale(1.25)" : "scale(1)";
    const shadow = isHighlighted ? "0 4px 16px rgba(244,162,53,0.5)" : "0 2px 8px rgba(0,0,0,0.4)";
    return `<div style="background:${bg};color:${fg};font-weight:800;font-size:12px;padding:4px 8px;border-radius:8px;white-space:nowrap;box-shadow:${shadow};border:2px solid ${isHighlighted?COLORS.accent:"#fff"};cursor:pointer;display:flex;align-items:center;gap:3px;transform:${scale};transition:all 0.2s;z-index:${isHighlighted?999:1};">${isFav?"❤️ ":""}$${priceDay}/d</div>`;
  };

  // Add/update markers when listings change
  useEffect(() => {
    if (!leafletLoaded || !mapInstanceRef.current) return;
    const L = window.L;
    const map = mapInstanceRef.current;

    // Clear existing markers
    Object.values(markersRef.current).forEach(m => map.removeLayer(m));
    markersRef.current = {};

    const bounds = [];
    listings.forEach(item => {
      const coords = zipToCoords(item.zip_code);
      if (!coords) return;
      bounds.push(coords);

      const priceDay = item.price_per_day || item.priceDay || 0;
      const isFav = favorites?.includes(item.id);
      const icon = L.divIcon({
        className: "",
        html: markerHtml(priceDay, isFav, false),
        iconSize: [0, 0],
        iconAnchor: [30, 15],
      });
      const marker = L.marker(coords, { icon }).addTo(map);
      marker.bindPopup(`
        <div style="font-family:sans-serif;min-width:180px;">
          <div style="font-weight:700;font-size:14px;margin-bottom:4px;">${item.title}</div>
          <div style="color:#666;font-size:12px;margin-bottom:4px;">${item.category} • ZIP ${item.zip_code}</div>
          <div style="font-weight:800;color:#F4A235;font-size:16px;">$${priceDay}/day</div>
          <div style="color:#888;font-size:11px;margin-top:4px;">${item.profiles?.full_name || "Owner"}</div>
        </div>
      `);
      marker.on("click", () => onSelectListing(item));
      marker.on("mouseover", () => onHoverListing?.(item.id));
      marker.on("mouseout", () => onHoverListing?.(null));
      markersRef.current[item.id] = marker;
    });

    if (bounds.length > 0) {
      try { map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 }); } catch(e) {}
    }
  }, [leafletLoaded, listings, favorites, onSelectListing]);

  // Update highlighted marker style
  useEffect(() => {
    if (!leafletLoaded) return;
    const L = window.L;
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const item = listings.find(l => l.id === id);
      if (!item) return;
      const priceDay = item.price_per_day || item.priceDay || 0;
      const isFav = favorites?.includes(item.id);
      const isHl = highlightedId === id;
      marker.setIcon(L.divIcon({
        className: "",
        html: markerHtml(priceDay, isFav, isHl),
        iconSize: [0, 0],
        iconAnchor: [30, 15],
      }));
      if (isHl) marker.setZIndexOffset(1000); else marker.setZIndexOffset(0);
    });
  }, [highlightedId, leafletLoaded, listings, favorites]);

  if (!leafletLoaded) return (
    <div style={{ height:"100%", minHeight:400, background:COLORS.card, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center", color:COLORS.muted }}>
        <div style={{ fontSize:36, marginBottom:8 }}>🗺️</div>
        <div>Loading map...</div>
      </div>
    </div>
  );

  return (
    <div style={{ position:"relative", height:"100%", minHeight:400 }}>
      <div ref={mapRef} style={{ height:"100%", width:"100%" }} />
      <div style={{ position:"absolute", bottom:12, left:12, background:"rgba(13,15,20,0.9)", borderRadius:10, padding:"10px 14px", zIndex:1000, border:`1px solid ${COLORS.border}` }}>
        <div style={{ color:COLORS.text, fontSize:11, fontWeight:700, marginBottom:4 }}>📍 {listings.length} listing{listings.length!==1?"s":""} on map</div>
        <div style={{ color:COLORS.muted, fontSize:10 }}>Hover a card or pin to highlight</div>
      </div>
    </div>
  );
}

// ─── MAP LISTING CARD (compact card for split view) ─────────────────────────
function MapListingCard({ item, isFav, isHighlighted, onSelect, onToggleFav, onHover, onLeave, user, onMessage }) {
  const priceDay = item.price_per_day || item.priceDay || 0;
  const priceWeek = item.price_per_week || item.priceWeek || 0;
  const ownerName = item.profiles?.full_name || item.owner || "Lister";
  const licReq = LICENSE_REQUIREMENTS[item.category];
  return (
    <div
      onClick={() => onSelect(item)}
      onMouseEnter={() => onHover(item.id)}
      onMouseLeave={() => onLeave()}
      style={{
        background: isHighlighted ? COLORS.card : COLORS.surface,
        borderRadius: 12,
        border: `2px solid ${isHighlighted ? COLORS.accent : COLORS.border}`,
        cursor: "pointer",
        transition: "all 0.2s",
        display: "flex",
        overflow: "hidden",
        transform: isHighlighted ? "scale(1.02)" : "scale(1)",
      }}
    >
      {/* Thumbnail */}
      <div style={{ width: 120, minHeight: 110, flexShrink: 0, position: "relative", overflow: "hidden" }}>
        <ImageCarousel images={[...parseImageUrls(item.image_url), ...(parseImageUrls(item.image_url).length === 0 ? [CAT_IMAGES[item.category] || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop"] : [])]} height={110} />
        {licReq?.required && <span style={{ position:"absolute", top:4, right:4, background:COLORS.purple+"ee", color:"#fff", fontSize:8, fontWeight:700, padding:"2px 5px", borderRadius:12 }}>🪪</span>}
      </div>
      {/* Info */}
      <div style={{ padding:"10px 12px", flex:1, display:"flex", flexDirection:"column", justifyContent:"space-between", minWidth:0 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:14, lineHeight:1.2, marginBottom:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.title}</div>
          <div style={{ color:COLORS.muted, fontSize:11, marginBottom:4 }}>{ownerName}{item._distance != null ? ` • ${item._distance} mi` : ""}</div>
          <div style={{ display:"flex", gap:3, flexWrap:"wrap", marginBottom:4 }}>
            <span style={{ ...S.badge(COLORS.blue), fontSize:10, padding:"2px 6px" }}>{item.category}</span>
            {(item.delivery_available||item.delivery) && <span style={{ ...S.badge(COLORS.green), fontSize:10, padding:"2px 6px" }}>🚚</span>}
          </div>
        </div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div><span style={{ fontSize:17, fontWeight:"bold", color:COLORS.accent }}>${priceDay}</span><span style={{ color:COLORS.muted, fontSize:11 }}>/day</span></div>
          <button onClick={e=>{e.stopPropagation(); onToggleFav(item.id);}} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, padding:0 }} title={isFav?"Remove from favorites":"Add to favorites"}>
            {isFav ? "❤️" : "🤍"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CATEGORY PRICING HELPER ───────────────────────────────────────────────────
const CATEGORY_PRICING = {
  "Boats": { low: 85, high: 250 },
  "Jet Skis": { low: 75, high: 200 },
  "Aircraft": { low: 400, high: 1500 },
  "Campers & RVs": { low: 120, high: 350 },
  "Automobiles": { low: 60, high: 180 },
  "Motorcycles": { low: 45, high: 140 },
  "Heavy Equipment": { low: 150, high: 500 },
  "Power Tools": { low: 15, high: 60 },
  "Outdoor Equipment": { low: 25, high: 100 },
  "Construction": { low: 30, high: 120 },
  "Garden": { low: 10, high: 50 },
  "Events": { low: 40, high: 300 },
  "Electronics": { low: 20, high: 150 },
  "Sports & Recreation": { low: 25, high: 100 },
  "Appliances": { low: 30, high: 80 },
  "Clothing & Fashion": { low: 5, high: 30 },
  "Costumes & Cosplay": { low: 8, high: 40 },
  "Furniture": { low: 20, high: 100 },
  "Camera & Film": { low: 35, high: 150 },
  "Musical Instruments": { low: 20, high: 100 },
  "Party & Entertainment": { low: 40, high: 300 },
  "Baby & Kids": { low: 10, high: 50 },
  "Wedding": { low: 50, high: 500 },
  "Camping & Hiking": { low: 15, high: 80 },
  "Fitness Equipment": { low: 10, high: 60 },
  "Drones": { low: 40, high: 150 },
  "Trailers": { low: 60, high: 180 },
  "Storage & Moving": { low: 50, high: 200 },
  "Inflatables": { low: 20, high: 100 },
  "Luxury & Designer": { low: 100, high: 500 },
  "Seasonal & Holiday": { low: 25, high: 150 },
  "Farm Equipment": { low: 100, high: 400 },
  "Medical Equipment": { low: 50, high: 300 },
  "AV & Sound": { low: 50, high: 300 },
  "Office Equipment": { low: 30, high: 150 },
};

// ─── OWNER EARNINGS DASHBOARD ───────────────────────────────────────────────────
function OwnerEarningsDashboard({ myListings, myBookings, user }) {
  const [earnings, setEarnings] = useState({ total: 0, thisMonth: 0, pending: 0 });
  const [monthlyData, setMonthlyData] = useState([]);

  useEffect(() => {
    if (!myBookings || myBookings.length === 0) return;
    // Calculate completed bookings (those with 'confirmed' status)
    const completed = myBookings.filter(b => b.status === "confirmed" || b.status === "completed");
    const total = completed.reduce((sum, b) => sum + (b.subtotal || 0), 0);

    // This month's earnings
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonth = completed
      .filter(b => new Date(b.created_at) >= thisMonthStart)
      .reduce((sum, b) => sum + (b.subtotal || 0), 0);

    // Pending (non-confirmed bookings)
    const pending = myBookings
      .filter(b => b.status === "pending")
      .reduce((sum, b) => sum + (b.subtotal || 0), 0);

    setEarnings({ total, thisMonth, pending });

    // Last 6 months data for chart
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
      const monthEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const monthTotal = completed
        .filter(b => {
          const bd = new Date(b.created_at);
          return bd >= monthStart && bd <= monthEnd;
        })
        .reduce((sum, b) => sum + (b.subtotal || 0), 0);
      months.push({ month: d.toLocaleString('default', { month: 'short' }), amount: monthTotal });
    }
    setMonthlyData(months);
  }, [myBookings]);

  const maxAmount = Math.max(...monthlyData.map(m => m.amount), 1);
  const nextPayoutDate = new Date();
  nextPayoutDate.setDate(nextPayoutDate.getDate() + (7 - nextPayoutDate.getDay()));

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 16, fontWeight: "bold", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <span>💰</span> Earnings Dashboard
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, marginBottom: 16 }}>
        <div style={{ background: COLORS.surface, borderRadius: 10, padding: 12, border: `1px solid ${COLORS.border}` }}>
          <div style={{ color: COLORS.muted, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Total Earnings</div>
          <div style={{ fontSize: 20, fontWeight: "bold", color: COLORS.green }}>${earnings.total}</div>
        </div>
        <div style={{ background: COLORS.surface, borderRadius: 10, padding: 12, border: `1px solid ${COLORS.border}` }}>
          <div style={{ color: COLORS.muted, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>This Month</div>
          <div style={{ fontSize: 20, fontWeight: "bold", color: COLORS.accent }}>${earnings.thisMonth}</div>
        </div>
        <div style={{ background: COLORS.surface, borderRadius: 10, padding: 12, border: `1px solid ${COLORS.border}` }}>
          <div style={{ color: COLORS.muted, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Pending</div>
          <div style={{ fontSize: 20, fontWeight: "bold", color: COLORS.blue }}>${earnings.pending}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 12, marginBottom: 16 }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: "bold" }}>{myListings.length}</div>
          <div style={{ color: COLORS.muted, fontSize: 11 }}>Active Listings</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: "bold" }}>{myBookings.length}</div>
          <div style={{ color: COLORS.muted, fontSize: 11 }}>Total Bookings</div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: "bold" }}>4.8</div>
          <div style={{ color: COLORS.muted, fontSize: 11 }}>Avg Rating</div>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: COLORS.text }}>Last 6 Months</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 80, background: COLORS.surface, borderRadius: 8, padding: 8 }}>
          {monthlyData.map((m, i) => (
            <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div style={{ width: "100%", background: COLORS.accent, borderRadius: 4, height: `${(m.amount / maxAmount) * 60 + 5}px`, minHeight: 5 }} />
              <div style={{ fontSize: 9, color: COLORS.muted }}>{m.month}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: COLORS.surface, borderRadius: 8, padding: 10, fontSize: 12, color: COLORS.muted }}>
        📅 <strong style={{ color: COLORS.text }}>Next Payout:</strong> {nextPayoutDate.toLocaleDateString()}
      </div>
    </div>
  );
}

// ─── PRICING SUGGESTION HELPER ─────────────────────────────────────────────────
function PricingSuggestion({ category, price }) {
  const range = CATEGORY_PRICING[category];
  if (!range || !price) return null;

  const priceNum = parseInt(price);
  let color = COLORS.green;
  let status = "In range!";

  if (priceNum < range.low) {
    color = COLORS.red;
    status = "Below range";
  } else if (priceNum > range.high) {
    color = COLORS.red;
    status = "Above range";
  } else if (priceNum < range.low + 10 || priceNum > range.high - 10) {
    color = COLORS.accent;
    status = "Slightly outside";
  }

  return (
    <div style={{ background: color + "22", border: `1px solid ${color}44`, borderRadius: 8, padding: 10, marginTop: 8, fontSize: 12, color: color }}>
      💡 Similar items rent for ${range.low} - ${range.high}/day — {status}
    </div>
  );
}

// ─── REFERRAL SECTION ────────────────────────────────────────────────────────
function ReferralSection({ userId }) {
  const [stats, setStats] = useState({ referrals: 0, credits: 0 });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(`rtrt_referral_${userId}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      setStats(parsed);
    }
  }, [userId]);

  const referralLink = `https://rentthisrentthat.com?ref=${userId}`;

  const copyLink = () => {
    try {
      navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch(e) {
      alert("Could not copy link");
    }
  };

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 16 }}>
      <div style={{ fontSize: 16, fontWeight: "bold", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
        <span>🎁</span> Referral Program
      </div>

      <div style={{ background: COLORS.surface, borderRadius: 10, padding: 12, marginBottom: 12, border: `1px solid ${COLORS.border}` }}>
        <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 600, marginBottom: 6 }}>Your Referral Link</div>
        <div style={{ fontSize: 12, fontFamily: "monospace", color: COLORS.accent, wordBreak: "break-all", marginBottom: 8 }}>{referralLink}</div>
        <button onClick={copyLink} style={{ ...S.btn("primary", "sm"), width: "100%" }}>
          {copied ? "✅ Copied!" : "📋 Copy Link"}
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ background: COLORS.surface, borderRadius: 10, padding: 12, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: "bold", color: COLORS.accent }}>{stats.referrals}</div>
          <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 4 }}>People Referred</div>
        </div>
        <div style={{ background: COLORS.surface, borderRadius: 10, padding: 12, border: `1px solid ${COLORS.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: "bold", color: COLORS.green }}>${stats.credits}</div>
          <div style={{ color: COLORS.muted, fontSize: 11, marginTop: 4 }}>Referral Credits</div>
        </div>
      </div>
    </div>
  );
}

// ─── DAMAGE CLAIM MODAL (Owner files a claim) ──────────────────────────────────
const CLAIM_TYPES = [
  { id:"damage",        label:"Item Damage",      icon:"🔨", desc:"Physical damage to the item during the rental period" },
  { id:"missing_items", label:"Missing Items",     icon:"📦", desc:"Accessories, parts, or components not returned" },
  { id:"late_return",   label:"Late Return",       icon:"⏰", desc:"Item was returned after the agreed end date" },
  { id:"cleaning",      label:"Excessive Cleaning",icon:"🧹", desc:"Item returned in a condition requiring professional cleaning" },
  { id:"other",         label:"Other",             icon:"📋", desc:"Any other issue not covered above" },
];
const CLAIM_STATUS_COLORS = {
  pending:      { bg:COLORS.accent+"22", color:COLORS.accent, label:"Pending" },
  under_review: { bg:COLORS.blue+"22",   color:COLORS.blue,   label:"Under Review" },
  approved:     { bg:COLORS.green+"22",  color:COLORS.green,  label:"Approved" },
  denied:       { bg:COLORS.red+"22",    color:COLORS.red,    label:"Denied" },
  resolved:     { bg:COLORS.green+"22",  color:COLORS.green,  label:"Resolved" },
  withdrawn:    { bg:COLORS.muted+"22",  color:COLORS.muted,  label:"Withdrawn" },
};

function DamageClaimModal({ booking, user, listing, onClose, onSaved }) {
  const [step, setStep] = useState("type");       // type → details → review → submitted
  const [claimType, setClaimType] = useState(null);
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [photos, setPhotos] = useState([]);        // { file, preview } objects
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const securityDeposit = booking.security_deposit || 0;
  const maxClaim = Math.max(securityDeposit, booking.total_amount || 0);
  const claimInfo = CLAIM_TYPES.find(t => t.id === claimType);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.slice(0, 5 - photos.length).map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      const next = [...prev];
      URL.revokeObjectURL(next[idx].preview);
      next.splice(idx, 1);
      return next;
    });
  };

  const submitClaim = async () => {
    setSaving(true);
    setError(null);
    try {
      // Upload photos first
      const urls = [];
      for (const p of photos) {
        const url = await uploadClaimPhoto(p.file, user.id);
        urls.push(url);
      }
      setUploadedUrls(urls);

      // Determine respondent (the renter)
      const respondentId = booking.renter_id;

      await createDamageClaim({
        bookingId:       booking.id,
        listingId:       listing?.id || booking.listing_id,
        claimantId:      user.id,
        respondentId:    respondentId,
        claimType:       claimType,
        description:     description,
        photoUrls:       urls,
        amountRequested: parseFloat(amount),
      });

      setStep("submitted");
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message || "Failed to submit claim");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mbox, maxWidth:560 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <div style={{ fontWeight:"bold", fontSize:17 }}>🛡️ File a Damage Claim</div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>

        {/* Step indicator */}
        <div style={{ display:"flex", gap:4, marginBottom:20, fontSize:11, color:COLORS.muted }}>
          {[["Type",step==="type"],["Details",step==="details"],["Review",step==="review"],["Done",step==="submitted"]].map(([label, active], i) => (
            <span key={label} style={{ color: active ? COLORS.accent : COLORS.muted, fontWeight: active ? 700 : 400 }}>
              {active ? "▶" : i < ["type","details","review","submitted"].indexOf(step) ? "✅" : "○"} {label}{i < 3 ? " › " : ""}
            </span>
          ))}
        </div>

        {/* Booking summary bar */}
        <div style={{ background:COLORS.surface, borderRadius:10, padding:12, marginBottom:18, display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:12 }}>
          <div>
            <div style={{ fontWeight:700, color:COLORS.text }}>{listing?.title || booking.listings?.title || "Rental Item"}</div>
            <div style={{ color:COLORS.muted, marginTop:2 }}>{booking.start_date} → {booking.end_date}</div>
          </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ color:COLORS.muted }}>Security Deposit</div>
            <div style={{ fontWeight:700, color:COLORS.blue }}>${securityDeposit}</div>
          </div>
        </div>

        {/* STEP: Select claim type */}
        {step === "type" && <>
          <div style={{ fontSize:13, color:COLORS.muted, marginBottom:14 }}>What type of issue occurred?</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:18 }}>
            {CLAIM_TYPES.map(ct => (
              <div key={ct.id} style={{ ...S.dlvBtn(claimType === ct.id), display:"flex", alignItems:"center", gap:12, textAlign:"left", padding:14 }} onClick={() => setClaimType(ct.id)}>
                <div style={{ fontSize:24, flexShrink:0 }}>{ct.icon}</div>
                <div>
                  <div style={{ fontWeight:700, fontSize:13, color: claimType === ct.id ? COLORS.accent : COLORS.text }}>{ct.label}</div>
                  <div style={{ color:COLORS.muted, fontSize:11, marginTop:2 }}>{ct.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => setStep("details")} disabled={!claimType} style={{ ...S.btn("primary","lg"), width:"100%", opacity: claimType ? 1 : 0.4 }}>Continue →</button>
        </>}

        {/* STEP: Details */}
        {step === "details" && <>
          <div style={S.fg}>
            <label style={S.label}>Describe the issue <span style={{ color:COLORS.red }}>*</span></label>
            <textarea
              style={{ ...S.input, height:100, resize:"vertical", fontFamily:"inherit" }}
              placeholder="Please describe what happened in detail. Include when you discovered the issue and the condition of the item before and after the rental."
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          <div style={S.fg}>
            <label style={S.label}>Amount Requested ($) <span style={{ color:COLORS.red }}>*</span></label>
            <input
              type="number"
              style={S.input}
              placeholder={`Up to $${maxClaim} (security deposit: $${securityDeposit})`}
              value={amount}
              onChange={e => setAmount(e.target.value)}
              min="1"
              max={maxClaim}
            />
            {parseFloat(amount) > securityDeposit && securityDeposit > 0 && (
              <div style={{ fontSize:11, color:COLORS.accent, marginTop:6 }}>
                ⚠️ Amount exceeds the ${securityDeposit} security deposit. Claims above the deposit amount require admin review.
              </div>
            )}
          </div>

          <div style={S.fg}>
            <label style={S.label}>Photo Evidence (up to 5 photos)</label>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:8 }}>
              {photos.map((p, i) => (
                <div key={i} style={{ position:"relative", width:80, height:80, borderRadius:8, overflow:"hidden", border:`1px solid ${COLORS.border}` }}>
                  <img src={p.preview} alt={`Evidence ${i+1}`} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  <button onClick={() => removePhoto(i)} style={{ position:"absolute", top:2, right:2, background:"rgba(0,0,0,0.7)", color:"#fff", border:"none", borderRadius:"50%", width:20, height:20, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                </div>
              ))}
              {photos.length < 5 && (
                <div onClick={() => fileRef.current?.click()} style={{ width:80, height:80, borderRadius:8, border:`2px dashed ${COLORS.border}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:24, color:COLORS.muted, transition:"border-color 0.2s" }}>
                  +
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={handleFileSelect} />
            <div style={{ fontSize:11, color:COLORS.muted }}>Photos of the damage help resolve claims faster. Before and after photos are ideal.</div>
          </div>

          <div style={S.row}>
            <button onClick={() => setStep("type")} style={{ ...S.btn("outline"), flex:1 }}>← Back</button>
            <button onClick={() => setStep("review")} disabled={!description.trim() || !amount || parseFloat(amount) <= 0} style={{ ...S.btn("primary"), flex:2, opacity: description.trim() && amount && parseFloat(amount) > 0 ? 1 : 0.4 }}>Review Claim →</button>
          </div>
        </>}

        {/* STEP: Review */}
        {step === "review" && <>
          <div style={{ background:COLORS.surface, borderRadius:10, padding:16, marginBottom:18 }}>
            <div style={{ fontWeight:700, marginBottom:12, fontSize:14 }}>Claim Summary</div>
            {[
              ["Claim Type", `${claimInfo?.icon} ${claimInfo?.label}`],
              ["Amount Requested", `$${parseFloat(amount).toFixed(2)}`],
              ["Evidence Photos", `${photos.length} photo${photos.length !== 1 ? "s" : ""} attached`],
            ].map(([k, v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:8 }}>
                <span style={{ color:COLORS.muted }}>{k}</span>
                <span style={{ color:COLORS.text, fontWeight:600 }}>{v}</span>
              </div>
            ))}
            <div style={{ borderTop:`1px solid ${COLORS.border}`, paddingTop:10, marginTop:6 }}>
              <div style={{ fontSize:11, color:COLORS.muted, marginBottom:4 }}>Description</div>
              <div style={{ fontSize:12, color:COLORS.text, lineHeight:1.6 }}>{description}</div>
            </div>
          </div>

          <div style={{ background:COLORS.accent+"11", border:`1px solid ${COLORS.accent}44`, borderRadius:8, padding:12, marginBottom:18, fontSize:11, color:COLORS.muted, lineHeight:1.6 }}>
            <strong style={{ color:COLORS.accent }}>What happens next:</strong><br />
            1. The renter will be notified and has 72 hours to respond.<br />
            2. If they accept, the claim amount is deducted from their security deposit.<br />
            3. If they dispute, Rent This Rent That will review the evidence and make a decision within 5 business days.<br />
            4. You can withdraw this claim at any time before resolution.
          </div>

          {error && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:10, marginBottom:12, fontSize:12, color:COLORS.red }}>❌ {error}</div>}

          <div style={S.row}>
            <button onClick={() => setStep("details")} style={{ ...S.btn("outline"), flex:1 }}>← Back</button>
            <button onClick={submitClaim} disabled={saving} style={{ ...S.btn("red"), flex:2 }}>{saving ? "⏳ Submitting..." : "🛡️ Submit Claim"}</button>
          </div>
        </>}

        {/* STEP: Submitted */}
        {step === "submitted" && (
          <div style={{ textAlign:"center", padding:"10px 0" }}>
            <div style={{ fontSize:50, marginBottom:10 }}>✅</div>
            <div style={{ fontSize:19, fontWeight:"bold", marginBottom:6 }}>Claim Submitted</div>
            <div style={{ color:COLORS.muted, fontSize:13, lineHeight:1.6, marginBottom:18 }}>
              Your {claimInfo?.label?.toLowerCase()} claim for <strong style={{ color:COLORS.accent }}>${parseFloat(amount).toFixed(2)}</strong> has been filed.
              The renter has 72 hours to respond.
            </div>
            <div style={{ background:COLORS.surface, borderRadius:10, padding:14, marginBottom:18, textAlign:"left" }}>
              {[
                ["Claim Type", `${claimInfo?.icon} ${claimInfo?.label}`],
                ["Amount", `$${parseFloat(amount).toFixed(2)}`],
                ["Status", "⏳ Pending renter response"],
                ["Evidence", `${photos.length} photo${photos.length !== 1 ? "s" : ""}`],
                ["Deadline", "Renter has 72 hours to respond"],
              ].map(([k, v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:8 }}>
                  <span style={{ color:COLORS.muted }}>{k}</span>
                  <span style={{ color:COLORS.text }}>{v}</span>
                </div>
              ))}
            </div>
            <button onClick={onClose} style={{ ...S.btn("primary","lg"), width:"100%" }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DISPUTE RESPONSE MODAL (Renter responds to a claim) ─────────────────────
function DisputeResponseModal({ claim, user, onClose, onSaved }) {
  const [mode, setMode] = useState(null);        // null → "accept" or "dispute"
  const [responseText, setResponseText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const claimInfo = CLAIM_TYPES.find(t => t.id === claim.claim_type);
  const statusInfo = CLAIM_STATUS_COLORS[claim.status] || CLAIM_STATUS_COLORS.pending;

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.slice(0, 5 - photos.length).map(file => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      // Upload dispute evidence photos
      const urls = [];
      for (const p of photos) {
        const url = await uploadClaimPhoto(p.file, user.id);
        urls.push(url);
      }

      await respondToClaim(claim.id, {
        responseText: responseText,
        responsePhotoUrls: urls,
        accept: mode === "accept",
      });
      setDone(true);
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message || "Failed to submit response");
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div style={S.modal} onClick={onClose}>
        <div style={{ ...S.mbox, maxWidth:480 }} onClick={e => e.stopPropagation()}>
          <div style={{ textAlign:"center", padding:"10px 0" }}>
            <div style={{ fontSize:50, marginBottom:10 }}>{mode === "accept" ? "✅" : "📋"}</div>
            <div style={{ fontSize:19, fontWeight:"bold", marginBottom:6 }}>
              {mode === "accept" ? "Claim Accepted" : "Dispute Submitted"}
            </div>
            <div style={{ color:COLORS.muted, fontSize:13, lineHeight:1.6, marginBottom:18 }}>
              {mode === "accept"
                ? `You've accepted the claim. $${claim.amount_requested} will be deducted from your security deposit.`
                : "Your dispute has been submitted for review. Rent This Rent That will review both sides and make a decision within 5 business days."
              }
            </div>
            <button onClick={onClose} style={{ ...S.btn("primary","lg"), width:"100%" }}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mbox, maxWidth:560 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <div style={{ fontWeight:"bold", fontSize:17 }}>🛡️ Respond to Claim</div>
          <button onClick={onClose} style={S.btn("outline","sm")}>✕</button>
        </div>

        {/* Claim summary */}
        <div style={{ background:COLORS.surface, borderRadius:10, padding:14, marginBottom:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <div style={{ fontWeight:700, fontSize:14 }}>{claimInfo?.icon} {claimInfo?.label}</div>
            <span style={{ ...S.badge(statusInfo.color), fontSize:11 }}>{statusInfo.label}</span>
          </div>
          {[
            ["Filed by", claim.claimant?.full_name || "Owner"],
            ["Amount Requested", `$${claim.amount_requested}`],
            ["Filed on", new Date(claim.created_at).toLocaleDateString()],
          ].map(([k, v]) => (
            <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:6 }}>
              <span style={{ color:COLORS.muted }}>{k}</span>
              <span style={{ color:COLORS.text, fontWeight:600 }}>{v}</span>
            </div>
          ))}
          <div style={{ borderTop:`1px solid ${COLORS.border}`, paddingTop:10, marginTop:8 }}>
            <div style={{ fontSize:11, color:COLORS.muted, marginBottom:4 }}>Owner's description</div>
            <div style={{ fontSize:12, color:COLORS.text, lineHeight:1.6 }}>{claim.description}</div>
          </div>
          {claim.photo_urls && claim.photo_urls.length > 0 && (
            <div style={{ marginTop:10 }}>
              <div style={{ fontSize:11, color:COLORS.muted, marginBottom:6 }}>Evidence photos</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {claim.photo_urls.map((url, i) => (
                  <img key={i} src={url} alt={`Evidence ${i+1}`} style={{ width:70, height:70, borderRadius:6, objectFit:"cover", border:`1px solid ${COLORS.border}` }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Choose: Accept or Dispute */}
        {!mode && (
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ background:COLORS.green+"11", border:`1px solid ${COLORS.green}44`, borderRadius:10, padding:16, cursor:"pointer", transition:"all 0.2s" }} onClick={() => setMode("accept")}>
              <div style={{ fontWeight:700, fontSize:14, color:COLORS.green, marginBottom:4 }}>✅ Accept Claim</div>
              <div style={{ fontSize:12, color:COLORS.muted, lineHeight:1.6 }}>You agree with the claim. ${claim.amount_requested} will be deducted from your security deposit. This resolves the matter immediately.</div>
            </div>
            <div style={{ background:COLORS.red+"11", border:`1px solid ${COLORS.red}44`, borderRadius:10, padding:16, cursor:"pointer", transition:"all 0.2s" }} onClick={() => setMode("dispute")}>
              <div style={{ fontWeight:700, fontSize:14, color:COLORS.red, marginBottom:4 }}>⚖️ Dispute Claim</div>
              <div style={{ fontSize:12, color:COLORS.muted, lineHeight:1.6 }}>You disagree with the claim. Provide your side of the story and any counter-evidence. Rent This Rent That will review and decide within 5 business days.</div>
            </div>
          </div>
        )}

        {/* Accept confirmation */}
        {mode === "accept" && (
          <>
            <div style={{ background:COLORS.accent+"11", border:`1px solid ${COLORS.accent}44`, borderRadius:8, padding:12, marginBottom:16, fontSize:12, color:COLORS.muted, lineHeight:1.6 }}>
              By accepting, <strong style={{ color:COLORS.accent }}>${claim.amount_requested}</strong> will be deducted from your security deposit of ${claim.bookings?.security_deposit || "—"}. This action cannot be undone.
            </div>
            <div style={S.fg}>
              <label style={S.label}>Optional note</label>
              <textarea style={{ ...S.input, height:60, resize:"vertical", fontFamily:"inherit" }} placeholder="Any comments (optional)" value={responseText} onChange={e => setResponseText(e.target.value)} />
            </div>
            {error && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:10, marginBottom:12, fontSize:12, color:COLORS.red }}>❌ {error}</div>}
            <div style={S.row}>
              <button onClick={() => setMode(null)} style={{ ...S.btn("outline"), flex:1 }}>← Back</button>
              <button onClick={handleSubmit} disabled={saving} style={{ ...S.btn("green"), flex:2 }}>{saving ? "⏳ Processing..." : "✅ Accept & Pay $"+claim.amount_requested}</button>
            </div>
          </>
        )}

        {/* Dispute form */}
        {mode === "dispute" && (
          <>
            <div style={S.fg}>
              <label style={S.label}>Your response <span style={{ color:COLORS.red }}>*</span></label>
              <textarea
                style={{ ...S.input, height:100, resize:"vertical", fontFamily:"inherit" }}
                placeholder="Explain your side. Include details about the condition of the item when you received and returned it."
                value={responseText}
                onChange={e => setResponseText(e.target.value)}
              />
            </div>
            <div style={S.fg}>
              <label style={S.label}>Counter-evidence photos (optional, up to 5)</label>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:6 }}>
                {photos.map((p, i) => (
                  <div key={i} style={{ position:"relative", width:70, height:70, borderRadius:6, overflow:"hidden", border:`1px solid ${COLORS.border}` }}>
                    <img src={p.preview} alt={`Counter ${i+1}`} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    <button onClick={() => { const next = [...photos]; URL.revokeObjectURL(next[i].preview); next.splice(i,1); setPhotos(next); }} style={{ position:"absolute", top:1, right:1, background:"rgba(0,0,0,0.7)", color:"#fff", border:"none", borderRadius:"50%", width:18, height:18, fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                  </div>
                ))}
                {photos.length < 5 && (
                  <div onClick={() => fileRef.current?.click()} style={{ width:70, height:70, borderRadius:6, border:`2px dashed ${COLORS.border}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:20, color:COLORS.muted }}>+</div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:"none" }} onChange={handleFileSelect} />
            </div>
            {error && <div style={{ background:COLORS.red+"22", border:`1px solid ${COLORS.red}44`, borderRadius:8, padding:10, marginBottom:12, fontSize:12, color:COLORS.red }}>❌ {error}</div>}
            <div style={S.row}>
              <button onClick={() => setMode(null)} style={{ ...S.btn("outline"), flex:1 }}>← Back</button>
              <button onClick={handleSubmit} disabled={saving || !responseText.trim()} style={{ ...S.btn("red"), flex:2, opacity: responseText.trim() ? 1 : 0.4 }}>{saving ? "⏳ Submitting..." : "⚖️ Submit Dispute"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── CLAIM STATUS BADGE (reusable) ──────────────────────────────────────────
function ClaimStatusBadge({ status }) {
  const info = CLAIM_STATUS_COLORS[status] || CLAIM_STATUS_COLORS.pending;
  return <span style={{ display:"inline-block", padding:"3px 8px", borderRadius:20, fontSize:10, fontWeight:700, background:info.bg, color:info.color, border:`1px solid ${info.color}44` }}>{info.label}</span>;
}

// ─── ZIP RADIUS DISTANCE UTILITY ──────────────────────────────────────────────
// Haversine formula for distance between two lat/lng points in miles
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 3959; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Lightweight ZIP-to-lat/lng lookup — uses a free API with in-memory cache
const ZIP_CACHE = {};
async function getZipCoords(zip) {
  if (!zip || zip.length !== 5) return null;
  if (ZIP_CACHE[zip]) return ZIP_CACHE[zip];
  try {
    const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!res.ok) return null;
    const data = await res.json();
    const place = data.places?.[0];
    if (!place) return null;
    const coords = { lat: parseFloat(place.latitude), lng: parseFloat(place.longitude), city: place['place name'], state: place['state abbreviation'] };
    ZIP_CACHE[zip] = coords;
    return coords;
  } catch { return null; }
}

// Filter listings by radius from a ZIP code
async function filterByRadius(listings, zip, radiusMiles) {
  if (!zip || zip.length !== 5 || radiusMiles === "all") return listings;
  const origin = await getZipCoords(zip);
  if (!origin) return listings;
  const r = parseInt(radiusMiles) || 25;
  const results = [];
  for (const listing of listings) {
    if (!listing.zip_code || listing.zip_code.length !== 5) {
      results.push({ ...listing, _distance: null }); // no ZIP = include but no distance
      continue;
    }
    const dest = await getZipCoords(listing.zip_code);
    if (!dest) { results.push({ ...listing, _distance: null }); continue; }
    const dist = haversineDistance(origin.lat, origin.lng, dest.lat, dest.lng);
    if (dist <= r) results.push({ ...listing, _distance: Math.round(dist) });
  }
  results.sort((a, b) => (a._distance ?? 9999) - (b._distance ?? 9999));
  return results;
}

// ─── NOTIFICATION CENTER ──────────────────────────────────────────────────────
const NOTIF_ICONS = {
  booking: "📦", message: "💬", claim: "🛡️", review: "⭐",
  listing: "📋", system: "🔔", promo: "🎉", welcome: "👋",
};

function NotificationCenter({ user, notifications, unreadCount, onClose, onMarkRead, onMarkAllRead, onNavigate }) {
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mbox, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:18, fontWeight:"bold" }}>🔔 Notifications</div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {unreadCount > 0 && (
              <button onClick={onMarkAllRead} style={{ background:"none", border:"none", color:COLORS.accent, fontSize:12, cursor:"pointer", fontWeight:600 }}>Mark all read</button>
            )}
            <button onClick={onClose} style={{ background:"none", border:"none", color:COLORS.muted, fontSize:20, cursor:"pointer" }}>✕</button>
          </div>
        </div>

        {notifications.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:COLORS.muted }}>
            <div style={{ fontSize:36, marginBottom:8 }}>🔕</div>
            <div>No notifications yet</div>
          </div>
        ) : (
          <div style={{ maxHeight:400, overflowY:"auto" }}>
            {notifications.map(n => (
              <div key={n.id} onClick={() => { if (!n.read) onMarkRead(n.id); if (n.link_type) onNavigate(n.link_type, n.link_id); }}
                style={{ display:"flex", gap:10, padding:"12px 8px", borderBottom:`1px solid ${COLORS.border}`, cursor: n.link_type ? "pointer" : "default", background: n.read ? "transparent" : COLORS.accent+"08", borderRadius:8, transition:"background 0.2s" }}>
                <div style={{ fontSize:22, flexShrink:0, marginTop:2 }}>{NOTIF_ICONS[n.type] || "🔔"}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight: n.read ? 400 : 700, color: n.read ? COLORS.muted : COLORS.text, marginBottom:2 }}>{n.title}</div>
                  {n.body && <div style={{ fontSize:12, color:COLORS.muted, lineHeight:1.4 }}>{n.body}</div>}
                  <div style={{ fontSize:11, color:COLORS.muted, marginTop:4 }}>{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <div style={{ width:8, height:8, borderRadius:"50%", background:COLORS.accent, flexShrink:0, marginTop:6 }} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ADMIN PANEL ──────────────────────────────────────────────────────────────
function AdminPanel({ user, onClose }) {
  const [tab, setTab] = useState("overview"); // overview, users, listings, bookings, claims
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimFilter, setClaimFilter] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, u, b, c] = await Promise.all([
          getAdminStats(),
          getAdminUsers({ limit: 100 }),
          getAdminBookings({ limit: 100 }),
          getAdminClaims({ limit: 100 }),
        ]);
        setStats(s);
        setUsers(u);
        setBookings(b);
        setClaims(c);
        // Fetch listings separately
        const { data: lData } = await supabase.from('listings').select('*, profiles:owner_id(full_name, email)').order('created_at', { ascending: false }).limit(100);
        setAllListings(lData || []);
      } catch (e) { console.error("Admin data error:", e); }
      setLoading(false);
    })();
  }, []);

  const handleResolveClaim = async (claimId, action) => {
    try {
      const claim = claims.find(c => c.id === claimId);
      if (!claim) return;
      if (action === "approve") {
        await updateClaimStatus(claimId, { status: "approved", amountApproved: claim.amount_requested, resolvedBy: "admin", resolutionNotes: "Approved by admin" });
      } else {
        await updateClaimStatus(claimId, { status: "denied", resolvedBy: "admin", resolutionNotes: "Denied by admin" });
      }
      // Refresh claims
      const updatedClaims = await getAdminClaims({ limit: 100 });
      setClaims(updatedClaims);
    } catch (e) { console.error("Resolve claim error:", e); }
  };

  const StatCard = ({ icon, label, value, color }) => (
    <div style={{ background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:16, textAlign:"center", flex:"1 1 120px" }}>
      <div style={{ fontSize:28, marginBottom:4 }}>{icon}</div>
      <div style={{ fontSize:22, fontWeight:800, color: color || COLORS.accent }}>{value}</div>
      <div style={{ fontSize:11, color:COLORS.muted, textTransform:"uppercase", letterSpacing:1, marginTop:2 }}>{label}</div>
    </div>
  );

  const adminTabs = [
    { id:"overview", label:"📊 Overview" },
    { id:"users", label:"👥 Users" },
    { id:"listings", label:"📋 Listings" },
    { id:"bookings", label:"📦 Bookings" },
    { id:"claims", label:"🛡️ Claims" },
  ];

  const filteredClaims = claimFilter ? claims.filter(c => c.status === claimFilter) : claims;

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mbox, maxWidth:900, maxHeight:"90vh" }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <div style={{ fontSize:20, fontWeight:"bold" }}>🔧 Admin Panel</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:COLORS.muted, fontSize:22, cursor:"pointer" }}>✕</button>
        </div>

        {/* Admin tab bar */}
        <div style={{ display:"flex", gap:4, marginBottom:20, flexWrap:"wrap" }}>
          {adminTabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ padding:"8px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:13, fontWeight:600, background: tab === t.id ? COLORS.accent : COLORS.surface, color: tab === t.id ? "#000" : COLORS.muted, transition:"all 0.2s" }}>{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:COLORS.muted }}>Loading admin data...</div>
        ) : (
          <>
            {/* ── OVERVIEW TAB ── */}
            {tab === "overview" && stats && (
              <div>
                <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:24 }}>
                  <StatCard icon="👥" label="Users" value={stats.totalUsers} color={COLORS.blue} />
                  <StatCard icon="📋" label="Listings" value={stats.totalListings} color={COLORS.green} />
                  <StatCard icon="📦" label="Bookings" value={stats.totalBookings} color={COLORS.purple} />
                  <StatCard icon="🛡️" label="Claims" value={stats.totalClaims} color={COLORS.red} />
                  <StatCard icon="💰" label="Revenue" value={`$${stats.totalRevenue.toLocaleString()}`} color={COLORS.accent} />
                </div>
                <div style={{ fontSize:14, fontWeight:700, marginBottom:10 }}>Recent Activity</div>
                <div style={{ fontSize:12, color:COLORS.muted, lineHeight:1.8 }}>
                  <div>• {bookings.filter(b=>b.status==="pending").length} pending bookings</div>
                  <div>• {bookings.filter(b=>b.status==="confirmed").length} confirmed bookings</div>
                  <div>• {claims.filter(c=>c.status==="pending"||c.status==="under_review").length} open claims</div>
                  <div>• {users.filter(u=>{ const d=new Date(u.created_at); const now=new Date(); return (now-d)<7*24*60*60*1000; }).length} new users this week</div>
                </div>
              </div>
            )}

            {/* ── USERS TAB ── */}
            {tab === "users" && (
              <div>
                <div style={{ fontSize:12, color:COLORS.muted, marginBottom:10 }}>{users.length} registered users</div>
                <div style={{ maxHeight:400, overflowY:"auto" }}>
                  {users.map(u => (
                    <div key={u.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:`1px solid ${COLORS.border}` }}>
                      <div style={{ width:36, height:36, borderRadius:"50%", background:COLORS.accent+"22", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>
                        {u.avatar_url ? <img src={u.avatar_url} alt="" style={{ width:36, height:36, borderRadius:"50%", objectFit:"cover" }} /> : "👤"}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600 }}>{u.full_name || "Unnamed"}</div>
                        <div style={{ fontSize:11, color:COLORS.muted }}>{u.email} • {u.city || u.zip_code || "No location"}</div>
                      </div>
                      <div style={{ fontSize:11, color:COLORS.muted, flexShrink:0 }}>
                        {new Date(u.created_at).toLocaleDateString()}
                      </div>
                      {u.is_admin && <span style={S.badge(COLORS.accent)}>Admin</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── LISTINGS TAB ── */}
            {tab === "listings" && (
              <div>
                <div style={{ fontSize:12, color:COLORS.muted, marginBottom:10 }}>{allListings.length} total listings</div>
                <div style={{ maxHeight:400, overflowY:"auto" }}>
                  {allListings.map(l => (
                    <div key={l.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 0", borderBottom:`1px solid ${COLORS.border}` }}>
                      <div style={{ width:48, height:36, borderRadius:6, overflow:"hidden", background:COLORS.surface, flexShrink:0 }}>
                        {parseImageUrls(l.image_url)[0] ? <img src={parseImageUrls(l.image_url)[0]} alt="" style={{ width:48, height:36, objectFit:"cover" }} /> : <div style={{ width:48, height:36, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{CAT_ICONS[l.category] || "📦"}</div>}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600 }}>{l.title}</div>
                        <div style={{ fontSize:11, color:COLORS.muted }}>{l.category} • ${l.price_per_day}/day • {l.profiles?.full_name || "Unknown owner"}</div>
                      </div>
                      <span style={S.badge(l.status === "active" ? COLORS.green : COLORS.muted)}>{l.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── BOOKINGS TAB ── */}
            {tab === "bookings" && (
              <div>
                <div style={{ fontSize:12, color:COLORS.muted, marginBottom:10 }}>{bookings.length} total bookings</div>
                <div style={{ maxHeight:400, overflowY:"auto" }}>
                  {bookings.map(b => {
                    const statusColor = b.status === "confirmed" ? COLORS.green : b.status === "pending" ? COLORS.accent : b.status === "completed" ? COLORS.blue : COLORS.muted;
                    return (
                      <div key={b.id} style={{ padding:"10px 0", borderBottom:`1px solid ${COLORS.border}` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                          <div style={{ fontSize:13, fontWeight:600 }}>{b.listings?.title || "Unknown listing"}</div>
                          <span style={S.badge(statusColor)}>{b.status}</span>
                        </div>
                        <div style={{ fontSize:11, color:COLORS.muted }}>
                          Renter: {b.profiles?.full_name || "Unknown"} • {b.start_date} → {b.end_date} • ${b.total_amount || 0}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── CLAIMS TAB ── */}
            {tab === "claims" && (
              <div>
                <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
                  {["","pending","under_review","approved","denied","resolved"].map(s => (
                    <button key={s} onClick={() => setClaimFilter(s)} style={{ padding:"5px 10px", borderRadius:20, border:`1px solid ${claimFilter===s?COLORS.accent:COLORS.border}`, background:claimFilter===s?COLORS.accent+"22":"transparent", color:claimFilter===s?COLORS.accent:COLORS.muted, cursor:"pointer", fontSize:11, fontWeight:600 }}>
                      {s || "All"} ({s ? claims.filter(c=>c.status===s).length : claims.length})
                    </button>
                  ))}
                </div>
                <div style={{ maxHeight:350, overflowY:"auto" }}>
                  {filteredClaims.map(c => (
                    <div key={c.id} style={{ padding:"12px 0", borderBottom:`1px solid ${COLORS.border}` }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                        <div style={{ fontSize:13, fontWeight:600 }}>{c.listings?.title || "Unknown listing"}</div>
                        <ClaimStatusBadge status={c.status} />
                      </div>
                      <div style={{ fontSize:12, color:COLORS.muted, marginBottom:4 }}>
                        {CLAIM_TYPES.find(t=>t.id===c.claim_type)?.icon} {CLAIM_TYPES.find(t=>t.id===c.claim_type)?.label} • Requested: ${c.amount_requested}
                      </div>
                      <div style={{ fontSize:11, color:COLORS.muted, marginBottom:6 }}>
                        By: {c.claimant?.full_name || "Unknown"} → {c.respondent?.full_name || "Unknown"} • {new Date(c.created_at).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize:12, color:COLORS.text, marginBottom:8, lineHeight:1.4 }}>{c.description}</div>
                      {(c.status === "pending" || c.status === "under_review") && (
                        <div style={{ display:"flex", gap:8 }}>
                          <button onClick={() => handleResolveClaim(c.id, "approve")} style={{ ...S.btn("green","sm") }}>✅ Approve ${c.amount_requested}</button>
                          <button onClick={() => handleResolveClaim(c.id, "deny")} style={{ ...S.btn("red","sm") }}>❌ Deny</button>
                        </div>
                      )}
                      {c.status === "approved" && c.amount_approved && (
                        <div style={{ fontSize:12, color:COLORS.green, fontWeight:600 }}>Approved: ${c.amount_approved}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── AI FAQ CHATBOT ───────────────────────────────────────────────────────────
function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ type: "bot", text: "Hi! 👋 Ask me about insurance, listing items, payments, delivery, licenses, or security deposits." }]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const FAQ = {
    insurance: "Our insurance (SecureRent Partners™) covers damage up to $2,500 for Standard Protection. You choose your coverage level during checkout. Theft is covered under Premium Protection ($10k coverage).",
    listing: "Click 'Create Listing' to add an item. Fill in title, category, daily/weekly price, ZIP code, and upload photos. Training or license requirements vary by category.",
    payment: "Funds are held in escrow by Rent This Rent That LLC during the rental. Your security deposit ($100-$300) is refunded within 48 hours of successful return.",
    delivery: "Choose Self Pickup & Return (free), Drop-Off & Pick-Up (+$25), or Full Service (+$49). Full Service includes setup and return assistance.",
    license: "Some items like Boats, Aircraft, Motorcycles, and Heavy Equipment require license verification. Upload your license in the checkout flow — it's verified instantly.",
    deposit: "The security deposit is typically 20% of the rental cost (minimum $25). It's held in escrow and fully refunded within 48 hours if the item is returned in good condition.",
    reviews: "Leave a review after each rental! Rate Communication, Quality, On-Time, and Value. Your reviews help build your reputation on Rent This Rent That.",
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMsg = input.toLowerCase();
    setMessages(prev => [...prev, { type: "user", text: input }]);
    setInput("");
    setIsTyping(true);

    // Simple keyword matching
    let response = "I'm not sure about that. Try asking about insurance, listing, payment, delivery, license, deposit, or reviews.";

    if (userMsg.includes("insur")) response = FAQ.insurance;
    else if (userMsg.includes("list")) response = FAQ.listing;
    else if (userMsg.includes("pay") || userMsg.includes("escrow")) response = FAQ.payment;
    else if (userMsg.includes("deliver") || userMsg.includes("pickup")) response = FAQ.delivery;
    else if (userMsg.includes("license") || userMsg.includes("driver") || userMsg.includes("pilot")) response = FAQ.license;
    else if (userMsg.includes("deposit")) response = FAQ.deposit;
    else if (userMsg.includes("review") || userMsg.includes("rating")) response = FAQ.reviews;
    else if (userMsg.includes("hi") || userMsg.includes("hello")) response = "Hello! How can I help you today? 😊";

    // Simulate typing
    setTimeout(() => {
      setMessages(prev => [...prev, { type: "bot", text: response }]);
      setIsTyping(false);
    }, 500);
  };

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)} style={{
        position: "fixed", bottom: 20, right: 20, width: 50, height: 50,
        borderRadius: "50%", background: COLORS.accent, border: "none", cursor: "pointer",
        fontSize: 24, display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)", zIndex: 998,
      }}>💬</button>
    );
  }

  return (
    <div style={{
      position: "fixed", bottom: 20, right: 20, width: 320, height: 500,
      background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 14,
      display: "flex", flexDirection: "column", zIndex: 998, boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
    }}>
      <div style={{ background: COLORS.surface, borderTopLeftRadius: 14, borderTopRightRadius: 14, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ fontSize: 14, fontWeight: "bold" }}>Rent This Rent That Help</div>
        <button onClick={() => setIsOpen(false)} style={{ background: "none", border: "none", color: COLORS.muted, fontSize: 18, cursor: "pointer" }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: "flex", justifyContent: msg.type === "bot" ? "flex-start" : "flex-end" }}>
            <div style={{
              maxWidth: "80%", padding: 10, borderRadius: 10, fontSize: 13,
              background: msg.type === "bot" ? COLORS.surface : COLORS.accent,
              color: msg.type === "bot" ? COLORS.text : "#000",
              wordBreak: "break-word",
            }}>
              {msg.text}
            </div>
          </div>
        ))}
        {isTyping && <div style={{ display: "flex", gap: 4, color: COLORS.muted }}><span>•</span><span>•</span><span>•</span></div>}
      </div>

      <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: 10, display: "flex", gap: 8 }}>
        <input
          type="text" value={input} onChange={e => setInput(e.target.value)} onKeyPress={e => e.key === "Enter" && handleSendMessage()}
          placeholder="Ask anything..." style={{ ...S.input, fontSize: 12, padding: "8px 10px" }}
        />
        <button onClick={handleSendMessage} style={{ ...S.btn("primary", "sm"), minWidth: 40 }}>→</button>
      </div>
    </div>
  );
}

// ─── NOTIFICATION PREFERENCES ───────────────────────────────────────────────────
function NotificationPreferences({ onClose }) {
  const [prefs, setPrefs] = useState(() => {
    const stored = localStorage.getItem("rtrt_notif_prefs");
    return stored ? JSON.parse(stored) : {
      messages: true, bookings: true, priceDrop: true, newListings: true, reviews: true, promos: true,
    };
  });

  const savePrefs = () => {
    localStorage.setItem("rtrt_notif_prefs", JSON.stringify(prefs));
    onClose();
  };

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ ...S.mbox, maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: "bold" }}>🔔 Notification Preferences</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: COLORS.muted, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {[
          { key: "messages", label: "New Messages", icon: "💬" },
          { key: "bookings", label: "Booking Confirmations", icon: "✅" },
          { key: "priceDrop", label: "Price Drop Alerts", icon: "📉" },
          { key: "newListings", label: "New Listings in Your Area", icon: "📍" },
          { key: "reviews", label: "Review Reminders", icon: "⭐" },
          { key: "promos", label: "Promotional Offers", icon: "🎉" },
        ].map(({ key, label, icon }) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 18 }}>{icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
            </div>
            <input
              type="checkbox" checked={prefs[key]} onChange={e => setPrefs(p => ({ ...p, [key]: e.target.checked }))}
              style={{ width: 18, height: 18, cursor: "pointer" }}
            />
          </div>
        ))}

        <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ ...S.btn("outline"), flex: 1 }}>Cancel</button>
          <button onClick={savePrefs} style={{ ...S.btn("primary"), flex: 1 }}>Save Preferences</button>
        </div>
      </div>
    </div>
  );
}

// ─── LEGAL PAGE COMPONENT ─────────────────────────────────────────────────────
function LegalPage({ title, children, onBack }) {
  return (
    <div style={{ maxWidth:800, margin:"0 auto", padding:"40px 20px 60px" }}>
      <button onClick={onBack} style={{ background:"none", border:"none", color:COLORS.accent, cursor:"pointer", fontSize:14, marginBottom:20, padding:0 }}>← Back to Home</button>
      <h1 style={{ fontSize:28, fontWeight:700, color:COLORS.text, marginBottom:8 }}>{title}</h1>
      <div style={{ color:COLORS.muted, fontSize:13, marginBottom:30 }}>Last updated: May 28, 2026</div>
      <div style={{ color:COLORS.text, fontSize:14, lineHeight:1.8 }}>{children}</div>
    </div>
  );
}

function PrivacyPolicyPage({ onBack }) {
  const h2 = { fontSize:20, fontWeight:600, color:COLORS.text, marginTop:32, marginBottom:12 };
  const h3 = { fontSize:16, fontWeight:600, color:COLORS.text, marginTop:20, marginBottom:8 };
  const p = { color:COLORS.muted, marginBottom:12, lineHeight:1.8 };
  return (
    <LegalPage title="Privacy Policy" onBack={onBack}>
      <p style={p}>Rent This Rent That LLC ("RTRT", "we", "us", or "our") operates the website rentthisrentthat.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.</p>

      <h2 style={h2}>1. Information We Collect</h2>
      <h3 style={h3}>Personal Information</h3>
      <p style={p}>When you register an account, list an item, or make a booking, we may collect: your name, email address, phone number, profile photo, mailing address, payment information (processed securely via Stripe), and government-issued ID for identity verification purposes.</p>
      <h3 style={h3}>Usage Data</h3>
      <p style={p}>We automatically collect information about how you interact with our platform, including: IP address, browser type, pages visited, time spent on pages, and referring URLs. This data helps us improve the user experience.</p>
      <h3 style={h3}>Listing & Booking Data</h3>
      <p style={p}>We collect information you provide when creating listings (item descriptions, photos, pricing, location) and when making bookings (dates, messages between users, reviews, and ratings).</p>

      <h2 style={h2}>2. How We Use Your Information</h2>
      <p style={p}>We use your information to: facilitate rentals and bookings between users; process payments through Stripe; send transactional emails (booking confirmations, reminders, receipts) via Resend; verify user identity and prevent fraud; provide customer support; improve our platform and develop new features; send marketing communications (with your consent); and comply with legal obligations.</p>

      <h2 style={h2}>3. Information Sharing</h2>
      <p style={p}>We do not sell your personal information. We may share information with: other users as necessary to facilitate rentals (e.g., your name and contact info with a renter/owner); payment processors (Stripe) to complete transactions; email service providers (Resend) to deliver transactional messages; law enforcement when required by law or to protect our rights; and service providers who help us operate the platform.</p>

      <h2 style={h2}>4. Data Security</h2>
      <p style={p}>We implement industry-standard security measures to protect your data, including encrypted data transmission (HTTPS/TLS), secure authentication via Supabase, PCI-compliant payment processing through Stripe, and regular security reviews. However, no method of electronic transmission is 100% secure, and we cannot guarantee absolute security.</p>

      <h2 style={h2}>5. Your Rights</h2>
      <p style={p}>Depending on your location, you may have the right to: access the personal data we hold about you; request correction of inaccurate data; request deletion of your data; opt out of marketing communications; and export your data in a portable format. To exercise these rights, contact us at rentthisrentthat@proton.me.</p>

      <h2 style={h2}>6. Cookies</h2>
      <p style={p}>We use essential cookies for authentication and session management. We do not use third-party advertising cookies. You can disable cookies in your browser settings, but some features may not function properly.</p>

      <h2 style={h2}>7. Children's Privacy</h2>
      <p style={p}>Our platform is not intended for use by anyone under the age of 18. We do not knowingly collect personal information from minors. If we learn that we have collected information from a child under 18, we will delete it promptly.</p>

      <h2 style={h2}>8. Changes to This Policy</h2>
      <p style={p}>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the platform after changes constitutes acceptance of the updated policy.</p>

      <h2 style={h2}>9. Contact Us</h2>
      <p style={p}>If you have questions about this Privacy Policy, please contact us at:</p>
      <p style={p}>Rent This Rent That LLC<br/>Email: rentthisrentthat@proton.me<br/>Website: rentthisrentthat.com</p>
    </LegalPage>
  );
}

function TermsOfServicePage({ onBack }) {
  const h2 = { fontSize:20, fontWeight:600, color:COLORS.text, marginTop:32, marginBottom:12 };
  const p = { color:COLORS.muted, marginBottom:12, lineHeight:1.8 };
  return (
    <LegalPage title="Terms of Service" onBack={onBack}>
      <p style={p}>Welcome to Rent This Rent That ("RTRT", "we", "us", or "our"). By accessing or using our platform at rentthisrentthat.com, you agree to be bound by these Terms of Service. If you do not agree, please do not use our services.</p>

      <h2 style={h2}>1. Eligibility</h2>
      <p style={p}>You must be at least 18 years old and capable of forming a binding contract to use RTRT. By creating an account, you represent that you meet these requirements and that all registration information you provide is accurate and complete.</p>

      <h2 style={h2}>2. Platform Role</h2>
      <p style={p}>RTRT is a peer-to-peer marketplace that connects item owners ("Owners") with renters ("Renters"). We are not a party to any rental agreement between users. We do not own, manage, or control any items listed on the platform. We act solely as an intermediary to facilitate connections and transactions.</p>

      <h2 style={h2}>3. User Accounts</h2>
      <p style={p}>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use. We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activity.</p>

      <h2 style={h2}>4. Listings & Rentals</h2>
      <p style={p}>Owners are responsible for: providing accurate descriptions and photos of their items; setting fair pricing and availability; ensuring items are in safe, working condition; maintaining appropriate insurance on their items; and complying with all applicable local laws and regulations.</p>
      <p style={p}>Renters are responsible for: using rented items with reasonable care; returning items on time and in the same condition received (normal wear excepted); reporting any damage promptly; and complying with any usage instructions provided by the Owner.</p>

      <h2 style={h2}>5. Payments & Fees</h2>
      <p style={p}>All payments are processed securely through Stripe. RTRT may charge a service fee on each transaction. Owners receive payment after the rental period begins, minus any applicable fees. Refund and cancellation policies are outlined in individual listing terms and our cancellation policy.</p>

      <h2 style={h2}>6. Damage & Disputes</h2>
      <p style={p}>If an item is damaged during a rental, the Owner may file a damage claim through the platform. Renters will have the opportunity to respond. RTRT may mediate disputes but is not obligated to resolve them. Users agree to first attempt resolution through our platform before pursuing other remedies.</p>

      <h2 style={h2}>7. Prohibited Conduct</h2>
      <p style={p}>You may not: list stolen, illegal, or hazardous items; misrepresent item condition or identity; harass, threaten, or discriminate against other users; manipulate reviews or ratings; circumvent the payment system; use the platform for any unlawful purpose; or attempt to reverse-engineer or disrupt the platform.</p>

      <h2 style={h2}>8. Limitation of Liability</h2>
      <p style={p}>To the maximum extent permitted by law, RTRT shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the platform, including but not limited to: damage to or loss of rented items; personal injury; loss of revenue or data; or any dispute between users. Our total liability shall not exceed the fees paid by you to RTRT in the 12 months preceding the claim.</p>

      <h2 style={h2}>9. Indemnification</h2>
      <p style={p}>You agree to indemnify and hold harmless RTRT, its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the platform, your violation of these terms, or your violation of any third-party rights.</p>

      <h2 style={h2}>10. Intellectual Property</h2>
      <p style={p}>All content on the RTRT platform (logos, design, text, graphics, software) is owned by Rent This Rent That LLC and protected by intellectual property laws. Users retain ownership of content they upload but grant RTRT a non-exclusive, royalty-free license to use, display, and distribute such content on the platform.</p>

      <h2 style={h2}>11. Termination</h2>
      <p style={p}>We may suspend or terminate your access to the platform at any time, with or without cause, and with or without notice. Upon termination, your right to use the platform ceases immediately. Provisions that by their nature should survive termination will remain in effect.</p>

      <h2 style={h2}>12. Governing Law</h2>
      <p style={p}>These Terms shall be governed by and construed in accordance with the laws of the State in which Rent This Rent That LLC is incorporated, without regard to conflict of law principles.</p>

      <h2 style={h2}>13. Changes to Terms</h2>
      <p style={p}>We reserve the right to modify these Terms at any time. Material changes will be communicated via email or a notice on the platform. Continued use after changes constitutes acceptance.</p>

      <h2 style={h2}>14. Contact</h2>
      <p style={p}>For questions about these Terms, contact us at:<br/>Rent This Rent That LLC<br/>Email: rentthisrentthat@proton.me</p>
    </LegalPage>
  );
}

function BrokerAgreementPage({ onBack }) {
  const h2 = { fontSize:20, fontWeight:600, color:COLORS.text, marginTop:32, marginBottom:12 };
  const p = { color:COLORS.muted, marginBottom:12, lineHeight:1.8 };
  return (
    <LegalPage title="Broker Agreement" onBack={onBack}>
      <p style={p}>This Broker Agreement ("Agreement") is entered into between Rent This Rent That LLC ("RTRT", "Platform", "we") and each user ("User", "you") who lists or rents items through our platform. By using RTRT, you agree to the terms outlined below.</p>

      <h2 style={h2}>1. Role of RTRT</h2>
      <p style={p}>RTRT acts as a licensed broker and intermediary platform that facilitates peer-to-peer rental transactions. We connect Owners who wish to rent out their personal property with Renters who wish to temporarily use such property. RTRT does not take possession of, own, or control any listed items.</p>

      <h2 style={h2}>2. Brokerage Services</h2>
      <p style={p}>RTRT provides the following brokerage services: a marketplace platform for listing and discovering rental items; secure payment processing and escrow services via Stripe; messaging and communication tools between Owners and Renters; booking management and scheduling; damage claim and dispute mediation; review and rating systems; and identity verification services.</p>

      <h2 style={h2}>3. Service Fees</h2>
      <p style={p}>In exchange for brokerage services, RTRT charges a service fee on each completed transaction. The fee is calculated as a percentage of the total rental price and is disclosed to both parties before a booking is confirmed. Fees are non-refundable except as required by law or as outlined in our refund policy.</p>

      <h2 style={h2}>4. Owner Obligations</h2>
      <p style={p}>As an Owner using RTRT's brokerage services, you agree to: provide truthful and complete information about your items; maintain items in safe and functional condition; respond to booking requests in a timely manner; honor confirmed bookings unless cancellation is unavoidable; carry appropriate insurance for high-value items; comply with all local, state, and federal regulations regarding rental of personal property; and report all rental income as required by tax authorities.</p>

      <h2 style={h2}>5. Renter Obligations</h2>
      <p style={p}>As a Renter using RTRT's brokerage services, you agree to: use rented items responsibly and as intended; return items on time and in proper condition; promptly report any damage or issues; provide accurate identification and payment information; and not sublease or transfer rented items to third parties.</p>

      <h2 style={h2}>6. Payment Terms</h2>
      <p style={p}>All payments must be processed through the RTRT platform. Users shall not attempt to circumvent the payment system or transact outside the platform for rentals initiated through RTRT. Owners will receive disbursement of rental funds, minus applicable fees, according to our standard payment schedule.</p>

      <h2 style={h2}>7. Insurance & Liability</h2>
      <p style={p}>RTRT may offer optional protection plans for rentals. These plans supplement but do not replace the Owner's personal insurance. RTRT is not an insurance provider and makes no guarantees regarding coverage. Each party is responsible for maintaining their own appropriate insurance coverage.</p>

      <h2 style={h2}>8. Dispute Resolution</h2>
      <p style={p}>In the event of a dispute between an Owner and a Renter, RTRT will provide mediation services through our damage claim system. Both parties agree to engage in good faith through the platform's dispute resolution process before pursuing external legal remedies. RTRT's mediation decisions are recommendations and are not legally binding arbitration.</p>

      <h2 style={h2}>9. Termination</h2>
      <p style={p}>Either party may terminate this Agreement at any time by closing their RTRT account. Termination does not affect obligations arising from bookings confirmed before termination. RTRT may terminate this Agreement immediately if a User violates these terms or engages in prohibited conduct.</p>

      <h2 style={h2}>10. Amendments</h2>
      <p style={p}>RTRT reserves the right to amend this Agreement. Users will be notified of material changes and continued use of the platform constitutes acceptance of amended terms.</p>

      <h2 style={h2}>11. Contact</h2>
      <p style={p}>For questions about this Broker Agreement, contact us at:<br/>Rent This Rent That LLC<br/>Email: rentthisrentthat@proton.me<br/>Website: rentthisrentthat.com</p>
    </LegalPage>
  );
}

// ─── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  // Auth state
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // UI state
  const [view, setView]             = useState("browse");
  const [searchZip, setSearchZip]   = useState("");
  const [radius, setRadius]         = useState("25");
  const [searchText, setSearchText] = useState("");
  const [category, setCategory]     = useState("All");
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [detailItem, setDetailItem]     = useState(null);
  const [showList, setShowList]         = useState(false);
  const [editItem, setEditItem]         = useState(null);
  const [calendarItem, setCalendarItem] = useState(null);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [showAuth, setShowAuth]         = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [toast, setToast]               = useState(null);

  // Data state
  const [listings, setListings]         = useState([]);
  const [myListings, setMyListings]     = useState([]);
  const [myBookings, setMyBookings]     = useState([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [videoLocation, setVideoLocation] = useState(null);
  // showAllLocations removed — now showing top 6 only
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "map"
  const [hoveredListingId, setHoveredListingId] = useState(null);
  // New feature state
  const [favorites, setFavorites] = useState(() => { try { return JSON.parse(localStorage.getItem("rtrt_favs")||"[]"); } catch { return []; } });
  const [msgTarget, setMsgTarget] = useState(null); // { userId, userName, listingTitle }
  const [showBlog, setShowBlog] = useState(false);
  const [homepageReviews, setHomepageReviews] = useState([]);
  const [expandedBlog, setExpandedBlog] = useState(null); // track which blog post is expanded
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);

  // New feature states (features 2-8)
  const [protectionTier, setProtectionTier] = useState("standard"); // Feature 2: Damage protection tiers
  const [showNotifications, setShowNotifications] = useState(false); // Feature 8: Notification preferences
  const [unreadCount, setUnreadCount] = useState(0); // Feature 8: Unread notifications badge
  const [instantBookEnabled, setInstantBookEnabled] = useState(false); // Feature 5: Instant book toggle (for current listing)
  // Damage claims & disputes
  const [claimBooking, setClaimBooking] = useState(null);   // booking object to file a claim against
  const [claimListing, setClaimListing] = useState(null);   // associated listing for the claim
  const [disputeClaim, setDisputeClaim] = useState(null);    // claim object to respond to
  const [myClaims, setMyClaims] = useState([]);              // all claims involving the user
  const [ownerBookings, setOwnerBookings] = useState([]);    // bookings on the owner's listings
  // Notification center
  const [notifications, setNotifications] = useState([]);
  const [showNotifCenter, setShowNotifCenter] = useState(false);
  // Admin
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  // ZIP radius filtering
  const [radiusFiltering, setRadiusFiltering] = useState(false);
  const [searchLocation, setSearchLocation] = useState(null); // { city, state } from ZIP lookup
  // Mobile menu
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleFavorite = (listingId) => {
    setFavorites(prev => {
      const next = prev.includes(listingId) ? prev.filter(id=>id!==listingId) : [...prev, listingId];
      try { localStorage.setItem("rtrt_favs", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const PROMO_CODES = { "WELCOME10": { discount:10, desc:"$10 off your first rental!" }, "REFER20": { discount:20, desc:"$20 referral bonus!" }, "SUMMER25": { discount:25, desc:"$25 off summer rentals!" }, "RTRT15": { discount:15, desc:"$15 new member discount!" } };
  const applyPromo = () => {
    const code = promoCode.toUpperCase().trim();
    if (PROMO_CODES[code]) { setPromoApplied(PROMO_CODES[code]); showToast(`🎉 ${PROMO_CODES[code].desc}`); }
    else { showToast("❌ Invalid promo code"); }
    setPromoCode("");
  };

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(null), 3200); };

  // ── Set page title & SEO meta tags ──
  useEffect(() => {
    document.title = "Rent This Rent That — Peer-to-Peer Rental Marketplace";
    const metas = {
      "description": "Rent boats, jet skis, RVs, aircraft, tools, and more from people in your community. List your stuff and earn extra income. Peer-to-peer rentals made easy.",
      "og:title": "Rent This Rent That — Peer-to-Peer Rental Marketplace",
      "og:description": "Rent anything from your neighbors — boats, RVs, tools, cameras, and more. Or list your own items and earn extra income.",
      "og:type": "website",
      "og:url": "https://rentthisrentthat.com",
      "og:site_name": "Rent This Rent That",
      "twitter:card": "summary_large_image",
      "twitter:title": "Rent This Rent That",
      "twitter:description": "Peer-to-peer rental marketplace. Rent what you need, earn from what you own.",
    };
    Object.entries(metas).forEach(([name, content]) => {
      const attr = name.startsWith("og:") ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute(attr, name); document.head.appendChild(tag); }
      tag.setAttribute("content", content);
    });
  }, []);

  // ── URL hash routing for direct links ──
  useEffect(() => {
    const hash = window.location.hash;
    // Password recovery detection
    if (hash && (hash.includes("type=recovery") || hash.includes("type=password_recovery"))) {
      setShowResetPassword(true);
      setAuthLoading(false);
      return;
    }
    // Direct page links: #privacy, #terms, #broker
    const page = hash.replace("#","").toLowerCase();
    if (page === "privacy") setView("privacy");
    else if (page === "terms") setView("terms");
    else if (page === "broker") setView("broker");
  }, []);

  // ── Check auth session on mount (with timeout fallback + lock recovery) ──
  useEffect(() => {
    // Clear any stale navigator locks that could freeze Supabase queries
    try {
      const keys = Object.keys(localStorage);
      keys.filter(k => k.startsWith('lock:') || (k.includes('sb-') && k.includes('auth-token-code-verifier')))
          .forEach(k => localStorage.removeItem(k));
    } catch(e) {}

    const init = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          setUser(session.user);
          try { const p = await getProfile(session.user.id); setProfile(p); } catch(e) {}
        }
      } catch(e) {
        console.error("Auth init error:", e);
        // If auth init fails, clear potentially corrupted state
        try {
          Object.keys(localStorage)
            .filter(k => k.includes('supabase') || k.includes('sb-') || k.includes('rtrt-auth'))
            .forEach(k => localStorage.removeItem(k));
        } catch(e2) {}
      } finally {
        setAuthLoading(false);
      }
    };
    init();
    // Failsafe: if auth check hangs, still show the app after 1.5 seconds (reduced from 3)
    const timeout = setTimeout(() => setAuthLoading(false), 1500);

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setShowResetPassword(true);
      }
      if (session?.user) {
        setUser(session.user);
        try { const p = await getProfile(session.user.id); setProfile(p); } catch(e) {}
      } else {
        setUser(null); setProfile(null);
        setIsAdmin(false);
      }
    });
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // ── Fetch listings (with retry & error state) ──
  const [listingsError, setListingsError] = useState(null);
  const fetchListings = useCallback(async (retryCount = 0) => {
    setListingsLoading(true);
    setListingsError(null);
    setRadiusFiltering(false);
    setSearchLocation(null);
    try {
      const catParam = (category==="All"||category==="__favs__") ? null : category;
      let data = await getListings({ category: catParam, search:searchText });
      if (category==="__favs__") data = data.filter(l=>favorites.includes(l.id));
      // Apply ZIP radius filtering client-side
      if (searchZip && searchZip.length === 5 && radius !== "all") {
        setRadiusFiltering(true);
        const origin = await getZipCoords(searchZip);
        if (origin) setSearchLocation({ city: origin.city, state: origin.state });
        data = await filterByRadius(data, searchZip, radius);
        setRadiusFiltering(false);
      }
      setListings(data);
    } catch(e) {
      console.error("Fetch listings error:", e);
      if (retryCount < 2) {
        setTimeout(() => fetchListings(retryCount + 1), 1500);
        return;
      }
      setListingsError("Could not load listings. Please check your connection and try again.");
    } finally {
      setListingsLoading(false);
      setRadiusFiltering(false);
    }
  }, [category, searchText, favorites, searchZip, radius]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  // ── Fetch real reviews for homepage ──
  useEffect(() => {
    supabase.from("reviews").select("*, profiles:reviewer_id(full_name, avatar_url)")
      .order("created_at", { ascending: false }).limit(6)
      .then(({ data }) => { if (data) setHomepageReviews(data.filter(r => r.review_text)); });
  }, []);

  // ── Fetch my listings and owner bookings when on that view ──
  useEffect(() => {
    if (view==="mylistings" && profile?.id) {
      getOwnerListings(profile.id).then(setMyListings).catch(console.error);
      getOwnerBookings(profile.id).then(setOwnerBookings).catch(console.error);
    }
  }, [view, profile]);

  // ── Fetch my bookings when on that view ──
  useEffect(() => {
    if (view==="myrentals" && profile?.id) {
      getRenterBookings(profile.id).then(setMyBookings).catch(console.error);
    }
  }, [view, profile]);

  // ── Fetch claims when viewing rentals or listings ──
  useEffect(() => {
    if ((view==="myrentals" || view==="mylistings") && profile?.id) {
      getMyClaims(profile.id).then(setMyClaims).catch(console.error);
    }
  }, [view, profile]);

  // ── Fetch notifications + check admin + real-time subscription ──
  useEffect(() => {
    if (!user?.id) { setNotifications([]); setUnreadCount(0); setIsAdmin(false); return; }
    // Load notifications
    getNotifications(user.id).then(notifs => {
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
    }).catch(console.error);
    // Check admin status
    checkIsAdmin(user.id).then(setIsAdmin).catch(() => setIsAdmin(false));
    // Subscribe to real-time notifications
    const channel = subscribeToNotifications(user.id, (newNotif) => {
      setNotifications(prev => [newNotif, ...prev]);
      setUnreadCount(prev => prev + 1);
    });
    return () => { channel.unsubscribe(); };
  }, [user?.id]);

  const handleAuth = (authUser, authProfile) => {
    setUser(authUser); setProfile(authProfile);
    setShowAuth(false);
    showToast(`👋 Welcome, ${authProfile?.full_name || authUser.email}!`);
  };

  const handleSignOut = async () => {
    try { await signOut(); } catch(e) { console.error("Sign out error:", e); }
    setUser(null); setProfile(null);
    setIsAdmin(false);
    setView("browse"); showToast("Signed out.");
    // Force re-fetch listings as anonymous user to ensure clean state
    setTimeout(() => fetchListings(), 100);
  };

  const otherCats = ALL_CATEGORIES.filter(c => !FEATURED_CATS.includes(c) && (listings.some(l=>l.category===c) || true));

  // ── RENDER ──
  if (authLoading) return (
    <div style={{ ...S.app, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🏠</div>
        <div style={{ fontFamily:"'Georgia',serif", fontSize:24, color:COLORS.accent, fontWeight:"bold" }}>Rent This Rent That</div>
        <div style={{ color:COLORS.muted, fontSize:13, marginTop:8 }}>Loading...</div>
      </div>
    </div>
  );

  return (
    <div style={S.app}>
      {/* ── RESPONSIVE CSS ── */}
      <style>{`
        @media (max-width: 768px) {
          .rtrt-nav-links { display: none !important; }
          .rtrt-nav-links.open { display: flex !important; flex-direction: column; position: absolute; top: 100%; left: 0; right: 0; background: ${COLORS.surface}; border-bottom: 1px solid ${COLORS.border}; padding: 12px; gap: 8px; z-index: 101; }
          .rtrt-hamburger { display: flex !important; }
          .rtrt-hero-title { font-size: 24px !important; }
          .rtrt-hero-sub { font-size: 14px !important; }
          .rtrt-search-row { flex-direction: column !important; }
          .rtrt-search-row > * { width: 100% !important; min-width: 0 !important; }
          .rtrt-cat-scroll { flex-wrap: nowrap !important; overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
        }
        @media (min-width: 769px) {
          .rtrt-hamburger { display: none !important; }
        }
        .rtrt-hamburger { background: none; border: none; cursor: pointer; font-size: 24px; padding: 4px 8px; color: ${COLORS.text}; }
      `}</style>
      {/* ── HEADER ── */}
      <div style={{ ...S.header, position:"relative" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", minWidth:0 }} onClick={()=>setView("browse")}>
          <img src="/logo.png" alt="RTRT" style={{ height:36, width:"auto", flexShrink:0 }} />
          <div style={{ minWidth:0 }}>
            <div style={S.logo}>Rent This Rent That</div>
          </div>
        </div>
        {/* Hamburger for mobile */}
        <button className="rtrt-hamburger" onClick={()=>setMobileMenuOpen(o=>!o)}>{mobileMenuOpen ? "✕" : "☰"}</button>
        <div className={`rtrt-nav-links${mobileMenuOpen?" open":""}`} style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
          {[["browse","Browse"],["myrentals","My Rentals"],["mylistings","My Listings"]].map(([k,label])=>(
            <button key={k} style={{ padding:"8px 12px", borderRadius:8, border:"none", cursor:"pointer", fontSize:14, fontWeight:600, background:view===k?COLORS.accent:"transparent", color:view===k?"#000":COLORS.muted, transition:"all 0.2s" }} onClick={()=>{setView(k);setMobileMenuOpen(false);}}>{label}</button>
          ))}
          <button style={{ ...S.btn("primary","sm") }} onClick={()=>{setShowList(true);setMobileMenuOpen(false);}}>+ List</button>
          {/* Notification bell → opens NotificationCenter */}
          <button style={{ position:"relative", background:"none", border:"none", cursor:"pointer", fontSize:18, padding:"4px 8px" }} onClick={() => { if (user) setShowNotifCenter(true); else setShowAuth(true); setMobileMenuOpen(false); }} title="Notifications">
            🔔
            {unreadCount > 0 && <span style={{ position:"absolute", top:0, right:0, background:COLORS.red, color:"#fff", fontSize:10, fontWeight:"bold", width:18, height:18, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center" }}>{unreadCount > 9 ? "9+" : unreadCount}</span>}
          </button>
          {/* Admin gear icon — only for admins */}
          {isAdmin && <button style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, padding:"4px 8px" }} onClick={() => { setShowAdmin(true); setMobileMenuOpen(false); }} title="Admin Panel">🔧</button>}
          <button style={{ ...S.btn("green","sm") }} onClick={()=>{setMobileMenuOpen(false);
            const refLink = user ? `https://rentthisrentthat.com?ref=${user.id}` : "https://rentthisrentthat.com";
            const subject = encodeURIComponent("You have to check out Rent This Rent That!");
            const body = encodeURIComponent(
              "Hey!\n\n" +
              "I've been using Rent This Rent That -- an awesome peer-to-peer rental marketplace where you can rent boats, RVs, jet skis, aircraft, tools, camera gear, and so much more from people near you.\n\n" +
              "Sign up using my link and we both get referral credits:\n\n" +
              refLink + "\n\n" +
              "You can also list your own stuff for rent and earn extra income!\n\n" +
              "Check it out: " + refLink + "\n\n" +
              "-- Sent via Rent This Rent That (rentthisrentthat.com)"
            );
            const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
            const w = window.open(mailtoUrl, "_blank");
            if (!w || w.closed) {
              // Fallback: copy referral link to clipboard if email client didn't open
              try { navigator.clipboard.writeText(refLink); } catch(e) {}
              showToast("Referral link copied to clipboard! Paste it in an email or message to share.");
            }
          }}>Refer a Friend</button>
          {user
            ? <button style={{ ...S.btn("red","sm") }} onClick={()=>{handleSignOut();setMobileMenuOpen(false);}}>Sign Out</button>
            : <button style={{ ...S.btn("primary","sm") }} onClick={()=>{setShowAuth(true);setMobileMenuOpen(false);}}>Sign In</button>
          }
        </div>
      </div>

      <div style={S.main}>
        {/* ── BROWSE VIEW ── */}
        {view==="browse" && <>
          {/* Hero with surfboard logo */}
          <div style={{ background:`linear-gradient(180deg, #F5C97E 0%, #E8A94E 40%, #D4883A 100%)`, border:`1px solid ${COLORS.border}`, borderRadius:16, padding:"24px 16px 20px", marginBottom:20, textAlign:"center", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", bottom:0, left:0, right:0, height:"40%", background:"linear-gradient(0deg, rgba(194,154,89,0.5) 0%, transparent 100%)" }} />
            <div style={{ position:"relative", zIndex:1 }}>
              <img src="/logo.png" alt="Rent This Rent That" style={{ width:"60%", maxWidth:280, height:"auto", marginBottom:12, filter:"drop-shadow(0 4px 20px rgba(0,0,0,0.3))" }} />
              <div style={{ fontSize:12, color:"#5a3d1a", marginBottom:4, fontWeight:600, textTransform:"uppercase", letterSpacing:2 }}>Peer-to-Peer Rental Platform</div>
              <div style={{ fontSize:22, fontWeight:"bold", marginBottom:8, lineHeight:1.2, color:"#3a2510" }}>Rent What You Need,<br />Earn From What You Own</div>
              <div style={{ color:"#6b4c2a", fontSize:13, marginBottom:16 }}>Boats • Jet Skis • Aircraft • RVs • Vehicles • Tools</div>
              <div style={{ display:"flex", gap:8, justifyContent:"center", flexWrap:"wrap" }}>
                {[[String(listings.length || 0),"Listings"],["100%","Insured"],["Secure","Payments"],["Local","Community"],["24/7","Support"]].map(([n,l])=>(
                  <div key={l} style={{ textAlign:"center", background:"rgba(255,255,255,0.25)", borderRadius:10, padding:"8px 12px", border:"1px solid rgba(255,255,255,0.3)", minWidth:60 }}><div style={{ fontSize:18, fontWeight:"bold", color:"#3a2510" }}>{n}</div><div style={{ fontSize:9, color:"#6b4c2a", textTransform:"uppercase", letterSpacing:1, marginTop:1 }}>{l}</div></div>
                ))}
              </div>
            </div>
          </div>

          {/* ── HOW IT WORKS ── */}
          <div style={{ margin:"10px 0 24px", padding:"24px 16px", background:COLORS.card, borderRadius:14, border:`1px solid ${COLORS.border}` }}>
            <div style={{ textAlign:"center", marginBottom:20 }}>
              <div style={{ fontSize:18, fontWeight:700, color:COLORS.text }}>How It Works</div>
              <div style={{ color:COLORS.muted, fontSize:13 }}>Three simple steps to start renting</div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px, 1fr))", gap:16, textAlign:"center" }}>
              {[
                { step:"1", icon:"📋", title:"List or Browse", desc:"Post your items for rent or find what you need from local owners." },
                { step:"2", icon:"📅", title:"Book & Pay", desc:"Choose your dates, pay securely through the platform with insurance included." },
                { step:"3", icon:"🤝", title:"Meet & Enjoy", desc:"Pick up your rental, enjoy it, and leave a review when you're done!" },
              ].map(s => (
                <div key={s.step}>
                  <div style={{ width:48, height:48, borderRadius:"50%", background:`${COLORS.accent}22`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, margin:"0 auto 10px" }}>{s.icon}</div>
                  <div style={{ fontSize:11, color:COLORS.accent, fontWeight:700, marginBottom:4 }}>STEP {s.step}</div>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:4, color:COLORS.text }}>{s.title}</div>
                  <div style={{ color:COLORS.muted, fontSize:12, lineHeight:1.5 }}>{s.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Location banners — Top 6 Featured Locations */}
          <div style={{ marginBottom:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <div style={{ fontSize:11, color:COLORS.muted, textTransform:"uppercase", letterSpacing:1 }}>🎬 Tap to Watch • Featured Locations</div>
            </div>

            <div style={{ display:"flex", gap:10, overflowX:"auto", paddingBottom:4 }}>
              {LOCATION_DATA.filter(l=>["California","Florida","Texas","New York","Hawaii","Colorado"].includes(l.name)).map(loc=>(
                <div key={loc.name} onClick={()=>setVideoLocation(loc)} style={{ position:"relative", minWidth:150, height:90, borderRadius:12, overflow:"hidden", cursor:"pointer", flexShrink:0, border:`1px solid ${COLORS.border}`, transition:"transform 0.2s, box-shadow 0.2s" }} onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.05)";e.currentTarget.style.boxShadow="0 4px 20px rgba(244,162,53,0.3)";}} onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";e.currentTarget.style.boxShadow="none";}}>
                  <img src={loc.img} alt={loc.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                  <div style={{ position:"absolute", inset:0, background:"linear-gradient(0deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 100%)" }} />
                  <div style={{ position:"absolute", top:6, right:6, background:"rgba(0,0,0,0.6)", borderRadius:"50%", width:22, height:22, display:"flex", alignItems:"center", justifyContent:"center" }}>
                    <span style={{ fontSize:10, color:"#fff" }}>▶</span>
                  </div>
                  <div style={{ position:"absolute", bottom:6, left:8, right:8 }}>
                    <div style={{ color:"#fff", fontSize:12, fontWeight:700, textShadow:"0 1px 3px rgba(0,0,0,0.5)" }}>{loc.flag} {loc.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── MONTHLY PROMOTIONS (click to browse category) ── */}
          <div style={{ display:"flex", gap:10, overflowX:"auto", marginBottom:16, paddingBottom:4 }}>
            {getSeasonalPromos().map((promo, i)=>(
              <div key={i} onClick={()=>{ if(promo.category) setCategory(promo.category); }} style={{ minWidth:280, borderRadius:12, padding:"16px 18px", background:promo.bg, flexShrink:0, cursor:"pointer", border:"1px solid rgba(255,255,255,0.1)", transition:"transform 0.2s" }}
                onMouseEnter={e=>e.currentTarget.style.transform="scale(1.02)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
                <div style={{ color:"#fff", fontWeight:700, fontSize:15, marginBottom:4 }}>{promo.title}</div>
                <div style={{ color:"rgba(255,255,255,0.85)", fontSize:12, lineHeight:1.5 }}>{promo.desc}</div>
                {promo.category && <div style={{ color:"rgba(255,255,255,0.6)", fontSize:10, marginTop:8, fontWeight:600, textTransform:"uppercase", letterSpacing:0.5 }}>Tap to browse {promo.category} listings →</div>}
              </div>
            ))}
            {/* Promo code input */}
            <div style={{ minWidth:220, borderRadius:12, padding:"14px 16px", background:COLORS.card, border:`1px solid ${COLORS.border}`, flexShrink:0, display:"flex", flexDirection:"column", justifyContent:"center", gap:8 }}>
              <div style={{ color:COLORS.text, fontSize:12, fontWeight:700 }}>🎟️ Have a promo code?</div>
              <div style={{ display:"flex", gap:6 }}>
                <input value={promoCode} onChange={e=>setPromoCode(e.target.value)} onKeyDown={e=>e.key==="Enter"&&applyPromo()} placeholder="Enter code" style={{ flex:1, padding:"6px 10px", borderRadius:6, border:`1px solid ${COLORS.border}`, background:COLORS.surface, color:COLORS.text, fontSize:12, outline:"none" }} />
                <button onClick={applyPromo} style={{ padding:"6px 12px", borderRadius:6, border:"none", background:COLORS.accent, color:"#000", fontWeight:700, fontSize:11, cursor:"pointer" }}>Apply</button>
              </div>
              {promoApplied && <div style={{ color:COLORS.green, fontSize:11 }}>✅ {promoApplied.desc}</div>}
            </div>
          </div>

          {/* ── FAVORITES FILTER ── */}
          {favorites.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <button onClick={()=>setCategory(category==="__favs__"?"All":"__favs__")} style={{ padding:"7px 14px", borderRadius:20, border:`1px solid ${category==="__favs__"?COLORS.red:COLORS.border}`, background:category==="__favs__"?COLORS.red+"22":"transparent", color:category==="__favs__"?COLORS.red:COLORS.muted, cursor:"pointer", fontSize:13, fontWeight:600, transition:"all 0.2s" }}>
                ❤️ My Favorites ({favorites.length})
              </button>
            </div>
          )}

          {/* Search bar - stacks on mobile */}
          <div className="rtrt-search-row" style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:20, background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:10 }}>
            <input style={{ ...S.input, flex:"1 1 100%", minWidth:0 }} placeholder="🔍  Search boats, aircraft, RVs, tools..." value={searchText} onChange={e=>setSearchText(e.target.value)} />
            <input style={{ ...S.input, flex:"1 1 30%", minWidth:80 }} placeholder="📍 ZIP" value={searchZip} onChange={e=>setSearchZip(e.target.value)} maxLength={5} />
            <select style={{ ...S.input, flex:"1 1 25%", minWidth:70 }} value={radius} onChange={e=>setRadius(e.target.value)}>
              {["5","10","25","50","100","250","500"].map(r=><option key={r} value={r}>{r} mi</option>)}
              <option value="all">All</option>
            </select>
            <button style={{ ...S.btn("primary"), flex:"1 1 25%", minWidth:70 }} onClick={fetchListings}>Search</button>
          </div>

          {/* Category pills */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, color:COLORS.muted, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Categories</div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:6 }}>
              {FEATURED_CATS.map(c=>(
                <button key={c} onClick={()=>setCategory(c)} style={{ padding:"8px 14px", borderRadius:20, border:`1px solid ${category===c?COLORS.accent:COLORS.border}`, background:category===c?COLORS.accent+"22":"transparent", color:category===c?COLORS.accent:COLORS.muted, cursor:"pointer", fontSize:14, fontWeight:600, transition:"all 0.2s" }}>
                  {CAT_ICONS[c]} {c}
                </button>
              ))}
            </div>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
              {otherCats.map(c=>(
                <button key={c} onClick={()=>setCategory(c)} style={{ padding:"7px 12px", borderRadius:20, border:`1px solid ${category===c?COLORS.accent:COLORS.border}`, background:category===c?COLORS.accent+"22":"transparent", color:category===c?COLORS.accent:COLORS.muted, cursor:"pointer", fontSize:13, fontWeight:600, transition:"all 0.2s" }}>
                  {CAT_ICONS[c]||"📦"} {c}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:12, color:COLORS.muted }}>
              {listingsLoading ? (radiusFiltering ? "📍 Filtering by distance..." : "Loading listings...") : (
                <>
                  {listings.length} listing{listings.length!==1?"s":""} {category!=="All"&&category!=="__favs__"?`in ${category}`:category==="__favs__"?"in favorites":"available"}
                  {searchLocation && searchZip && radius !== "all" && (
                    <span style={{ color:COLORS.accent }}> within {radius} mi of {searchLocation.city}, {searchLocation.state} ({searchZip})</span>
                  )}
                </>
              )}
            </div>
            {/* Grid / Map toggle */}
            <div style={{ display:"flex", gap:4, background:COLORS.card, borderRadius:8, padding:3, border:`1px solid ${COLORS.border}` }}>
              <button onClick={()=>setViewMode("grid")} style={{ padding:"5px 12px", borderRadius:6, border:"none", background:viewMode==="grid"?COLORS.accent:"transparent", color:viewMode==="grid"?"#000":COLORS.muted, fontWeight:700, fontSize:12, cursor:"pointer", transition:"all 0.2s" }}>▦ Grid</button>
              <button onClick={()=>setViewMode("map")} style={{ padding:"5px 12px", borderRadius:6, border:"none", background:viewMode==="map"?COLORS.accent:"transparent", color:viewMode==="map"?"#000":COLORS.muted, fontWeight:700, fontSize:12, cursor:"pointer", transition:"all 0.2s" }}>🗺️ Map</button>
            </div>
          </div>

          {/* ── SPLIT MAP + LIST VIEW ── */}
          {viewMode === "map" && listingsLoading && (
            <div style={{ textAlign:"center", padding:"60px 0", color:COLORS.muted }}>
              <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
              <div>{radiusFiltering ? "📍 Filtering by distance..." : "Loading listings..."}</div>
            </div>
          )}
          {viewMode === "map" && !listingsLoading && listings.length > 0 ? (
            <div style={{ display:"flex", gap:0, borderRadius:14, overflow:"hidden", border:`1px solid ${COLORS.border}`, height:"calc(100vh - 280px)", minHeight:500 }}>
              {/* Left: scrollable listing cards */}
              <div style={{ width:380, minWidth:320, flexShrink:0, overflowY:"auto", background:COLORS.surface, borderRight:`1px solid ${COLORS.border}`, padding:12, display:"flex", flexDirection:"column", gap:10 }}>
                {listings.map(item => (
                  <MapListingCard
                    key={item.id}
                    item={item}
                    isFav={favorites.includes(item.id)}
                    isHighlighted={hoveredListingId === item.id}
                    onSelect={item => setDetailItem(item)}
                    onToggleFav={toggleFavorite}
                    onHover={setHoveredListingId}
                    onLeave={() => setHoveredListingId(null)}
                    user={user}
                    onMessage={(userId, userName, title) => setMsgTarget({ userId, userName, listingTitle: title })}
                  />
                ))}
              </div>
              {/* Right: sticky map */}
              <div style={{ flex:1, minWidth:0 }}>
                <MapView listings={listings} onSelectListing={item=>setDetailItem(item)} favorites={favorites} toggleFavorite={toggleFavorite} highlightedId={hoveredListingId} onHoverListing={setHoveredListingId} />
              </div>
            </div>
          ) : viewMode === "map" && !listingsLoading && listings.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 0", color:COLORS.muted }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🗺️</div>
              <div style={{ fontWeight:700, fontSize:17, marginBottom:7, color:COLORS.text }}>No listings to map</div>
              <div style={{ marginBottom:18 }}>Try a different category or search.</div>
            </div>
          ) : null}

          {/* ── GRID VIEW ── */}
          {viewMode === "grid" && (listingsLoading ? (
            <div style={{ textAlign:"center", padding:"60px 0", color:COLORS.muted }}>
              <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
              <div>Loading listings from database...</div>
            </div>
          ) : listingsError ? (
            <div style={{ textAlign:"center", padding:"60px 0", color:COLORS.muted }}>
              <div style={{ fontSize:48, marginBottom:12 }}>⚠️</div>
              <div style={{ fontWeight:700, fontSize:17, marginBottom:7, color:COLORS.red }}>{listingsError}</div>
              <button onClick={()=>fetchListings(0)} style={{ ...S.btn("primary"), marginTop:12 }}>🔄 Retry</button>
            </div>
          ) : listings.length === 0 ? (
            <div style={{ textAlign:"center", padding:"60px 0", color:COLORS.muted }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🏷️</div>
              <div style={{ fontWeight:700, fontSize:17, marginBottom:7, color:COLORS.text }}>No Listings Yet</div>
              <div style={{ marginBottom:18 }}>Be the first to list in {category!=="All"?category:"this category"}!</div>
              <button onClick={()=>setShowList(true)} style={S.btn("primary")}>+ Add the First Listing</button>
            </div>
          ) : (
            <div style={S.grid}>
              {listings.map(item=>{
                const licReq = LICENSE_REQUIREMENTS[item.category];
                const priceDay = item.price_per_day || item.priceDay || 0;
                const priceWeek = item.price_per_week || item.priceWeek || 0;
                const ownerName = item.profiles?.full_name || item.owner || "Lister";
                return (
                  <div key={item.id} style={S.card} onClick={()=>setDetailItem(item)}
                    onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.borderColor=COLORS.accent+"66";}}
                    onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.borderColor=COLORS.border;}}>
                    <div style={S.cardImg}>
                      <ImageCarousel images={[...parseImageUrls(item.image_url), ...(parseImageUrls(item.image_url).length === 0 ? [CAT_IMAGES[item.category] || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop"] : [])]} height={160} />
                      {licReq?.required && <span style={{ position:"absolute", top:8, right:8, background:COLORS.purple+"ee", color:"#fff", fontSize:9, fontWeight:700, padding:"3px 7px", borderRadius:20 }}>🪪 License Req.</span>}
                      <button onClick={e=>{e.stopPropagation(); toggleFavorite(item.id);}} style={{ position:"absolute", top:8, left:8, background:"rgba(0,0,0,0.5)", border:"none", borderRadius:"50%", width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:16, transition:"transform 0.2s" }} title={favorites.includes(item.id)?"Remove from favorites":"Add to favorites"}>
                        {favorites.includes(item.id) ? "❤️" : "🤍"}
                      </button>
                      <button onClick={e=>{e.stopPropagation(); const url=`https://rentthisrentthat.com/listing/${item.id}`; const text=`Check out "${item.title}" for $${priceDay}/day on Rent This Rent That!`; if(navigator.share){navigator.share({title:item.title,text,url}).catch(()=>{});}else{try{navigator.clipboard.writeText(url);}catch{}} }} style={{ position:"absolute", top:8, left:48, background:"rgba(0,0,0,0.5)", border:"none", borderRadius:"50%", width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:14, transition:"transform 0.2s" }} title="Share listing">
                        📤
                      </button>
                    </div>
                    <div style={S.cardBody}>
                      <div style={{ fontWeight:700, fontSize:15, marginBottom:4, lineHeight:1.3 }}>{item.title}</div>
                      {item._distance != null && <div style={{ fontSize:10, color:COLORS.accent, fontWeight:700, marginBottom:3 }}>📍 {item._distance} mi away</div>}
                      <div style={{ color:COLORS.muted, fontSize:12, marginBottom:6, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span style={{ display:"flex", alignItems:"center", gap:4 }}>
                          {item.profiles?.avatar_url ? (
                            <img src={item.profiles.avatar_url} alt="" style={{ width:18, height:18, borderRadius:"50%", objectFit:"cover" }} />
                          ) : (
                            <span style={{ width:18, height:18, borderRadius:"50%", background:`${COLORS.accent}33`, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:COLORS.accent }}>{ownerName[0]}</span>
                          )}
                          {ownerName}
                        </span>
                        {user && item.user_id !== user.id && <button onClick={e=>{e.stopPropagation(); setMsgTarget({ userId:item.user_id, userName:ownerName, listingTitle:item.title });}} style={{ background:"none", border:"none", color:COLORS.accent, cursor:"pointer", fontSize:11, fontWeight:700 }}>💬 Message</button>}
                      </div>
                      <div style={{ marginBottom:8, display:"flex", flexWrap:"wrap", gap:2 }}>
                        <span style={S.badge(COLORS.blue)}>{item.category}</span>
                        {(item.delivery_available||item.delivery) && <span style={S.badge(COLORS.green)}>🚚 Delivery</span>}
                        {item.instant_book && <span style={S.badge(COLORS.accent)}>⚡ Instant Book</span>}
                        <span style={S.badge(COLORS.green)}>🛡️ Insured</span>
                      </div>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <div><span style={{ fontSize:20, fontWeight:"bold", color:COLORS.accent }}>${priceDay}</span><span style={{ color:COLORS.muted, fontSize:13 }}>/day</span>{priceWeek>0&&<div style={{ color:COLORS.muted, fontSize:12 }}>${priceWeek}/wk</div>}</div>
                        <div style={{ textAlign:"right" }}>
                          {item.rating_as_owner>0&&<div style={{ color:COLORS.accent, fontSize:12 }}>★ {item.rating_as_owner}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* ── SUGGESTED LISTINGS ── */}
          {listings.length > 3 && (
            <div style={{ marginTop:30, marginBottom:30 }}>
              <div style={{ fontSize:18, fontWeight:700, color:COLORS.text, marginBottom:12 }}>🔥 You Might Also Like</div>
              <div style={{ display:"flex", gap:12, overflowX:"auto", paddingBottom:8 }}>
                {listings.filter(l => category === "All" || l.category !== category).sort(()=>0.5-Math.random()).slice(0, 6).map(item => {
                  const priceDay = item.price_per_day || item.priceDay || 0;
                  return (
                    <div key={item.id} onClick={()=>setDetailItem(item)} style={{ minWidth:180, background:COLORS.card, borderRadius:12, overflow:"hidden", border:`1px solid ${COLORS.border}`, cursor:"pointer", flexShrink:0, transition:"transform 0.2s" }}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.03)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
                      <img src={parseImageUrls(item.image_url)[0] || CAT_IMAGES[item.category] || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=300&h=160&fit=crop"} alt={item.title} style={{ width:"100%", height:100, objectFit:"cover" }} onError={e => { e.target.onerror=null; e.target.src=CAT_IMAGES[item.category]||"https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=300&h=160&fit=crop"; }} />
                      <div style={{ padding:"10px 12px" }}>
                        <div style={{ fontWeight:700, fontSize:13, color:COLORS.text, marginBottom:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.title}</div>
                        <div style={{ color:COLORS.muted, fontSize:11, marginBottom:4 }}>{item.category}</div>
                        <div style={{ color:COLORS.accent, fontWeight:700, fontSize:15 }}>${priceDay}<span style={{ color:COLORS.muted, fontSize:11, fontWeight:400 }}>/day</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── CUSTOMER TESTIMONIALS ── */}
          {/* ── COMMUNITY REVIEWS (real reviews from Supabase) ── */}
          <div style={{ marginTop:30, marginBottom:30 }}>
            <div style={{ textAlign:"center", marginBottom:24 }}>
              <div style={{ fontSize:22, fontWeight:"bold", color:COLORS.text, marginBottom:6 }}>⭐ Community Reviews</div>
              <div style={{ color:COLORS.muted, fontSize:14 }}>Real experiences from our renters and owners</div>
            </div>
            {homepageReviews && homepageReviews.length > 0 ? (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:16 }}>
                {homepageReviews.map((r) => (
                  <div key={r.id} style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14, padding:18, transition:"transform 0.2s" }}
                    onMouseEnter={e=>e.currentTarget.style.transform="translateY(-3px)"} onMouseLeave={e=>e.currentTarget.style.transform=""}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
                      {r.profiles?.avatar_url ? (
                        <img src={r.profiles.avatar_url} alt="" style={{ width:42, height:42, borderRadius:"50%", objectFit:"cover" }} />
                      ) : (
                        <div style={{ width:42, height:42, borderRadius:"50%", background:`linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:700, color:"#000" }}>
                          {(r.profiles?.full_name || "?")[0].toUpperCase()}
                        </div>
                      )}
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:14, color:COLORS.text }}>{r.profiles?.full_name || "RTRT User"}</div>
                        <div style={{ color:COLORS.muted, fontSize:11 }}>{new Date(r.created_at).toLocaleDateString("en-US", { month:"short", year:"numeric" })}</div>
                      </div>
                      <div style={{ color:COLORS.accent, fontSize:12 }}>{"★".repeat(r.overall_rating)}</div>
                    </div>
                    {r.review_text && <p style={{ color:"#ccc", fontSize:13, lineHeight:1.7, margin:"0 0 10px", fontStyle:"italic" }}>"{r.review_text}"</p>}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign:"center", padding:40, background:COLORS.card, borderRadius:14, border:`1px solid ${COLORS.border}` }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🌟</div>
                <div style={{ fontWeight:700, fontSize:18, marginBottom:8, color:COLORS.text }}>Be Our First Reviewer!</div>
                <div style={{ color:COLORS.muted, fontSize:14, lineHeight:1.6, maxWidth:400, margin:"0 auto 20px" }}>
                  Rent something amazing, then leave a review. Your experience helps build our community!
                </div>
                <button onClick={()=>setView("browse")} style={S.btn("primary")}>Browse Listings →</button>
              </div>
            )}
          </div>

          {/* ── EMAIL CAPTURE / NEWSLETTER ── */}
          <div style={{ margin:"20px 0 30px", padding:"28px 24px", background:`linear-gradient(135deg, ${COLORS.accent}15, ${COLORS.purple}15)`, borderRadius:14, border:`1px solid ${COLORS.accent}33`, textAlign:"center" }}>
            <div style={{ fontSize:18, fontWeight:700, color:COLORS.text, marginBottom:6 }}>Stay in the Loop</div>
            <div style={{ color:COLORS.muted, fontSize:13, marginBottom:16, maxWidth:400, margin:"0 auto 16px" }}>Get notified about new listings, deals, and community updates. No spam — just the good stuff.</div>
            <form onSubmit={async(e)=>{
              e.preventDefault();
              const email = e.target.elements.nlEmail.value.trim();
              if (!email) return;
              try {
                await supabase.from("newsletter_subscribers").insert([{ email }]);
                showToast("You're subscribed! Welcome aboard.");
                e.target.reset();
              } catch(err) {
                if (err.code === "23505") showToast("You're already subscribed!");
                else showToast("Couldn't subscribe. Try again.");
              }
            }} style={{ display:"flex", gap:8, maxWidth:400, margin:"0 auto", flexWrap:"wrap", justifyContent:"center" }}>
              <input name="nlEmail" type="email" placeholder="your@email.com" required style={{ ...S.input, flex:"1 1 220px", minWidth:0 }} />
              <button type="submit" style={{ ...S.btn("primary"), whiteSpace:"nowrap" }}>Subscribe</button>
            </form>
          </div>

          {/* ── BLOG / TIPS SECTION ── */}
          <div style={{ marginTop:30, marginBottom:30 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
              <div>
                <div style={{ fontSize:20, fontWeight:"bold", color:COLORS.text }}>📚 Tips & Resources</div>
                <div style={{ color:COLORS.muted, fontSize:13 }}>Guides for renters and owners</div>
              </div>
              <button onClick={()=>setShowBlog(!showBlog)} style={{ background:"transparent", border:`1px solid ${COLORS.border}`, color:COLORS.accent, fontSize:12, fontWeight:700, padding:"6px 14px", borderRadius:8, cursor:"pointer" }}>
                {showBlog ? "Show Less ▲" : "See All ▼"}
              </button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(260px, 1fr))", gap:14 }}>
              {(showBlog ? BLOG_POSTS : BLOG_POSTS.slice(0,4)).map(post => (
                <div key={post.id} onClick={()=>setExpandedBlog(expandedBlog===post.id ? null : post.id)}
                  style={{ background:COLORS.card, border:`1px solid ${expandedBlog===post.id ? COLORS.accent : COLORS.border}`, borderRadius:12, padding:18, cursor:"pointer", transition:"transform 0.2s, border-color 0.2s", gridColumn: expandedBlog===post.id ? "1 / -1" : "auto" }}
                  onMouseEnter={e=>{if(expandedBlog!==post.id){e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.borderColor=COLORS.accent+"66";}}}
                  onMouseLeave={e=>{if(expandedBlog!==post.id){e.currentTarget.style.transform="";e.currentTarget.style.borderColor=COLORS.border;}}}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    <span style={{ fontSize:24 }}>{post.icon}</span>
                    <span style={S.badge(COLORS.blue)}>{post.category}</span>
                    <span style={{ color:COLORS.muted, fontSize:10, marginLeft:"auto" }}>{post.readTime} read</span>
                  </div>
                  <div style={{ fontWeight:700, fontSize: expandedBlog===post.id ? 18 : 14, color:COLORS.text, marginBottom:8, lineHeight:1.4 }}>{post.title}</div>
                  {expandedBlog===post.id ? (
                    <div>
                      <div style={{ color:COLORS.muted, fontSize:13, lineHeight:1.8, whiteSpace:"pre-line", marginBottom:12 }}>{post.content}</div>
                      <div style={{ marginTop:10, color:COLORS.accent, fontSize:12, fontWeight:700 }}>Close ✕</div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ color:COLORS.muted, fontSize:12, lineHeight:1.6 }}>{post.excerpt}</div>
                      <div style={{ marginTop:10, color:COLORS.accent, fontSize:12, fontWeight:700 }}>Read More →</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>}

        {/* ── MY RENTALS VIEW ── */}
        {view==="myrentals" && <>
          <div style={{ fontSize:26, fontWeight:"bold", marginBottom:6 }}>My Rentals</div>
          <div style={{ color:COLORS.muted, marginBottom:26, fontSize:14 }}>Your active and past rental transactions</div>

          {/* ── Profile card with avatar upload ── */}
          {user && profile && (
            <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14, padding:20, marginBottom:20, display:"flex", alignItems:"center", gap:16 }}>
              <div style={{ position:"relative" }}>
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt="Avatar" style={{ width:64, height:64, borderRadius:"50%", objectFit:"cover", border:`2px solid ${COLORS.accent}` }} />
                ) : (
                  <div style={{ width:64, height:64, borderRadius:"50%", background:`${COLORS.accent}33`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, fontWeight:700, color:COLORS.accent, border:`2px solid ${COLORS.accent}44` }}>
                    {(profile.full_name || user.email || "?")[0].toUpperCase()}
                  </div>
                )}
                <label style={{ position:"absolute", bottom:-2, right:-2, width:24, height:24, borderRadius:"50%", background:COLORS.accent, color:"#000", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:12, fontWeight:700, border:`2px solid ${COLORS.bg}` }} title="Upload photo">
                  📷
                  <input type="file" accept="image/*" style={{ display:"none" }} onChange={async(e)=>{
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { showToast("Image must be under 5MB"); return; }
                    try {
                      showToast("Uploading photo...");
                      const url = await uploadAvatar(file, user.id);
                      setProfile(prev => ({ ...prev, avatar_url: url }));
                      showToast("Profile photo updated!");
                    } catch(err) {
                      console.error("Avatar upload error:", err);
                      showToast("Failed to upload photo. Try again.");
                    }
                  }} />
                </label>
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:16 }}>{profile.full_name || user.email}</div>
                <div style={{ color:COLORS.muted, fontSize:12 }}>{user.email}</div>
                <div style={{ color:COLORS.muted, fontSize:11, marginTop:4 }}>Member since {new Date(user.created_at).toLocaleDateString("en-US", { month:"long", year:"numeric" })}</div>
              </div>
            </div>
          )}

          {!user ? (
            <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14, padding:32, textAlign:"center" }}>
              <div style={{ fontSize:44, marginBottom:10 }}>🔐</div>
              <div style={{ fontWeight:700, fontSize:17, marginBottom:7 }}>Sign In to See Your Rentals</div>
              <button onClick={()=>setShowAuth(true)} style={S.btn("primary")}>Sign In</button>
            </div>
          ) : myBookings.length === 0 ? (
            <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14, padding:32, textAlign:"center" }}>
              <div style={{ fontSize:44, marginBottom:10 }}>📦</div>
              <div style={{ fontWeight:700, fontSize:17, marginBottom:7 }}>No Active Rentals</div>
              <div style={{ color:COLORS.muted, marginBottom:18, fontSize:13 }}>Browse listings and rent your first item!</div>
              <button onClick={()=>setView("browse")} style={S.btn("primary")}>Browse Listings</button>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {myBookings.map(b=>(
                <div key={b.id} style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:18 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>{b.listings?.title}</div>
                      <div style={{ color:COLORS.muted, fontSize:12 }}>{b.start_date} → {b.end_date}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <span style={S.badge(b.status==="confirmed"?COLORS.green:b.status==="pending"?COLORS.accent:COLORS.muted)}>{b.status}</span>
                      <div style={{ color:COLORS.accent, fontWeight:700, marginTop:4 }}>${b.total_amount}</div>
                    </div>
                  </div>
                  {/* Claims against this booking (renter sees) */}
                  {(()=>{
                    const bookingClaims = myClaims.filter(c => c.booking_id === b.id);
                    const pendingClaim = bookingClaims.find(c => c.status === "pending" && c.respondent_id === profile?.id);
                    return bookingClaims.length > 0 ? (
                      <div style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${COLORS.border}22` }}>
                        {bookingClaims.map(c => (
                          <div key={c.id} style={{ background:COLORS.surface, borderRadius:8, padding:10, marginBottom:6, fontSize:12 }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                              <span style={{ fontWeight:700 }}>🛡️ {CLAIM_TYPES.find(t=>t.id===c.claim_type)?.label || c.claim_type}</span>
                              <ClaimStatusBadge status={c.status} />
                            </div>
                            <div style={{ display:"flex", justifyContent:"space-between", color:COLORS.muted, fontSize:11 }}>
                              <span>Requested: <strong style={{ color:COLORS.red }}>${c.amount_requested}</strong></span>
                              <span>{new Date(c.created_at).toLocaleDateString()}</span>
                            </div>
                            {c.status === "pending" && c.respondent_id === profile?.id && (
                              <button onClick={() => setDisputeClaim(c)} style={{ ...S.btn("red","sm"), marginTop:8, width:"100%" }}>⚖️ Respond to Claim</button>
                            )}
                            {c.status === "under_review" && <div style={{ color:COLORS.blue, fontSize:11, marginTop:6 }}>📋 Under review — RTRT will decide within 5 business days</div>}
                            {c.status === "resolved" && <div style={{ color:COLORS.green, fontSize:11, marginTop:6 }}>✅ Resolved — ${c.amount_approved || c.amount_requested} deducted from deposit</div>}
                          </div>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  <div style={{ display:"flex", gap:8, marginTop:10, paddingTop:10, borderTop:`1px solid ${COLORS.border}22` }}>
                    <button onClick={()=>setReviewBooking(b)} style={{ ...S.btn("purple","sm"), display:"flex", alignItems:"center", gap:4 }}>
                      ⭐ Leave Review
                    </button>
                    {b.reviewed && <span style={{ fontSize:12, color:COLORS.green, display:"flex", alignItems:"center", gap:4 }}>✅ Reviewed</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>}

        {/* ── MY LISTINGS VIEW ── */}
        {view==="mylistings" && <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <div style={{ fontSize:26, fontWeight:"bold" }}>My Listings</div>
            <button style={S.btn("primary")} onClick={()=>setShowList(true)}>+ Add Listing</button>
          </div>
          <div style={{ color:COLORS.muted, marginBottom:26, fontSize:14 }}>Items you've listed for rent</div>

          {/* Owner Analytics Dashboard */}
          {user && myListings.length > 0 && <OwnerDashboard myListings={myListings} myBookings={ownerBookings} user={user} claims={myClaims} onFileClaim={(booking, listing) => { setClaimBooking(booking); setClaimListing(listing); }} />}

          {!user ? (
            <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14, padding:32, textAlign:"center" }}>
              <div style={{ fontSize:44, marginBottom:10 }}>🔐</div>
              <div style={{ fontWeight:700, fontSize:17, marginBottom:7 }}>Sign In to Manage Your Listings</div>
              <button onClick={()=>setShowAuth(true)} style={S.btn("primary")}>Sign In</button>
            </div>
          ) : myListings.length === 0 ? (
            <div style={{ background:COLORS.card, border:`1px solid ${COLORS.border}`, borderRadius:14, padding:32, textAlign:"center" }}>
              <div style={{ fontSize:44, marginBottom:10 }}>🏷️</div>
              <div style={{ fontWeight:700, fontSize:17, marginBottom:7 }}>No Listings Yet</div>
              <div style={{ color:COLORS.muted, marginBottom:18, fontSize:13 }}>List your boat, RV, aircraft, tools, or anything else!</div>
              <button onClick={()=>setShowList(true)} style={S.btn("primary")}>List Your First Item</button>
            </div>
          ) : (
            <div style={S.grid}>
              {myListings.map(item=>(
                <div key={item.id} style={S.card}>
                  <div style={S.cardImg}>
                    <ImageCarousel images={[...parseImageUrls(item.image_url), ...(parseImageUrls(item.image_url).length === 0 ? [CAT_IMAGES[item.category] || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop"] : [])]} height={160} />
                    <span style={{ position:"absolute", top:8, right:8, background:item.status==="active"?COLORS.green+"ee":"#555", color:"#fff", fontSize:9, fontWeight:700, padding:"3px 7px", borderRadius:20 }}>{item.status}</span>
                  </div>
                  <div style={S.cardBody}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:3 }}>{item.title}</div>
                    <div style={{ color:COLORS.muted, fontSize:11, marginBottom:8 }}>{item.category} • ZIP {item.zip_code}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                      <div><span style={{ fontSize:18, fontWeight:"bold", color:COLORS.accent }}>${item.price_per_day}</span><span style={{ color:COLORS.muted, fontSize:12 }}>/day</span></div>
                      <span style={S.badge(COLORS.green)}>🛡️ Insured</span>
                    </div>
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={()=>setEditItem(item)} style={{ ...S.btn("outline","sm"), flex:1, textAlign:"center" }}>✏️ Edit</button>
                      <button onClick={()=>setCalendarItem(item)} style={{ ...S.btn("primary","sm"), flex:1, textAlign:"center" }}>📅 Availability</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>}
      </div>

      {/* ── MODALS ── */}
      {detailItem && <ListingDetailModal item={detailItem} onClose={()=>setDetailItem(null)} onRentNow={item=>{ setDetailItem(null); setCheckoutItem(item); }} />}

      {checkoutItem && <CheckoutModal item={checkoutItem} user={user} profile={profile} onClose={()=>setCheckoutItem(null)} onComplete={()=>{ setCheckoutItem(null); showToast("🎉 Rental confirmed! Check My Rentals."); }} onNeedAuth={()=>{ setCheckoutItem(null); setShowAuth(true); }} />}

      {showList && <ListItemModal user={user} profile={profile} onClose={()=>setShowList(false)} onNeedAuth={()=>{ setShowList(false); setShowAuth(true); }} onSave={listing=>{ setListings(l=>[listing,...l]); showToast("✅ Your listing is live!"); }} />}

      {editItem && <ListItemModal user={user} profile={profile} editItem={editItem} onClose={()=>setEditItem(null)} onNeedAuth={()=>{ setEditItem(null); setShowAuth(true); }} onSave={updated=>{ setMyListings(l=>l.map(x=>x.id===updated.id?updated:x)); setListings(l=>l.map(x=>x.id===updated.id?updated:x)); showToast("✅ Listing updated!"); }} onRemove={id=>{ setMyListings(l=>l.filter(x=>x.id!==id)); setListings(l=>l.filter(x=>x.id!==id)); setEditItem(null); showToast("🗑️ Listing removed."); }} />}

      {calendarItem && <ManageAvailabilityModal item={calendarItem} onClose={()=>setCalendarItem(null)} />}

      {reviewBooking && <ReviewModal booking={reviewBooking} user={user} onClose={()=>setReviewBooking(null)} onSaved={()=>showToast("⭐ Review submitted! Thank you.")} />}

      {showAuth && <AuthModal onClose={()=>setShowAuth(false)} onAuth={handleAuth} />}
      {showResetPassword && <SetNewPasswordModal onClose={()=>setShowResetPassword(false)} onSuccess={()=>showToast("Password updated successfully!")} />}

      {/* ── MESSAGING MODAL ── */}
      {msgTarget && user && <MessagingModal user={user} otherUserId={msgTarget.userId} otherUserName={msgTarget.userName} listingTitle={msgTarget.listingTitle} onClose={()=>setMsgTarget(null)} />}

      {/* ── LOCATION VIDEO MODAL ── */}
      {videoLocation && <LocationVideoModal location={videoLocation} onClose={()=>setVideoLocation(null)} />}

      {/* ── OWNER EARNINGS DASHBOARD (Feature 3) ── */}
      {view === "mylistings" && user && myListings.length > 0 && <OwnerEarningsDashboard myListings={myListings} myBookings={myBookings} user={user} />}

      {/* ── REFERRAL PROGRAM (Feature 6) ── */}
      {view === "mylistings" && user && <ReferralSection userId={user.id} />}

      {/* ── NOTIFICATION CENTER ── */}
      {showNotifCenter && user && (
        <NotificationCenter
          user={user}
          notifications={notifications}
          unreadCount={unreadCount}
          onClose={() => setShowNotifCenter(false)}
          onMarkRead={(id) => {
            markNotificationRead(id).catch(console.error);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
          }}
          onMarkAllRead={() => {
            markAllNotificationsRead(user.id).catch(console.error);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
          }}
          onNavigate={(linkType, linkId) => {
            setShowNotifCenter(false);
            if (linkType === "booking") setView("myrentals");
            else if (linkType === "listing") setView("mylistings");
            else if (linkType === "message") setView("myrentals");
            else if (linkType === "claim") setView("myrentals");
          }}
        />
      )}

      {/* ── ADMIN PANEL ── */}
      {showAdmin && isAdmin && user && <AdminPanel user={user} onClose={() => setShowAdmin(false)} />}

      {/* ── NOTIFICATION PREFERENCES (Feature 8) ── */}
      {showNotifications && <NotificationPreferences onClose={() => setShowNotifications(false)} />}

      {/* ── CHATBOT (Feature 7) ── */}
      <ChatBot />

      {/* ── PROMO VIDEO ── */}
      <div style={{ maxWidth:560, margin:"40px auto 0", padding:"0 16px" }}>
        <div style={{ fontSize:13, color:COLORS.muted, textAlign:"center", marginBottom:8, textTransform:"uppercase", letterSpacing:1, fontWeight:600 }}>🎬 Watch Our Story</div>
        <div style={{ borderRadius:14, overflow:"hidden", border:`1px solid ${COLORS.border}`, boxShadow:"0 4px 20px rgba(0,0,0,0.3)" }}>
          <video
            src="/rentthis.mp4"
            controls
            muted
            playsInline
            style={{ width:"100%", maxHeight:315, display:"block", objectFit:"cover" }}
            onEnded={e => { e.target.pause(); e.target.currentTime = e.target.duration; }}
          />
        </div>
      </div>

      {/* ── LEGAL PAGES ── */}
      {view==="privacy" && <PrivacyPolicyPage onBack={()=>{ setView("browse"); window.scrollTo(0,0); }} />}
      {view==="terms" && <TermsOfServicePage onBack={()=>{ setView("browse"); window.scrollTo(0,0); }} />}
      {view==="broker" && <BrokerAgreementPage onBack={()=>{ setView("browse"); window.scrollTo(0,0); }} />}

      {/* ── FOOTER ── */}
      <footer style={{ background:COLORS.surface, borderTop:`1px solid ${COLORS.border}`, marginTop:60, padding:"40px 20px 24px" }}>
        <div style={{ maxWidth:1120, margin:"0 auto" }}>
          {/* Top section - Logo, Contact, Social */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px, 1fr))", gap:30, marginBottom:30 }}>

            {/* Company Info */}
            <div>
              <div style={{ fontFamily:"'Georgia',serif", fontSize:18, fontWeight:"bold", color:COLORS.accent, marginBottom:10, letterSpacing:1 }}>RENT THIS RENT THAT</div>
              <div style={{ color:COLORS.muted, fontSize:13, lineHeight:1.7, marginBottom:14 }}>The peer-to-peer rental marketplace connecting owners and renters in their communities.</div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                <a href="https://facebook.com/rentthisrentthat" target="_blank" rel="noopener noreferrer" style={{ width:36, height:36, borderRadius:"50%", background:"#1877F2", display:"flex", alignItems:"center", justifyContent:"center", textDecoration:"none", fontSize:18, color:"#fff", transition:"opacity 0.2s" }} title="Facebook">f</a>
                <a href="https://instagram.com/rentthisrentthat" target="_blank" rel="noopener noreferrer" style={{ width:36, height:36, borderRadius:"50%", background:"linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)", display:"flex", alignItems:"center", justifyContent:"center", textDecoration:"none", fontSize:16, color:"#fff", transition:"opacity 0.2s" }} title="Instagram">📷</a>
                <a href="https://tiktok.com/@rentthisrentthat" target="_blank" rel="noopener noreferrer" style={{ width:36, height:36, borderRadius:"50%", background:"#000", display:"flex", alignItems:"center", justifyContent:"center", textDecoration:"none", fontSize:16, color:"#fff", border:"1px solid #333", transition:"opacity 0.2s" }} title="TikTok">♪</a>
                <a href="https://x.com/rentthisrentthat" target="_blank" rel="noopener noreferrer" style={{ width:36, height:36, borderRadius:"50%", background:"#000", display:"flex", alignItems:"center", justifyContent:"center", textDecoration:"none", fontSize:14, color:"#fff", border:"1px solid #333", fontWeight:900, transition:"opacity 0.2s" }} title="X (Twitter)">𝕏</a>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:COLORS.text, marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>Contact Us</div>
              <div style={{ color:COLORS.muted, fontSize:13, lineHeight:2 }}>
                <div>📧 <a href="mailto:rentthisrentthat@proton.me" style={{ color:COLORS.accent, textDecoration:"none" }}>rentthisrentthat@proton.me</a></div>
                <div>🌐 <a href="https://rentthisrentthat.com" style={{ color:COLORS.accent, textDecoration:"none" }}>rentthisrentthat.com</a></div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:COLORS.text, marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>Quick Links</div>
              <div style={{ color:COLORS.muted, fontSize:13, lineHeight:2 }}>
                <div style={{ cursor:"pointer", color:COLORS.muted }} onClick={()=>{ window.scrollTo(0,0); }}>🏠 Home</div>
                <div style={{ cursor:"pointer", color:COLORS.muted }} onClick={()=>{ setView("broker"); window.scrollTo(0,0); }}>📋 Broker Agreement</div>
                <div>🛡️ Insurance & Protection</div>
                <div>❓ FAQ & Help Center</div>
                <div>📞 Customer Support</div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop:`1px solid ${COLORS.border}`, paddingTop:20, display:"flex", flexWrap:"wrap", justifyContent:"space-between", alignItems:"center", gap:12 }}>
            <div style={{ color:COLORS.muted, fontSize:12 }}>
              © 2026 Rent This Rent That LLC. All rights reserved.
            </div>
            <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
              <span style={{ color:COLORS.muted, fontSize:11, cursor:"pointer" }} onClick={()=>{ setView("privacy"); window.location.hash="privacy"; window.scrollTo(0,0); }}>Privacy Policy</span>
              <span style={{ color:COLORS.muted, fontSize:11, cursor:"pointer" }} onClick={()=>{ setView("terms"); window.location.hash="terms"; window.scrollTo(0,0); }}>Terms of Service</span>
              <span style={{ color:COLORS.muted, fontSize:11, cursor:"pointer" }} onClick={()=>{ setView("broker"); window.location.hash="broker"; window.scrollTo(0,0); }}>Broker Agreement</span>
              <span style={{ color:COLORS.muted, fontSize:11 }}>Accessibility</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ── DAMAGE CLAIM MODALS ── */}
      {claimBooking && <DamageClaimModal booking={claimBooking} user={user} listing={claimListing} onClose={() => { setClaimBooking(null); setClaimListing(null); }} onSaved={() => { showToast("🛡️ Claim submitted successfully!"); if (profile?.id) getMyClaims(profile.id).then(setMyClaims).catch(console.error); }} />}
      {disputeClaim && <DisputeResponseModal claim={disputeClaim} user={user} onClose={() => setDisputeClaim(null)} onSaved={() => { showToast("📋 Response submitted!"); if (profile?.id) getMyClaims(profile.id).then(setMyClaims).catch(console.error); }} />}

      {/* ── TOAST ── */}
      {toast && <div style={{ position:"fixed", bottom:26, right:26, background:COLORS.card, border:`1px solid ${COLORS.green}`, borderRadius:10, padding:"13px 18px", color:COLORS.text, fontWeight:600, fontSize:13, zIndex:9999, boxShadow:"0 4px 20px rgba(0,0,0,0.5)" }}>{toast}</div>}
    </div>
  );
}
